/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useHistory, Redirect } from "react-router-dom";
import { checkTraineeManagePermission } from "./traineeAuth.js";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import { Modal, ModalHeader, ModalBody, ModalFooter, Badge, Nav, NavItem, NavLink } from "reactstrap";
import Select from "react-select";
import Pagination from "react-responsive-pagination";
import "bootstrap/dist/css/bootstrap.min.css";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import "moment/locale/th";

// Icons
import HotelIcon from "@material-ui/icons/Hotel";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import PeopleIcon from "@material-ui/icons/People";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import SchoolIcon from "@material-ui/icons/School";
import BusinessIcon from "@material-ui/icons/Business";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import Dormitory3DBuildingView from "views/Dormitory/Dormitory3DBuildingView.js";
import {
  ROOM_CATEGORIES,
  getRoomCategoryKey,
  getRoomCategoryInfo
} from "views/Dormitory/dormitoryRoomCategoryHelper.js";
import {
  calculateDormFeeByDays,
  countDaysBetween,
  calculatePaymentBalance,
  calculateCheckoutFee,
  THAI_FISCAL_MONTHS,
  extractActiveMonths,
  DEFAULT_RATES,
  isStayActive,
  isStayCurrent,
  isStayFuture,
  isStayPast,
  getStayTimelineStatus,
  parseThaiEndDate,
  formatThaiDateRangeText,
  formatStayDateRangeThai,
  parseThaiDateRange,
  getTraineeTrainingPeriodStatus,
  getStayDateRange,
  checkDateRangesOverlap
} from "./dormitoryFeeCalculator.js";

moment.locale("th");

// ฟังก์ชันสร้างรายชื่อผู้เข้าพักที่เป็นไปได้จากข้อมูลแพทย์และนักศึกษา
const buildCandidateList = (apiDoctors = [], apiStudents = []) => {
  const list = [];
  const seenIds = new Set();

  // 1. แพทย์ฝึกอบรม / ดูงาน
  const docs = (apiDoctors && apiDoctors.length > 0) ? apiDoctors : [];
  docs.forEach((d) => {
    const rawId = d.id || d.doctorID || d.DoctorID || `doc-${d.fullNameTH || d.nameTH}`;
    const idKey = String(rawId);
    if (!seenIds.has(idKey)) {
      seenIds.add(idKey);
      list.push({
        id: idKey,
        type: "DOCTOR",
        name: d.nameTH || d.fullNameTH || d.FullNameTH || d.name || "",
        position: d.position || d.Position || d.doctorPositionType || d.doctorType || "นายแพทย์ปฏิบัติงาน",
        institution: d.institution || d.institutionName || d.InstitutionName || "สถาบันโรคผิวหนัง",
        department: d.department || d.departmentName || d.DepartmentName || "",
        phone: d.phone || d.Phone || "",
        startDate: (d.startDate || d.StartDate || "").toString().split("T")[0],
        endDate: (d.endDate || d.EndDate || "").toString().split("T")[0],
        dateRange: formatStayDateRangeThai(d.dateRange || d.dateRangeText || d.DateRangeText || "", (d.startDate || d.StartDate || "").toString().split("T")[0], (d.endDate || d.EndDate || "").toString().split("T")[0]),
        fiscalYear: d.fiscalYear || d.FiscalYear || 2569,
        statusKey: d.statusKey ?? d.StatusKey,
        statusText: d.statusText || d.StatusText || d.status || "",
        requiresAccommodation: d.requiresAccommodation ?? d.RequiresAccommodation ?? false,
        dormFee: d.dormFee || (d.doctorPositionType && d.doctorPositionType.includes("elective") ? "วันละ 100 บาท" : "500 บาท"),
        roomNumber: d.roomNumber || d.assignedRoomNumber || d.AssignedRoomNumber || ""
      });
    }
  });

  // 2. นักศึกษาฝึกงาน
  const stus = (apiStudents && apiStudents.length > 0) ? apiStudents : [];
  stus.forEach((s) => {
    const rawId = s.id || s.studentID || s.StudentID || `std-${s.fullName || s.name}`;
    const idKey = String(rawId);
    if (!seenIds.has(idKey)) {
      seenIds.add(idKey);
      list.push({
        id: idKey,
        type: "STUDENT",
        name: s.name || s.fullName || s.FullName || "",
        position: s.role || s.level || s.positionLevel || s.traineeRole || "นศ.ฝึกงาน",
        institution: s.institution || s.institutionName || s.InstitutionName || "",
        department: s.department || s.departmentName || s.DepartmentName || "",
        phone: s.phone || s.Phone || "",
        startDate: (s.startDate || s.StartDate || "").toString().split("T")[0],
        endDate: (s.endDate || s.EndDate || "").toString().split("T")[0],
        dateRange: formatStayDateRangeThai(s.dateRange || s.dateRangeText || s.DateRangeText || "", (s.startDate || s.StartDate || "").toString().split("T")[0], (s.endDate || s.EndDate || "").toString().split("T")[0]),
        fiscalYear: s.fiscalYear || s.FiscalYear || 2569,
        statusKey: s.statusKey ?? s.StatusKey,
        statusText: s.statusText || s.StatusText || s.status || "",
        requiresAccommodation: s.requiresAccommodation ?? s.RequiresAccommodation ?? false,
        dormFee: s.dormFee ? (s.dormFee.includes("บาท") ? s.dormFee : `${s.dormFee} บาท`) : "500 บาท",
        roomNumber: s.roomNumber || s.assignedRoomNumber || s.AssignedRoomNumber || ""
      });
    }
  });

  return list;
};

const BUILDINGS = [
  { id: 1, dormitoryID: 1, buildingNo: "1", name: "อาคารหอพัก 1 (ตึก 1)", shortName: "ตึก 1", totalFloors: 3, note: "นักศึกษาเภสัชฯ & ผู้ช่วยพยาบาล" },
  { id: 2, dormitoryID: 2, buildingNo: "2", name: "อาคารหอพัก 2 (ตึก 2)", shortName: "ตึก 2", totalFloors: 3, note: "แพทย์ Fellow & Elective" },
  { id: 3, dormitoryID: 3, buildingNo: "3", name: "อาคารหอพัก 3 (ตึก 3)", shortName: "ตึก 3", totalFloors: 3, note: "แพทย์ Elective & ทั่วไป" },
  { id: 4, dormitoryID: 4, buildingNo: "4", name: "อาคารหอพัก 4 (ตึก 4)", shortName: "ตึก 4", totalFloors: 3, note: "นักศึกษาชาย & บุคลากร" }
];

// ข้อมูลห้องพัก 4 ตึก ตึกละ 3 ชั้น (เลขหลักที่ 1 = ตึก, เลขหลักที่ 2 = ชั้น)
const INITIAL_ROOMS = [
  // ตึก 1 (หอ 1: ชั้น 1 = 111, 112 / ชั้น 2 = 121, 122, 123, 124 / ชั้น 3 = 131, 132, 133, 134 รวม 10 ห้อง)
  { id: 1, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "111", roomNumber: "111", RoomNumber: "111", floor: 1, Floor: 1, type: "นักศึกษา", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 1" },
  { id: 2, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "112", roomNumber: "112", RoomNumber: "112", floor: 1, Floor: 1, type: "นักศึกษา", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 1" },
  { id: 3, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "121", roomNumber: "121", RoomNumber: "121", floor: 2, Floor: 2, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 2" },
  { id: 4, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "122", roomNumber: "122", RoomNumber: "122", floor: 2, Floor: 2, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 2" },
  { id: 5, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "123", roomNumber: "123", RoomNumber: "123", floor: 2, Floor: 2, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 2" },
  { id: 6, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "124", roomNumber: "124", RoomNumber: "124", floor: 2, Floor: 2, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 2" },
  { id: 7, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "131", roomNumber: "131", RoomNumber: "131", floor: 3, Floor: 3, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 3" },
  { id: 8, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "132", roomNumber: "132", RoomNumber: "132", floor: 3, Floor: 3, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 3" },
  { id: 9, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "133", roomNumber: "133", RoomNumber: "133", floor: 3, Floor: 3, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 3" },
  { id: 10, dormitoryID: 1, DormitoryID: 1, buildingNo: "1", BuildingNo: "1", buildingName: "ตึก 1", BuildingName: "ตึก 1", roomNo: "134", roomNumber: "134", RoomNumber: "134", floor: 3, Floor: 3, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "หอพัก 1 ชั้น 3" },

  // ตึก 2 (หอพักแพทย์) - ขึ้นต้นด้วย 2 คือตึก 2, หลักที่ 2 คือชั้น
  { id: 8, dormitoryID: 2, DormitoryID: 2, buildingNo: "2", BuildingNo: "2", buildingName: "ตึก 2", BuildingName: "ตึก 2", roomNo: "211", roomNumber: "211", RoomNumber: "211", floor: 1, Floor: 1, type: "แพทย์", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "OCCUPIED", Status: "OCCUPIED", CurrentResidents: [], notes: "ห้องพักชั้น 1 แพทย์ Fellow (มีผู้พักอาศัย)" },
  { id: 9, dormitoryID: 2, DormitoryID: 2, buildingNo: "2", BuildingNo: "2", buildingName: "ตึก 2", BuildingName: "ตึก 2", roomNo: "212", roomNumber: "212", RoomNumber: "212", floor: 1, Floor: 1, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องพักชั้น 1 ว่าง" },
  { id: 10, dormitoryID: 2, DormitoryID: 2, buildingNo: "2", BuildingNo: "2", buildingName: "ตึก 2", BuildingName: "ตึก 2", roomNo: "221", roomNumber: "221", RoomNumber: "221", floor: 2, Floor: 2, type: "แพทย์", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องพักชั้น 2 แพทย์ Elective ว่าง" },
  { id: 11, dormitoryID: 2, DormitoryID: 2, buildingNo: "2", BuildingNo: "2", buildingName: "ตึก 2", BuildingName: "ตึก 2", roomNo: "222", roomNumber: "222", RoomNumber: "222", floor: 2, Floor: 2, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องพักชั้น 2 ว่าง" },
  { id: 12, dormitoryID: 2, DormitoryID: 2, buildingNo: "2", BuildingNo: "2", buildingName: "ตึก 2", BuildingName: "ตึก 2", roomNo: "231", roomNumber: "231", RoomNumber: "231", floor: 3, Floor: 3, type: "แพทย์", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องพักชั้น 3 แพทย์ Fellow / Elective ว่าง" },
  { id: 13, dormitoryID: 2, DormitoryID: 2, buildingNo: "2", BuildingNo: "2", buildingName: "ตึก 2", BuildingName: "ตึก 2", roomNo: "232", roomNumber: "232", RoomNumber: "232", floor: 3, Floor: 3, type: "แพทย์", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องพักชั้น 3 แพทย์ Elective ว่าง" },
  { id: 14, dormitoryID: 2, DormitoryID: 2, buildingNo: "2", BuildingNo: "2", buildingName: "ตึก 2", BuildingName: "ตึก 2", roomNo: "233", roomNumber: "233", RoomNumber: "233", floor: 3, Floor: 3, type: "แพทย์", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องพักชั้น 3 สำรองรอบถัดไป" },

  // ตึก 3 (หอพักบุคลากร / นศ.หญิง)
  { id: 15, dormitoryID: 3, DormitoryID: 3, buildingNo: "3", BuildingNo: "3", buildingName: "ตึก 3", BuildingName: "ตึก 3", roomNo: "311", roomNumber: "311", RoomNumber: "311", floor: 1, Floor: 1, type: "พยาบาล", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 1 นศ.พยาบาล" },
  { id: 16, dormitoryID: 3, DormitoryID: 3, buildingNo: "3", BuildingNo: "3", buildingName: "ตึก 3", BuildingName: "ตึก 3", roomNo: "312", roomNumber: "312", RoomNumber: "312", floor: 1, Floor: 1, type: "พยาบาล", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 1 นศ.พยาบาล" },
  { id: 17, dormitoryID: 3, DormitoryID: 3, buildingNo: "3", BuildingNo: "3", buildingName: "ตึก 3", BuildingName: "ตึก 3", roomNo: "321", roomNumber: "321", RoomNumber: "321", floor: 2, Floor: 2, type: "พยาบาล", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 2 พยาบาลฝึกอบรม" },
  { id: 18, dormitoryID: 3, DormitoryID: 3, buildingNo: "3", BuildingNo: "3", buildingName: "ตึก 3", BuildingName: "ตึก 3", roomNo: "331", roomNumber: "331", RoomNumber: "331", floor: 3, Floor: 3, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 3" },

  // ตึก 4 (หอพักรับรองพิเศษ / นศ.ชาย)
  { id: 19, dormitoryID: 4, DormitoryID: 4, buildingNo: "4", BuildingNo: "4", buildingName: "ตึก 4", BuildingName: "ตึก 4", roomNo: "411", roomNumber: "411", RoomNumber: "411", floor: 1, Floor: 1, type: "แพทย์", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 1 แพทย์เพิ่มพูนทักษะ" },
  { id: 20, dormitoryID: 4, DormitoryID: 4, buildingNo: "4", BuildingNo: "4", buildingName: "ตึก 4", BuildingName: "ตึก 4", roomNo: "421", roomNumber: "421", RoomNumber: "421", floor: 2, Floor: 2, type: "แพทย์", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 2 อาจารย์แพทย์" },
  { id: 21, dormitoryID: 4, DormitoryID: 4, buildingNo: "4", BuildingNo: "4", buildingName: "ตึก 4", BuildingName: "ตึก 4", roomNo: "431", roomNumber: "431", RoomNumber: "431", floor: 3, Floor: 3, type: "ทั่วไป", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 3" },
  { id: 22, dormitoryID: 4, DormitoryID: 4, buildingNo: "4", BuildingNo: "4", buildingName: "ตึก 4", BuildingName: "ตึก 4", roomNo: "433", roomNumber: "433", RoomNumber: "433", floor: 3, Floor: 3, type: "นักศึกษา", capacity: 2, BedCapacity: 2, fee: 500, dailyFee: 100, status: "AVAILABLE", Status: "AVAILABLE", CurrentResidents: [], notes: "ห้องว่างชั้น 3 นศ.ชาย" }
];

// ข้อมูลการเข้าพักเริ่มต้น (ดึงจากฐานข้อมูลจริง 100%)
const INITIAL_STAYS = [];

const useStyles = makeStyles({
  container: {
    padding: "15px",
    fontFamily: '"Sarabun", sans-serif',
    "& *": {
      fontFamily: '"Sarabun", sans-serif'
    }
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "0.875rem",
    fontFamily: '"Sarabun", sans-serif'
  },
  th: {
    fontFamily: '"Sarabun", sans-serif',
    backgroundColor: "#f4f6f9",
    color: "#333",
    fontWeight: "600",
    fontSize: "0.875rem",
    padding: "10px 8px",
    borderBottom: "2px solid #dee2e6",
    whiteSpace: "nowrap",
    textAlign: "left"
  },
  thCenter: {
    fontFamily: '"Sarabun", sans-serif',
    backgroundColor: "#f4f6f9",
    color: "#333",
    fontWeight: "600",
    fontSize: "0.875rem",
    padding: "10px 8px",
    borderBottom: "2px solid #dee2e6",
    whiteSpace: "nowrap",
    textAlign: "center"
  },
  td: {
    fontFamily: '"Sarabun", sans-serif',
    fontSize: "0.875rem",
    padding: "9px 8px",
    borderBottom: "1px solid #e9ecef",
    verticalAlign: "middle"
  },
  roomCard: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "15px",
    backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: "0 4px 10px rgba(0,0,0,0.12)"
    }
  },
  floorHeader: {
    backgroundColor: "#eceff1",
    padding: "8px 14px",
    borderRadius: "6px",
    fontWeight: "700",
    color: "#37474f",
    marginBottom: "12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }
});

export default function DormitoryManagement() {
  const classes = useStyles();
  const history = useHistory();

  // ป้องกันการเข้าถึงโดยตรง หากไม่มีสิทธิ์ ให้ redirect ไปหน้า Dashboard
  if (!checkTraineeManagePermission()) {
    return <Redirect to="/trainee/dashboard" />;
  }

  const [activeTab, setActiveTab] = useState(0); // 0 = ผังห้องพัก (Floor Grid), 1 = ทะเบียนผู้เข้าพักและการเงิน
  const [viewMode, setViewMode] = useState("3D"); // '3D' = 3D Interactive Building, 'GRID' = Classic Floor Grid
  const [staffCandidates, setStaffCandidates] = useState([]);

  // Rooms & Stays State
  const [rooms, setRooms] = useState(INITIAL_ROOMS);
  const [stays, setStays] = useState([]);
  const [electricityList, setElectricityList] = useState([]);

  // Filters
  const [selectedBuilding, setSelectedBuilding] = useState("ALL"); // "ALL", "ตึก 1", "ตึก 2", "ตึก 3", "ตึก 4"
  const [floorFilter, setFloorFilter] = useState("ALL"); // "ALL", "1", "2", "3"
  const [roomOccupancyFilter, setRoomOccupancyFilter] = useState("ALL"); // "ALL", "ACTIVE", "OVERDUE", "ACTIVE_OVERDUE", "VACANT"
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("ALL");
  const [traineeTypeFilter, setTraineeTypeFilter] = useState("ALL");
  const [stayStatusFilter, setStayStatusFilter] = useState("ALL"); // "ALL", "ACTIVE" (กำลังพักอาศัย), "ENDED" (ออกไปแล้ว)

  // Pagination for Stay Roster
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Modals
  const [stayModal, setStayModal] = useState(false);
  const [roomModal, setRoomModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Manage Beds Modal
  const [manageBedsModal, setManageBedsModal] = useState(false);
  const [editingRoomBeds, setEditingRoomBeds] = useState(null);
  const [newBedCapacity, setNewBedCapacity] = useState(2);
  const [isSavingBeds, setIsSavingBeds] = useState(false);

  // Candidate Trainees from Database & Mock Data (for Dropdown selection)
  const [traineeCandidates, setTraineeCandidates] = useState(() => buildCandidateList());
  const [onlyActiveOrFuture, setOnlyActiveOrFuture] = useState(true);

  // รายชื่อผู้ที่มีห้องพักอยู่แล้วทั้งปัจจุบันและอนาคต (เพื่อนำออกจากตัวเลือก)
  const assignedTraineeSet = useMemo(() => {
    const set = new Set();
    stays.forEach((s) => {
      if (isStayActive(s) || isStayFuture(s)) {
        const name = (s.traineeName || "").trim();
        if (name && name !== "-") {
          set.add(name);
          const stripped = name.replace(/^(นพ\.|พญ\.|นาย|นางสาว|น\.ส\.|ดร\.)\s*/, "").trim();
          if (stripped) set.add(stripped);
        }
        if (s.id) set.add(String(s.id));
        if (s.doctorID) set.add(String(s.doctorID));
        if (s.studentID) set.add(String(s.studentID));
      }
    });
    return set;
  }, [stays]);

  // กรองเฉพาะคนที่อยู่ในช่วงเวลา หรือคนในอนาคตที่ใส่มาก่อน (หากเกินแล้วไม่ต้องโชว์)
  // และตัดคนที่มีห้องอยู่แล้วทั้งตอนนี้และอนาคตออก ไม่ให้แสดงในตัวเลือก
  const filteredCandidates = useMemo(() => {
    let list = traineeCandidates;
    if (onlyActiveOrFuture) {
      list = list.filter((c) => {
        const periodStatus = getTraineeTrainingPeriodStatus(c);
        return periodStatus.key !== "PAST";
      });
    }

    return list.filter((c) => {
      const cName = (c.name || "").trim();
      const stripped = cName.replace(/^(นพ\.|พญ\.|นาย|นางสาว|น\.ส\.|ดร\.)\s*/, "").trim();
      const hasRoom =
        assignedTraineeSet.has(cName) ||
        assignedTraineeSet.has(stripped) ||
        (c.id && assignedTraineeSet.has(String(c.id)));
      return !hasRoom;
    });
  }, [traineeCandidates, onlyActiveOrFuture, assignedTraineeSet]);

  // ตัวเลือกสำหรับ Searchable Dropdown (react-select)
  const traineeSelectOptions = useMemo(() => {
    const docOptions = filteredCandidates
      .filter((c) => c.type === "DOCTOR")
      .map((c) => {
        const period = getTraineeTrainingPeriodStatus(c);
        const icon = period.key === "ACTIVE" ? "🟢 [กำลังฝึก]" : period.key === "UPCOMING" ? "⏳ [รอเริ่มฝึก/อนาคต]" : "⚪ [พ้นกำหนด]";
        return {
          value: c.id,
          label: `${icon} ${c.name} — ${c.position} (${c.institution || "สถาบันโรคผิวหนัง"} | ${c.dateRange || "ไม่ระบุวัน"})`,
          trainee: c
        };
      });

    const stdOptions = filteredCandidates
      .filter((c) => c.type === "STUDENT")
      .map((c) => {
        const period = getTraineeTrainingPeriodStatus(c);
        const icon = period.key === "ACTIVE" ? "🟢 [กำลังฝึก]" : period.key === "UPCOMING" ? "⏳ [รอเริ่มฝึก/อนาคต]" : "⚪ [พ้นกำหนด]";
        return {
          value: c.id,
          label: `${icon} ${c.name} — ${c.position} (${c.institution || "ไม่ระบุสถาบัน"} | ${c.dateRange || "ไม่ระบุวัน"})`,
          trainee: c
        };
      });

    const groups = [];
    if (docOptions.length > 0) {
      groups.push({ label: `👨‍⚕️ แพทย์ฝึกอบรม / แพทย์ดูงาน (${docOptions.length} ท่านที่ยังไม่มีห้อง)`, options: docOptions });
    }
    if (stdOptions.length > 0) {
      groups.push({ label: `🎓 นักศึกษาฝึกงาน / ฝึกปฏิบัติงาน (${stdOptions.length} ท่านที่ยังไม่มีห้อง)`, options: stdOptions });
    }
    groups.push({
      label: "✍️ อื่นๆ",
      options: [{ value: "CUSTOM", label: "✍️ กรอกชื่อบุคคลภายนอก / พิมพ์ชื่อใหม่เอง" }]
    });

    return groups;
  }, [filteredCandidates]);

  // Stay Form
  const [stayForm, setStayForm] = useState({
    selectedTraineeId: "",
    building: "ตึก 1",
    floor: 1,
    roomNo: "111",
    traineeName: "",
    traineeType: "DOCTOR",
    position: "",
    institution: "",
    checkInDate: "",
    checkOutDate: "",
    dateRange: "",
    feeAmount: 500,
    feeText: "500 บาท",
    paymentStatus: "PAID",
    remarks: "",
    phone: ""
  });

  const selectedCandidateOption = useMemo(() => {
    if (!stayForm.selectedTraineeId) return null;
    if (stayForm.selectedTraineeId === "CUSTOM") {
      return { value: "CUSTOM", label: "✍️ กรอกชื่อบุคคลภายนอก / พิมพ์ชื่อใหม่เอง" };
    }
    for (const group of traineeSelectOptions) {
      const found = group.options.find((o) => o.value === stayForm.selectedTraineeId);
      if (found) return found;
    }
    return null;
  }, [stayForm.selectedTraineeId, traineeSelectOptions]);

  // Tab 2: Monthly Checklist State (Finance & Management Tracking)
  const [checklistYear, setChecklistYear] = useState("2569");
  const [checklistBuilding, setChecklistBuilding] = useState("ALL");
  const [checklistStatus, setChecklistStatus] = useState("ALL"); // "ALL", "OVERDUE", "PARTIAL", "PAID"
  const [checklistSearch, setChecklistSearch] = useState("");

  // Payment Recording Modal State
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedStayForPayment, setSelectedStayForPayment] = useState(null);
  const [paymentInput, setPaymentInput] = useState({
    feeAmount: 500,
    paidAmount: 0,
    remainingBalance: 0,
    paymentStatus: "PAID",
    receiptNo: "",
    paidDate: new Date().toISOString().split("T")[0],
    note: ""
  });

  // Check-out Current Occupant Modal State
  const [checkoutModal, setCheckoutModal] = useState(false);
  const [selectedStayForCheckout, setSelectedStayForCheckout] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({
    checkInDate: "",
    checkOutDate: "",
    isManualDuration: false,
    customMonths: 0,
    customDays: 0,
    calculationMode: "DAILY", // "DAILY", "MONTHLY", "PRORATED"
    dailyRate: 100,
    monthlyRate: 500,
    feeAmount: 0,
    paidAmount: 0,
    remainingToPay: 0,
    refundAmount: 0,
    formulaText: "",
    remarks: ""
  });

  // Open Payment Modal for a stay
  const handleOpenPaymentModal = (stay) => {
    if (!stay) return;
    if (isStaffOccupant(stay) || isWelfareStay(stay)) {
      setNotifyModal({
        isOpen: true,
        type: "INFO",
        title: "🏠 ห้องสวัสดิการเจ้าหน้าที่",
        message: "ผู้เข้าพักท่านนี้เป็นเจ้าหน้าที่โรงพยาบาลในห้องสวัสดิการ (ไม่มีค่าเช่าห้องพัก และสงวนสิทธิ์การจัดการที่ระบบหอพักบุคลากร)"
      });
      return;
    }
    setSelectedStayForPayment(stay);
    const fee = Number(stay.feeAmount) >= 0 ? Number(stay.feeAmount) : 500;
    const isOverdue = stay.paymentStatus === "OVERDUE";
    const currentPaid = isOverdue ? 0 : (stay.paidAmount !== undefined ? Number(stay.paidAmount) : (stay.paymentStatus === "PAID" ? fee : 0));
    const rem = isOverdue ? fee : (stay.remainingBalance !== undefined ? Number(stay.remainingBalance) : Math.max(0, fee - currentPaid));
    const currentStatus = stay.paymentStatus || (rem === 0 ? "PAID" : (currentPaid > 0 ? "PARTIAL" : "OVERDUE"));

    setPaymentInput({
      feeAmount: fee,
      paidAmount: currentPaid,
      remainingBalance: rem,
      paymentStatus: currentStatus,
      receiptNo: stay.receiptNo || `REC-2569-${Math.floor(1000 + Math.random() * 9000)}`,
      paidDate: isOverdue ? "" : (stay.paidDate ? String(stay.paidDate).split("T")[0] : new Date().toISOString().split("T")[0]),
      note: stay.remarks || ""
    });
    setPaymentModal(true);
  };

  // Leave System Styled Confirm Modal State (สำหรับยืนยันการทำรายการ เช่น นำคนในอนาคตออก)
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    icon: "far fa-question-circle",
    iconColor: "red",
    confirmText: "ยืนยัน",
    cancelText: "ยกเลิก",
    stayData: null,
    onConfirm: null
  });

  // Leave System Styled Alert/Notification Modal State (สำหรับแจ้งเตือนสำเร็จ/คำเตือน)
  const [notifyModal, setNotifyModal] = useState({
    isOpen: false,
    type: "SUCCESS", // "SUCCESS", "WARNING", "ERROR"
    title: "",
    message: ""
  });

  // ยกเลิกการจอง / เอาคนในอนาคตออก (เปิด Confirm Modal สไตล์ระบบลา)
  const handleRemoveFutureOccupant = (stay) => {
    if (!stay) return;
    setConfirmModal({
      isOpen: true,
      title: "ยืนยันการนำผู้เข้าพักล่วงหน้าออกจากห้องพักใช่หรือไม่?",
      icon: "far fa-question-circle",
      iconColor: "red",
      confirmText: "ยืนยัน",
      cancelText: "ยกเลิก",
      stayData: stay,
      onConfirm: async () => {
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        try {
          if (stay.id) {
            await axios.put(`/api/v1/dormitory/stays/${stay.id}`, {
              isCurrentStay: false,
              paymentStatus: "CANCELLED",
              remarks: (stay.remarks ? stay.remarks + " | " : "") + `[ยกเลิกการจองห้องพัก ${new Date().toLocaleDateString('th-TH')}]`
            }).catch((e) => console.warn("API stay update error", e));
          }
        } catch (err) {
          console.warn("Could not cancel future stay", err);
        }

        setStays((prev) => prev.filter((s) => s.id !== stay.id && !(s.traineeName === stay.traineeName && s.roomNo === stay.roomNo && s.dateRange === stay.dateRange)));
        setNotifyModal({
          isOpen: true,
          type: "SUCCESS",
          title: "ยกเลิกการจองสำเร็จ",
          message: `ยกเลิกการจองห้องพักของ "${stay.traineeName}" (${stay.building} ห้อง ${stay.roomNo}) เรียบร้อยแล้ว`
        });
        fetchDormitoryData();
      }
    });
  };

  // เปิดหน้าต่างยืนยันการย้ายออก (Check-out) และคำนวณค่าใช้จ่ายตามจริง
  const handleOpenCheckoutModal = (stay) => {
    if (!stay) return;
    if (isStaffOccupant(stay)) {
      setNotifyModal({
        isOpen: true,
        type: "INFO",
        title: "🔒 เจ้าหน้าที่โรงพยาบาล",
        message: "ผู้เข้าพักท่านนี้เป็นบุคลากรโรงพยาบาล การแจ้งย้ายออกหรือจัดการห้องพักต้องดำเนินการที่ระบบจัดการหอพักบุคลากร"
      });
      return;
    }
    setSelectedStayForCheckout(stay);

    const roomObj = rooms.find((r) => r.roomNo === stay.roomNo);
    const dRate = roomObj?.dailyFee || 100;
    const mRate = roomObj?.fee || 500;

    let inDate = stay.checkInDate ? String(stay.checkInDate).split("T")[0] : "";
    if (!inDate && stay.dateRange) {
      const parsed = parseThaiDateRange(stay.dateRange, stay.fiscalYear || 2569);
      if (parsed.start) inDate = parsed.start;
    }
    if (!inDate) inDate = new Date().toISOString().split("T")[0];

    const outDate = new Date().toISOString().split("T")[0];
    const paid = Number(stay.paidAmount) || 0;
    const isB2 = stay.building && (stay.building.includes("2") || stay.building.includes("แพทย์"));
    const defaultMode = isB2 ? "DAILY" : "MONTHLY";

    const calc = calculateCheckoutFee({
      building: stay.building || "ตึก 1",
      checkInDate: inDate,
      checkOutDate: outDate,
      isManualDuration: false,
      customMonths: 0,
      customDays: 0,
      calculationMode: defaultMode,
      dailyRate: dRate,
      monthlyRate: mRate,
      paidAmount: paid
    });

    const electShare = Number(stay.electricityShare) || 0;
    const totalDueWithElect = calc.totalFee + electShare;
    const remToPay = Math.max(0, totalDueWithElect - paid);
    const refAmount = Math.max(0, paid - totalDueWithElect);

    setCheckoutForm({
      checkInDate: inDate,
      checkOutDate: outDate,
      isManualDuration: false,
      customMonths: calc.elapsedMonths,
      customDays: calc.remainingDays,
      calculationMode: defaultMode,
      dailyRate: dRate,
      monthlyRate: mRate,
      feeAmount: totalDueWithElect,
      baseFeeAmount: calc.totalFee,
      electricityShare: electShare,
      paidAmount: paid,
      remainingToPay: remToPay,
      refundAmount: refAmount,
      formulaText: calc.formulaText + (electShare > 0 ? ` + ค่าไฟ ${electShare.toLocaleString()} บ.` : ""),
      remarks: `ย้ายออกเมื่อ ${new Date().toLocaleDateString('th-TH')}`
    });

    setCheckoutModal(true);
  };

  // คำนวณค่าใช้จ่ายย้ายออกใหม่ เมื่อปรับเปลี่ยนเงื่อนไขหรือระบุวัน/เดือนเอง
  const handleRecalculateCheckout = (overrideParams = {}) => {
    const updated = { ...checkoutForm, ...overrideParams };
    const electShare = Number(selectedStayForCheckout?.electricityShare) || 0;
    const calc = calculateCheckoutFee({
      building: selectedStayForCheckout?.building || "ตึก 1",
      checkInDate: updated.checkInDate,
      checkOutDate: updated.checkOutDate,
      isManualDuration: updated.isManualDuration,
      customMonths: updated.customMonths,
      customDays: updated.customDays,
      calculationMode: updated.calculationMode,
      dailyRate: updated.dailyRate,
      monthlyRate: updated.monthlyRate,
      paidAmount: updated.paidAmount
    });

    const totalFeeWithElect = calc.totalFee + electShare;
    const remToPay = Math.max(0, totalFeeWithElect - updated.paidAmount);
    const refAmount = Math.max(0, updated.paidAmount - totalFeeWithElect);

    setCheckoutForm({
      ...updated,
      customMonths: updated.isManualDuration ? updated.customMonths : calc.elapsedMonths,
      customDays: updated.isManualDuration ? updated.customDays : calc.remainingDays,
      feeAmount: totalFeeWithElect,
      baseFeeAmount: calc.totalFee,
      electricityShare: electShare,
      remainingToPay: remToPay,
      refundAmount: refAmount,
      formulaText: calc.formulaText + (electShare > 0 ? ` + ค่าไฟ ${electShare.toLocaleString()} บ.` : "")
    });
  };

  // ยืนยันการย้ายออก (Check-out)
  const handleConfirmCheckout = async () => {
    if (!selectedStayForCheckout) return;

    const stay = selectedStayForCheckout;
    const finalFee = Number(checkoutForm.feeAmount) || 0;
    const paid = Number(checkoutForm.paidAmount) || 0;
    const remaining = Math.max(0, finalFee - paid);
    const pStatus = remaining <= 0 ? "PAID" : (paid > 0 ? "PARTIAL" : "OVERDUE");

    const durationDesc = checkoutForm.isManualDuration
      ? `${checkoutForm.customMonths} เดือน ${checkoutForm.customDays} วัน (ระบุเอง)`
      : formatStayDateRangeThai("", checkoutForm.checkInDate, checkoutForm.checkOutDate);

    const updatedRemarks = (stay.remarks ? stay.remarks + " | " : "") +
      `[ย้ายออกเมื่อ ${checkoutForm.checkOutDate} อยู่จริง: ${durationDesc}, ยอดคิดจริง ${finalFee.toLocaleString()} บ., ${checkoutForm.formulaText}]`;

    try {
      if (stay.id) {
        await axios.put(`/api/v1/dormitory/stays/${stay.id}`, {
          checkOutDate: checkoutForm.checkOutDate,
          isCurrentStay: false,
          feeAmount: finalFee,
          paidAmount: paid,
          remainingBalance: remaining,
          paymentStatus: pStatus,
          remarks: updatedRemarks
        }).catch((e) => console.warn("API stay update error", e));
      }
    } catch (err) {
      console.warn("Could not save checkout to DB, updating local state", err);
    }

    // Update stays state
    setStays((prev) =>
      prev.map((s) => {
        if (s.id === stay.id) {
          return {
            ...s,
            checkOutDate: checkoutForm.checkOutDate,
            isCurrentStay: false,
            feeAmount: finalFee,
            remainingBalance: remaining,
            paymentStatus: pStatus,
            feeText: pStatus === "PAID" ? `จ่ายแล้ว ${finalFee.toLocaleString()} บาท` : `ค้างชำระ ${remaining.toLocaleString()} บาท`,
            remarks: updatedRemarks
          };
        }
        return s;
      })
    );

    // Update room status
    setRooms((prev) =>
      prev.map((r) => {
        if (r.roomNo === stay.roomNo) {
          return {
            ...r,
            status: "AVAILABLE"
          };
        }
        return r;
      })
    );

    setCheckoutModal(false);
    setNotifyModal({
      isOpen: true,
      type: "SUCCESS",
      title: "บันทึกการย้ายออกเรียบร้อยแล้ว",
      message: `บันทึกการย้ายออกของ "${stay.traineeName}" เรียบร้อยแล้ว\n(ยอดที่ต้องชำระจริง ${finalFee.toLocaleString()} บาท)`
    });
  };

  const [loadingData, setLoadingData] = useState(false);
  const isMountedRef = React.useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ดึงข้อมูลห้องพักและประวัติการเข้าพักจริงจาก Database API
  const fetchDormitoryData = async () => {
    if (!isMountedRef.current) return;
    setLoadingData(true);
    try {
      const [roomsRes, staysRes, doctorsRes, studentsRes, staffRes, userViewRes, electRes] = await Promise.allSettled([
        axios.get("/api/v1/dormitory/rooms"),
        axios.get("/api/v1/dormitory/stays"),
        axios.get("/api/v1/trainee/doctors"),
        axios.get("/api/v1/trainee/students"),
        axios.get("/api/v1/dormitory/staff-candidates"),
        axios.get("/api/v1/userview?isShowAll=true"),
        axios.get("/api/v1/dormitory/electricity")
      ]);

      if (!isMountedRef.current) return;

      let mergedStaff = [];
      if (staffRes.status === "fulfilled" && staffRes.value?.data && staffRes.value.data.success) {
        mergedStaff = staffRes.value.data.data || [];
      } else if (staffRes.status === "fulfilled" && Array.isArray(staffRes.value?.data)) {
        mergedStaff = staffRes.value.data;
      }

      const usersFromView = (userViewRes?.status === "fulfilled" && userViewRes.value?.data?.data)
        ? userViewRes.value.data.data
        : [];
      if (usersFromView.length > 0 && mergedStaff.length > 0) {
        mergedStaff = mergedStaff.map((s) => {
          const matched = usersFromView.find((u) => {
            if (s.citizenId && u.cid && String(s.citizenId) === String(u.cid)) return true;
            if (s.empCode && u.userID && String(s.empCode) === String(u.userID)) return true;
            const sName = (s.name || "").replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, "").trim();
            const uName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
            return sName && uName && (sName === uName || sName.includes(uName) || uName.includes(sName));
          });
          const target = matched?.userName || s.userName || s.empCode || s.id;
          return {
            ...s,
            userName: matched?.userName || s.userName,
            avatarUrl: target ? `https://e.trangskin.go.th/avatar/${target}.jpg` : null
          };
        });
      }
      if (isMountedRef.current) setStaffCandidates(mergedStaff);

      const apiDoctors = (doctorsRes.status === "fulfilled" && doctorsRes.value?.data?.data && doctorsRes.value.data.data.length > 0)
        ? doctorsRes.value.data.data
        : [];
      const apiStudents = (studentsRes.status === "fulfilled" && studentsRes.value?.data?.data && studentsRes.value.data.data.length > 0)
        ? studentsRes.value.data.data
        : [];
      if (isMountedRef.current) {
        setTraineeCandidates(buildCandidateList(apiDoctors, apiStudents));
      }

      let rawElect = [];
      if (electRes.status === "fulfilled" && electRes.value?.data?.data) {
        rawElect = electRes.value.data.data;
      } else if (electRes.status === "fulfilled" && Array.isArray(electRes.value?.data)) {
        rawElect = electRes.value.data;
      }
      if (isMountedRef.current) setElectricityList(rawElect);

      // Map latest electricity record by room ID and room number
      const electricityByRoomMap = {};
      rawElect.forEach((el) => {
        const rId = el.roomID || el.RoomID;
        const rNum = String(el.roomNumber || el.RoomNumber || "").trim();
        if (rId && !electricityByRoomMap[String(rId)]) {
          electricityByRoomMap[String(rId)] = el;
        }
        if (rNum && !electricityByRoomMap[rNum]) {
          electricityByRoomMap[rNum] = el;
        }
      });

      let mappedStays = [];
      if (staysRes.status === "fulfilled" && staysRes.value?.data?.data && staysRes.value.data.data.length > 0) {
        const rawStays = staysRes.value.data.data;

        // Count active occupants per room to split electricity equally
        const activeOccupantCountByRoom = {};
        rawStays.forEach((s) => {
          if (s.isCurrentStay ?? s.IsCurrentStay) {
            const rKeyId = s.roomID || s.RoomID ? String(s.roomID || s.RoomID) : null;
            const rKeyNum = s.roomNumber || s.RoomNumber ? String(s.roomNumber || s.RoomNumber).trim() : null;
            if (rKeyId) activeOccupantCountByRoom[rKeyId] = (activeOccupantCountByRoom[rKeyId] || 0) + 1;
            if (rKeyNum) activeOccupantCountByRoom[rKeyNum] = (activeOccupantCountByRoom[rKeyNum] || 0) + 1;
          }
        });

        mappedStays = rawStays.map(s => {
          const rNum = s.roomNumber || s.RoomNumber || "";
          const bNo = s.buildingNo || s.BuildingNo || (rNum ? rNum.substring(0, 1) : "1");
          const fl = s.floor || s.Floor || (rNum.length >= 2 ? parseInt(rNum.substring(1, 2)) : 1);
          const rawDateRange = s.stayDateRangeText || s.StayDateRangeText || "";
          const checkIn = (s.checkInDate || s.CheckInDate || "").toString().split("T")[0];
          const checkOut = (s.checkOutDate || s.CheckOutDate || "").toString().split("T")[0];
          const dateRange = formatStayDateRangeThai(rawDateRange, checkIn, checkOut);
          const thaiMonths = ["ต.ค.", "พ.ย.", "ธ.ค.", "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย."];
          const stayMonths = thaiMonths.filter(m => dateRange.includes(m));

          const traineeName = s.traineeName || s.TraineeName || "-";
          const fallbackStay = INITIAL_STAYS.find(is => is.traineeName === traineeName);

          const cleanName = traineeName.replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
          const matchedStaff = mergedStaff.find((st) => {
            if (s.staffCode && st.empCode && String(s.staffCode) === String(st.empCode)) return true;
            if (s.StaffCode && st.empCode && String(s.StaffCode) === String(st.empCode)) return true;
            const stName = (st.name || '').replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
            return cleanName && stName && (cleanName === stName || stName.includes(cleanName) || cleanName.includes(stName));
          });
          const matchedUser = usersFromView.find((u) => {
            const uName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
            return cleanName && uName && (cleanName === uName || cleanName.includes(uName) || uName.includes(cleanName));
          });
          const avatarUrl = matchedStaff?.avatarUrl || (matchedUser?.userName ? `https://e.trangskin.go.th/avatar/${matchedUser.userName}.jpg` : (s.avatarUrl || s.AvatarUrl || null));

          const instStr = String(s.institutionName || s.InstitutionName || s.institution || fallbackStay?.institution || "");
          const isStaff = Boolean(
            matchedStaff || 
            s.staffCode || 
            s.StaffCode || 
            s.traineeType === "STAFF" || 
            s.TraineeType === "STAFF" ||
            instStr.includes("โรงพยาบาล") ||
            instStr.includes("รพ.") ||
            instStr.includes("ผิวหนัง")
          );
          const isWelfare = Boolean(isStaff || s.feeRateType === "FREE" || Number(s.feeAmount ?? s.FeeAmount) === 0);

          // ดึงข้อมูลมิเตอร์ค่าไฟที่จดไว้ในจัดการห้องพักสำหรับห้องนี้
          const rIdStr = String(s.roomID || s.RoomID || "");
          const rNumStr = String(s.roomNumber || s.RoomNumber || "").trim();
          const electRecord = (rIdStr && electricityByRoomMap[rIdStr]) || (rNumStr && electricityByRoomMap[rNumStr]) || null;
          const roomElectTotal = electRecord && Number(electRecord.totalAmount) > 0 ? Number(electRecord.totalAmount) : 0;
          
          // สัดส่วนค่าไฟเฉลี่ยตามจำนวนผู้พักในห้อง
          const roomOccCount = Math.max(1, (rIdStr && activeOccupantCountByRoom[rIdStr]) || (rNumStr && activeOccupantCountByRoom[rNumStr]) || 1);
          const electShare = isWelfare ? 0 : Math.round(roomElectTotal / roomOccCount);

          const baseFee = isWelfare ? 0 : (s.feeAmount ?? s.FeeAmount ?? 500);
          const fee = baseFee + electShare;
          const rawPaid = isWelfare ? 0 : (s.paidAmount ?? s.PaidAmount ?? 0);
          const rawStatus = (s.paymentStatus || s.PaymentStatus || "").toUpperCase();

          let pStatus = rawStatus;
          let paid = rawPaid;
          let rem = isWelfare ? 0 : Math.max(0, fee - paid);

          if (isWelfare || rawStatus === "FREE") {
            pStatus = "FREE";
            paid = 0;
            rem = 0;
          } else if (rawStatus === "OVERDUE") {
            pStatus = "OVERDUE";
            if (rawPaid >= fee && fee > 0) {
              paid = 0;
              rem = fee;
            }
          } else if (rawStatus === "PENDING" || rawStatus === "CANCELLED") {
            pStatus = rawStatus;
          } else if (rawStatus === "PAID") {
            pStatus = "PAID";
            if (paid < fee) paid = fee;
            rem = 0;
          } else if (rawStatus === "PARTIAL") {
            pStatus = "PARTIAL";
          } else {
            pStatus = rem <= 0 ? "PAID" : (paid > 0 ? "PARTIAL" : "OVERDUE");
          }

          let feeText = isWelfare
            ? "ห้องสวัสดิการ (ไม่มีค่าเช่า)"
            : (pStatus === "PAID"
                ? `จ่ายแล้ว ${fee.toLocaleString()} บาท`
                : (pStatus === "OVERDUE" || paid === 0
                    ? `ค้างชำระ ${(rem > 0 ? rem : fee).toLocaleString()} บาท`
                    : `จ่ายแล้ว ${paid.toLocaleString()} (ค้าง ${rem.toLocaleString()} บาท)`));
          if (electShare > 0 && !isWelfare) {
            feeText += ` (รวมค่าไฟ ${electShare.toLocaleString()} บ.)`;
          }

          return {
            id: s.stayID || s.StayID,
            StayID: s.stayID || s.StayID,
            stayID: s.stayID || s.StayID,
            fiscalYear: s.fiscalYear || s.FiscalYear || 2569,
            bookNo: s.bookNo || s.BookNo || fallbackStay?.bookNo || "",
            institution: s.institutionName || s.InstitutionName || fallbackStay?.institution || "-",
            traineeName: traineeName,
            TraineeName: traineeName,
            traineeType: isStaff ? "STAFF" : (s.traineeType || s.TraineeType || "STUDENT"),
            TraineeType: isStaff ? "STAFF" : (s.traineeType || s.TraineeType || "STUDENT"),
            staffCode: s.staffCode || s.StaffCode || matchedStaff?.empCode || "",
            StaffCode: s.staffCode || s.StaffCode || matchedStaff?.empCode || "",
            position: s.positionText || s.PositionText || fallbackStay?.position || (matchedStaff ? matchedStaff.position : (isStaff ? "เจ้าหน้าที่ รพ." : "ผู้เข้าพัก")),
            PositionText: s.positionText || s.PositionText || fallbackStay?.position || (matchedStaff ? matchedStaff.position : (isStaff ? "เจ้าหน้าที่ รพ." : "ผู้เข้าพัก")),
            dateRange: dateRange,
            checkInDate: s.checkInDate || s.CheckInDate || fallbackStay?.checkInDate || "",
            checkOutDate: s.checkOutDate || s.CheckOutDate || fallbackStay?.checkOutDate || "",
            isCurrentStay: s.isCurrentStay ?? s.IsCurrentStay ?? (fallbackStay?.isCurrentStay ?? false),
            IsCurrentStay: s.isCurrentStay ?? s.IsCurrentStay ?? (fallbackStay?.isCurrentStay ?? false),
            building: `ตึก ${bNo}`,
            BuildingNo: String(bNo),
            buildingNo: String(bNo),
            floor: fl,
            Floor: fl,
            roomNo: rNum,
            RoomNumber: rNum,
            feeAmount: fee,
            baseFeeAmount: baseFee,
            electricityShare: electShare,
            roomElectricityTotal: roomElectTotal,
            electricityRecord: electRecord,
            paidAmount: paid,
            remainingBalance: rem,
            feeText: feeText,
            paymentStatus: pStatus,
            feeRateType: isWelfare ? "FREE" : (s.feeRateType || "MONTHLY"),
            isWelfare: isWelfare,
            isStaff: isStaff,
            receiptNo: s.receiptNo || s.ReceiptNo || "",
            paidDate: s.paidDate || s.PaidDate || "",
            prorationText: isWelfare ? "สวัสดิการ รพ." : (s.prorationFormula || s.ProrationFormula || `${fee} บ.`),
            stayMonths: stayMonths.length > 0 ? stayMonths : ["พ.ค."],
            remarks: s.remarks || s.Remarks || fallbackStay?.remarks || "",
            phone: s.contactPhone || s.ContactPhone || fallbackStay?.phone || "",
            coResidents: s.coResidents || s.CoResidents || "",
            CoResidents: s.coResidents || s.CoResidents || "",
            avatarUrl: avatarUrl,
            AvatarUrl: avatarUrl
          };
        });
        if (isMountedRef.current) setStays(mappedStays);
      }

      const activeStays = mappedStays.filter(s => s.isCurrentStay || s.IsCurrentStay);

      if (roomsRes.status === "fulfilled" && roomsRes.value?.data?.data && roomsRes.value.data.data.length > 0) {
        const rawRooms = roomsRes.value.data.data;
        const mappedRooms = rawRooms.map(r => {
          const rId = r.roomID || r.RoomID;
          const rNum = String(r.roomNumber || r.RoomNumber || r.roomNo || "");
          const rawBNo = String(r.buildingNo || r.BuildingNo || r.dormitoryID || r.DormitoryID || (rNum ? rNum.substring(0, 1) : "1"));
          const bNo = rawBNo.replace(/\D+/g, "") || (rNum ? rNum.substring(0, 1) : "1");
          const fl = r.floor || r.Floor || (rNum.length >= 2 ? parseInt(rNum.substring(1, 2)) : 1);

          // Find active stays matching this room
          const roomStays = activeStays.filter(
            (s) => (s.roomID === rId || s.RoomID === rId) ||
                   (String(s.roomNumber || s.RoomNumber) === rNum && String(s.buildingNo || s.BuildingNo || s.dormitoryID) === bNo)
          );

          let residents = (r.CurrentResidents || r.currentResidents || []);
          if (residents.length > 0) {
            residents = residents.map((cr) => {
              const fullStay = activeStays.find((s) => (s.StayID === cr.StayID || s.stayID === cr.stayID || s.StayID === cr.stayId));
              return fullStay ? { ...cr, ...fullStay } : cr;
            });
          } else if (roomStays.length > 0) {
            residents = roomStays;
          }

          const hasStaffResident = residents.some(cr => {
            const crType = cr.traineeType || cr.TraineeType;
            const crInst = String(cr.institution || cr.institutionName || "");
            return crType === "STAFF" || cr.staffCode || cr.StaffCode || crInst.includes("โรงพยาบาล") || crInst.includes("รพ.") || crInst.includes("ผิวหนัง");
          });
          const catKey = getRoomCategoryKey(r);
          const isWelfareRoomConfig = catKey === "STAFF_GENERAL" || catKey === "STAFF_FAMILY" || r.targetGroup === "STAFF" || r.TargetGroup === "STAFF" || hasStaffResident;
          const monthlyFee = isWelfareRoomConfig ? 0 : (r.monthlyRate || r.MonthlyRate || 500);
          const dailyFee = isWelfareRoomConfig ? 0 : (r.dailyRate || r.DailyRate || 100);

          // ดึงมิเตอร์ค่าไฟของห้องนี้จากที่จดในจัดการห้องพัก
          const roomElectRecord = (rId && electricityByRoomMap[String(rId)]) || (rNum && electricityByRoomMap[rNum]) || null;
          const roomElectAmount = roomElectRecord && Number(roomElectRecord.totalAmount) > 0 ? Number(roomElectRecord.totalAmount) : 0;
          const totalMonthlyWithElect = isWelfareRoomConfig ? 0 : (monthlyFee + roomElectAmount);

          return {
            id: rId,
            RoomID: rId,
            dormitoryID: Number(bNo) || 1,
            DormitoryID: Number(bNo) || 1,
            buildingName: `ตึก ${bNo}`,
            BuildingName: `ตึก ${bNo}`,
            buildingNo: String(bNo),
            BuildingNo: String(bNo),
            roomNo: rNum,
            RoomNumber: rNum,
            floor: fl,
            Floor: fl,
            type: isWelfareRoomConfig ? (r.isFamilyRoom ? "ครอบครัว" : "สวัสดิการ") : (r.roomType || r.RoomType || "ทั่วไป"),
            RoomType: isWelfareRoomConfig ? (r.isFamilyRoom ? "ครอบครัว" : "สวัสดิการ") : (r.roomType || r.RoomType || "ทั่วไป"),
            targetGroup: isWelfareRoomConfig ? "STAFF" : (r.targetGroup || r.TargetGroup || "ALL"),
            TargetGroup: isWelfareRoomConfig ? "STAFF" : (r.targetGroup || r.TargetGroup || "ALL"),
            capacity: r.bedCapacity || r.BedCapacity || 2,
            bedCapacity: r.bedCapacity || r.BedCapacity || 2,
            BedCapacity: r.bedCapacity || r.BedCapacity || 2,
            currentOccupancy: r.currentOccupancy ?? r.CurrentOccupancy ?? residents.length,
            CurrentOccupancy: r.currentOccupancy ?? r.CurrentOccupancy ?? residents.length,
            fee: monthlyFee,
            baseFee: monthlyFee,
            monthlyRate: monthlyFee,
            MonthlyRate: monthlyFee,
            dailyFee: dailyFee,
            dailyRate: dailyFee,
            DailyRate: dailyFee,
            electricity: roomElectRecord,
            electricityAmount: roomElectAmount,
            totalMonthlyFeeWithElectricity: totalMonthlyWithElect,
            status: r.status || r.Status || (residents.length > 0 ? "OCCUPIED" : "AVAILABLE"),
            Status: r.status || r.Status || (residents.length > 0 ? "OCCUPIED" : "AVAILABLE"),
            notes: r.notes || r.Notes || "",
            Notes: r.notes || r.Notes || "",
            isWelfareRoom: isWelfareRoomConfig,
            hasStaffResident: hasStaffResident,
            CurrentResidents: residents,
            currentResidents: residents
          };
        });
        if (isMountedRef.current) setRooms(mappedRooms);
      }
    } catch (err) {
      console.warn("Could not load dormitory data from API, using default data", err);
    } finally {
      if (isMountedRef.current) setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchDormitoryData();
  }, []);

  // Save payment
  const handleSavePayment = async () => {
    if (!selectedStayForPayment) return;
    const fee = Number(paymentInput.feeAmount) >= 0 ? Number(paymentInput.feeAmount) : (selectedStayForPayment.feeAmount || 500);
    let paid = Math.max(0, Number(paymentInput.paidAmount) || 0);
    let newStatus = paymentInput.paymentStatus;

    if (newStatus === "OVERDUE") {
      paid = 0;
    } else if (newStatus === "PAID" && paid < fee) {
      paid = fee;
    }
    const remaining = Math.max(0, fee - paid);
    
    if (!newStatus) {
      if (remaining === 0) newStatus = "PAID";
      else if (paid > 0) newStatus = "PARTIAL";
      else newStatus = "OVERDUE";
    }

    const payloadPaidDate = (newStatus === "OVERDUE" || !paymentInput.paidDate) ? null : paymentInput.paidDate;

    try {
      await axios.post(`/api/v1/dormitory/stays/${selectedStayForPayment.id}/record-payment`, {
        feeAmount: fee,
        amountPaid: 0,
        totalPaidAmount: paid,
        receiptNo: paymentInput.receiptNo || "",
        paidDate: payloadPaidDate,
        remarks: paymentInput.note || "",
        paymentStatus: newStatus
      });
      fetchDormitoryData();
    } catch (err) {
      console.warn("Could not persist payment to database, updating local state", err);
    }

    setStays((prev) =>
      prev.map((s) => {
        if (s.id === selectedStayForPayment.id) {
          return {
            ...s,
            feeAmount: fee,
            paidAmount: paid,
            remainingBalance: remaining,
            paymentStatus: newStatus,
            receiptNo: paymentInput.receiptNo,
            paidDate: payloadPaidDate,
            remarks: paymentInput.note,
            feeText: newStatus === "PAID" 
              ? `จ่ายแล้ว ${fee.toLocaleString()} บาท` 
              : (paid > 0 
                  ? `จ่ายแล้ว ${paid.toLocaleString()} (ค้าง ${remaining.toLocaleString()} บาท)` 
                  : `ค้างชำระ ${remaining.toLocaleString()} บาท`)
          };
        }
        return s;
      })
    );
    setPaymentModal(false);
  };

  // Filter Stays for Checklist Tab (Tab 2)
  const checklistStays = useMemo(() => {
    return stays.filter((s) => {
      if (checklistYear !== "ALL" && s.fiscalYear && s.fiscalYear !== Number(checklistYear)) return false;
      if (checklistBuilding !== "ALL" && s.building !== checklistBuilding) return false;
      if (checklistStatus !== "ALL" && s.paymentStatus !== checklistStatus) return false;
      if (checklistSearch) {
        const query = checklistSearch.toLowerCase();
        const matchName = s.traineeName && s.traineeName.toLowerCase().includes(query);
        const matchRoom = s.roomNo && s.roomNo.toLowerCase().includes(query);
        const matchInst = s.institution && s.institution.toLowerCase().includes(query);
        if (!matchName && !matchRoom && !matchInst) return false;
      }
      return true;
    });
  }, [stays, checklistYear, checklistBuilding, checklistStatus, checklistSearch]);

  // Financial KPI totals for Checklist Tab
  const checklistKpis = useMemo(() => {
    let totalDue = 0;
    let totalPaid = 0;
    let totalRemaining = 0;
    let overdueCount = 0;
    let fullyPaidCount = 0;

    checklistStays.forEach((s) => {
      const fee = s.feeAmount || 0;
      const paid = s.paidAmount !== undefined ? s.paidAmount : (s.paymentStatus === "PAID" ? fee : 0);
      const remaining = s.remainingBalance !== undefined ? s.remainingBalance : Math.max(0, fee - paid);

      totalDue += fee;
      totalPaid += paid;
      totalRemaining += remaining;
      if (remaining > 0) overdueCount += 1;
      else fullyPaidCount += 1;
    });

    const collectionRate = totalDue > 0 ? Math.round((totalPaid / totalDue) * 1000) / 10 : 100;

    return { totalDue, totalPaid, totalRemaining, overdueCount, fullyPaidCount, collectionRate };
  }, [checklistStays]);

  // Map active room occupancy (เฉพาะคนที่วันเวลาอยู่ในช่วงจริงๆ)
  const roomOccupantsMap = useMemo(() => {
    const map = {};
    const activeStays = stays.filter(isStayActive);
    activeStays.forEach((stay) => {
      const key = `${stay.building || "ตึก 1"}-${stay.roomNo}`;
      if (!map[key]) map[key] = [];
      map[key].push(stay);
      // also map by roomNo alone for fallback
      if (!map[stay.roomNo]) map[stay.roomNo] = [];
      map[stay.roomNo].push(stay);
    });
    return map;
  }, [stays]);

  // Map future room occupancy (คนที่เข้าพักในอนาคต/ยังไม่ถึงเวลา/ไม่ใช่ตอนนี้)
  const roomFutureOccupantsMap = useMemo(() => {
    const map = {};
    const futureStays = stays.filter(isStayFuture);
    futureStays.forEach((stay) => {
      const key = `${stay.building || "ตึก 1"}-${stay.roomNo}`;
      if (!map[key]) map[key] = [];
      map[key].push(stay);
      // also map by roomNo alone for fallback
      if (!map[stay.roomNo]) map[stay.roomNo] = [];
      map[stay.roomNo].push(stay);
    });
    return map;
  }, [stays]);

  // Map past room occupancy (คนที่พ้นวันฝึก/ออกไปแล้ว)
  const roomPastOccupantsMap = useMemo(() => {
    const map = {};
    const pastStays = stays.filter(isStayPast);
    pastStays.forEach((stay) => {
      const key = `${stay.building || "ตึก 1"}-${stay.roomNo}`;
      if (!map[key]) map[key] = [];
      map[key].push(stay);
      if (!map[stay.roomNo]) map[stay.roomNo] = [];
      map[stay.roomNo].push(stay);
    });
    return map;
  }, [stays]);

  // Summary occupant counts
  const activeOccupantsCount = useMemo(() => {
    return stays.filter(isStayActive).length;
  }, [stays]);

  const futureOccupantsCount = useMemo(() => {
    return stays.filter(isStayFuture).length;
  }, [stays]);

  const pastOccupantsCount = useMemo(() => {
    return stays.filter(isStayPast).length;
  }, [stays]);

  const occupiedRoomsCount = useMemo(() => {
    return rooms.filter((r) => (roomOccupantsMap[`${r.buildingName}-${r.roomNo}`]?.length || 0) > 0).length;
  }, [rooms, roomOccupantsMap]);

  // Helper to determine if an occupant/stay is a hospital staff member
  const isStaffOccupant = (occ) => {
    if (!occ) return false;
    if (occ.isStaff || occ.isStaffLinked) return true;
    if (occ.traineeType === "STAFF" || occ.TraineeType === "STAFF") return true;
    if (occ.staffCode || occ.StaffCode) return true;
    const inst = String(occ.institution || occ.institutionName || occ.Institution || occ.InstitutionName || "");
    if (inst.includes("โรงพยาบาล") || inst.includes("รพ.") || inst.includes("ผิวหนัง")) return true;
    const tName = (occ.traineeName || occ.TraineeName || "").replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
    if (tName && (staffCandidates || []).some(s => {
      const sName = (s.name || '').replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
      return sName && (sName === tName || sName.includes(tName) || tName.includes(sName));
    })) return true;
    return false;
  };

  // Helper to determine if a room is configured as a staff/welfare room
  const isWelfareRoom = (room) => {
    if (!room) return false;
    if (room.isWelfareRoom || room.hasStaffResident) return true;
    const tg = String(room.targetGroup || room.TargetGroup || "").toUpperCase();
    if (tg === "STAFF") return true;
    const rt = String(room.roomType || room.RoomType || room.type || "").trim();
    if (rt.includes("สวัสดิการ") || rt === "ครอบครัว" || rt === "ห้องครอบครัว") return true;
    const feeVal = Number(room.fee ?? room.monthlyRate ?? room.MonthlyRate ?? 0);
    if (feeVal === 0 && rt !== "นักศึกษา" && rt !== "แพทย์") return true;
    const catKey = getRoomCategoryKey(room);
    if (catKey === "STAFF_GENERAL" || catKey === "STAFF_FAMILY") return true;
    const occs = roomOccupantsMap[`${room.buildingName}-${room.roomNo}`] || roomOccupantsMap[room.roomNo] || room.CurrentResidents || room.currentResidents || [];
    if (occs.some(isStaffOccupant)) return true;
    return false;
  };

  // Helper to determine if a stay/occupant is under welfare (no rental fee)
  const isWelfareStay = (occ, room = null) => {
    if (!occ) return false;
    if (occ.isWelfare) return true;
    if (isStaffOccupant(occ)) return true;
    if (occ.feeRateType === "FREE" || occ.paymentStatus === "FREE") return true;
    const feeVal = Number(occ.feeAmount ?? occ.FeeAmount ?? -1);
    if (feeVal === 0) return true;
    if (room && isWelfareRoom(room)) return true;
    return false;
  };

  // Helper for overdue check: Welfare stays and Staff are NEVER overdue
  const isStayOverdue = (s) => {
    if (!s) return false;
    if (isWelfareStay(s)) return false;
    return (s.paymentStatus === "OVERDUE" || s.paymentStatus === "PARTIAL" || (Number(s.remainingBalance) > 0));
  };

  // Overdue occupant counts
  const activeOverdueOccupantsCount = useMemo(() => {
    return stays.filter((s) => isStayActive(s) && isStayOverdue(s)).length;
  }, [stays]);

  const pastOverdueOccupantsCount = useMemo(() => {
    return stays.filter((s) => isStayPast(s) && isStayOverdue(s)).length;
  }, [stays]);

  const allOverdueOccupantsCount = useMemo(() => {
    return stays.filter(isStayOverdue).length;
  }, [stays]);

  // Overdue rooms counts
  const allOverdueRoomsCount = useMemo(() => {
    return rooms.filter((r) => {
      const occs = roomOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomOccupantsMap[r.roomNo] || [];
      const pastOccs = roomPastOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomPastOccupantsMap[r.roomNo] || [];
      return occs.some(isStayOverdue) || pastOccs.some(isStayOverdue);
    }).length;
  }, [rooms, roomOccupantsMap, roomPastOccupantsMap]);

  const activeOverdueRoomsCount = useMemo(() => {
    return rooms.filter((r) => {
      const occs = roomOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomOccupantsMap[r.roomNo] || [];
      return occs.some(isStayOverdue);
    }).length;
  }, [rooms, roomOccupantsMap]);

  const pastOverdueRoomsCount = useMemo(() => {
    return rooms.filter((r) => {
      const pastOccs = roomPastOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomPastOccupantsMap[r.roomNo] || [];
      return pastOccs.some(isStayOverdue);
    }).length;
  }, [rooms, roomPastOccupantsMap]);

  // Dynamic filter button counts based on selectedBuilding and floorFilter
  const roomFilterCounts = useMemo(() => {
    let targetRooms = rooms;
    if (selectedBuilding !== "ALL") {
      targetRooms = targetRooms.filter((r) => r.buildingName === selectedBuilding);
    }
    if (floorFilter !== "ALL") {
      targetRooms = targetRooms.filter((r) => String(r.floor) === String(floorFilter));
    }

    let total = targetRooms.length;
    let active = 0;
    let allOverdue = 0;
    let activeOverdue = 0;
    let pastOverdue = 0;
    let future = 0;
    let vacant = 0;

    targetRooms.forEach((r) => {
      const occs = roomOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomOccupantsMap[r.roomNo] || [];
      const pastOccs = roomPastOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomPastOccupantsMap[r.roomNo] || [];
      const futureOccs = roomFutureOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomFutureOccupantsMap[r.roomNo] || [];

      const hasActiveOvd = occs.some(isStayOverdue);
      const hasPastOvd = pastOccs.some(isStayOverdue);
      const hasAnyOvd = hasActiveOvd || hasPastOvd;
      const isVct = occs.length === 0;

      if (!isVct) {
        active += 1;
      } else {
        vacant += 1;
      }

      if (futureOccs.length > 0) future += 1;
      if (hasAnyOvd) allOverdue += 1;
      if (!isVct && hasActiveOvd) activeOverdue += 1;
      if (hasPastOvd) pastOverdue += 1;
    });

    return { total, active, allOverdue, activeOverdue, pastOverdue, future, vacant };
  }, [rooms, selectedBuilding, floorFilter, roomOccupantsMap, roomPastOccupantsMap, roomFutureOccupantsMap]);

  // Open Room Details Modal
  const handleOpenRoomModal = (room) => {
    setSelectedRoom(room);
    setRoomModal(true);
  };

  const getBldgDigit = (bStr) => {
    if (!bStr) return "1";
    const m = String(bStr).match(/\d+/);
    return m ? m[0] : "1";
  };

  const getRoomNumberStr = (r) => {
    if (!r) return "";
    return String(r.roomNo || r.RoomNumber || r.roomNumber || r.RoomNo || "").trim();
  };

  const getRoomFloor = (r) => {
    if (!r) return 1;
    if (r.floor !== undefined && r.floor !== null && r.floor !== "") return Number(r.floor);
    if (r.Floor !== undefined && r.Floor !== null && r.Floor !== "") return Number(r.Floor);
    const rNum = getRoomNumberStr(r);
    if (rNum.length >= 2) {
      const flDigit = parseInt(rNum.substring(1, 2), 10);
      if (!isNaN(flDigit)) return flDigit;
    }
    return 1;
  };

  const getRoomBldgDigit = (r) => {
    if (!r) return "1";
    const val = r.buildingName || r.buildingNo || r.BuildingName || r.BuildingNo || r.dormitoryID || r.DormitoryID;
    if (val) return getBldgDigit(val);
    const rNum = getRoomNumberStr(r);
    if (rNum.length >= 1) {
      return getBldgDigit(rNum.substring(0, 1));
    }
    return "1";
  };

  // รายการห้องพักในตึกและชั้นที่เลือกจาก Master Data
  const roomsOnFloor = useMemo(() => {
    const bldgNum = getBldgDigit(stayForm.building);
    const floorNum = Number(stayForm.floor) || 1;
    return rooms.filter((r) => {
      if (!r) return false;
      const rBldgNum = getRoomBldgDigit(r);
      const rFloor = getRoomFloor(r);
      return rBldgNum === bldgNum && rFloor === floorNum;
    });
  }, [rooms, stayForm.building, stayForm.floor]);

  // Handlers สำหรับเลือกอาคาร ชั้น และเลขห้อง
  const handleStayBuildingChange = (newBuilding) => {
    const bldgNum = getBldgDigit(newBuilding);
    const currentFloor = stayForm.floor || 1;
    const bldgRooms = rooms.filter((r) => r && getRoomBldgDigit(r) === bldgNum);
    const sameFloorRoom = bldgRooms.find((r) => getRoomFloor(r) === Number(currentFloor)) || bldgRooms[0];
    const newRoomNo = sameFloorRoom ? getRoomNumberStr(sameFloorRoom) : "";
    const newFloor = sameFloorRoom ? getRoomFloor(sameFloorRoom) : currentFloor;

    setStayForm((prev) => ({
      ...prev,
      building: newBuilding,
      floor: newFloor,
      roomNo: newRoomNo || prev.roomNo
    }));
  };

  const handleStayFloorChange = (newFloor) => {
    const bldgNum = getBldgDigit(stayForm.building);
    const floorRoom = rooms.find((r) => r && getRoomBldgDigit(r) === bldgNum && getRoomFloor(r) === Number(newFloor));
    const newRoomNo = floorRoom ? getRoomNumberStr(floorRoom) : "";

    setStayForm((prev) => ({
      ...prev,
      floor: newFloor,
      roomNo: newRoomNo || prev.roomNo
    }));
  };

  const handleStayRoomNoChange = (newRoomNo) => {
    const cleanNo = String(newRoomNo || "").trim();
    const matchedRoom = rooms.find((r) => r && getRoomNumberStr(r) === cleanNo);
    if (matchedRoom) {
      const bldg = matchedRoom.buildingName || `ตึก ${matchedRoom.buildingNo || matchedRoom.BuildingNo || getRoomBldgDigit(matchedRoom)}`;
      const fl = getRoomFloor(matchedRoom);
      setStayForm((prev) => ({
        ...prev,
        roomNo: cleanNo,
        building: bldg || prev.building,
        floor: fl || prev.floor
      }));
    } else {
      setStayForm((prev) => ({
        ...prev,
        roomNo: cleanNo
      }));
    }
  };

  // Open Check-in Modal
  const handleOpenAddStay = (preselectedRoomNo, preselectedBuilding, preselectedFloor) => {
    const bldg = preselectedBuilding || (selectedBuilding === "ALL" ? "ตึก 1" : selectedBuilding);
    const fl = preselectedFloor || 1;
    const bldgNum = getBldgDigit(bldg);

    let defaultRoomNo = preselectedRoomNo;
    if (!defaultRoomNo) {
      const matchRoom = rooms.find((r) => {
        const rb = getBldgDigit(r.buildingName || r.buildingNo || r.BuildingName || r.BuildingNo || r.roomNo || r.RoomNumber);
        const rf = Number(r.floor || r.Floor || (String(r.roomNo || r.RoomNumber).length >= 2 ? String(r.roomNo || r.RoomNumber).substring(1, 2) : 1));
        return rb === bldgNum && rf === Number(fl);
      });
      defaultRoomNo = matchRoom ? (matchRoom.roomNo || matchRoom.RoomNumber) : "111";
    }

    setStayForm({
      selectedTraineeId: "",
      building: bldg,
      floor: fl,
      roomNo: defaultRoomNo,
      traineeName: "",
      traineeType: "DOCTOR",
      position: "",
      institution: "",
      checkInDate: "",
      checkOutDate: "",
      dateRange: "",
      feeAmount: 500,
      feeText: "500 บาท",
      paymentStatus: "PAID",
      remarks: "",
      phone: ""
    });
    setStayModal(true);
  };

  // จัดนักศึกษาเข้าพักจากโมเดล 3 มิติ (3D Check-in Handler)
  const handleOpenCheckInFrom3D = (room) => {
    const rNum = String(room?.RoomNumber || room?.roomNo || "");
    const bDigit = rNum.length >= 1 ? rNum.substring(0, 1) : "1";
    const bldgName = `ตึก ${bDigit}`;
    const fl = Number(room?.Floor || room?.floor || (rNum.length >= 2 ? rNum.substring(1, 2) : 1));
    handleOpenAddStay(rNum, bldgName, fl);
  };

  // จัดการเช็คเอาท์จากโมเดล 3 มิติ (3D Check-out Handler)
  const handleOpenCheckOutFrom3D = (stay, room) => {
    const stayId = stay?.StayID || stay?.stayID || stay?.id;
    const targetName = stay?.TraineeName || stay?.traineeName;
    const matchedStay = stays.find((s) => (stayId && (s.id === stayId || s.stayID === stayId)) || (targetName && s.traineeName === targetName));
    if (matchedStay) {
      handleOpenCheckoutModal(matchedStay);
    } else {
      const rNum = room?.RoomNumber || room?.roomNo || stay?.RoomNumber || stay?.roomNo;
      const bDigit = rNum ? String(rNum).substring(0, 1) : "1";
      handleOpenCheckoutModal({
        id: stayId || `temp-${Date.now()}`,
        stayID: stayId,
        traineeName: targetName || "ผู้เข้าพัก",
        roomNo: rNum,
        building: `ตึก ${bDigit}`,
        checkInDate: stay?.CheckInDate || stay?.checkInDate || new Date().toISOString().split("T")[0],
        paidAmount: 500,
        monthlyRate: 500
      });
    }
  };

  // เลือกผู้เข้าพักจากรายชื่อในระบบ (Autofill ข้อมูลที่เคยกรอกไว้ทั้งหมด)
  const handleSelectTraineeCandidate = (traineeId) => {
    if (!traineeId) {
      setStayForm((prev) => ({
        ...prev,
        selectedTraineeId: "",
        traineeName: "",
        position: "",
        institution: "",
        phone: "",
        checkInDate: "",
        checkOutDate: "",
        dateRange: ""
      }));
      return;
    }

    if (traineeId === "CUSTOM") {
      setStayForm((prev) => ({
        ...prev,
        selectedTraineeId: "CUSTOM",
        traineeName: "",
        position: "",
        institution: "",
        phone: "",
        checkInDate: "",
        checkOutDate: "",
        dateRange: ""
      }));
      return;
    }

    const candidate = traineeCandidates.find((c) => String(c.id) === String(traineeId));
    if (candidate) {
      let sDate = candidate.startDate || "";
      let eDate = candidate.endDate || "";
      if ((!sDate || !eDate) && candidate.dateRange) {
        const parsed = parseThaiDateRange(candidate.dateRange);
        if (!sDate && parsed.start) sDate = parsed.start;
        if (!eDate && parsed.end) eDate = parsed.end;
      }
      const autoRange = formatStayDateRangeThai(candidate.dateRange || "", sDate, eDate);

      // ถ้าผู้เข้าพักมีห้องพักที่จัดสรรไว้ในข้อมูลส่วนตัว
      let autoBuilding = stayForm.building;
      let autoFloor = stayForm.floor;
      let autoRoomNo = stayForm.roomNo;
      if (candidate.roomNumber) {
        autoRoomNo = candidate.roomNumber;
        const matched = rooms.find((r) => String(r.roomNo || r.RoomNumber) === String(candidate.roomNumber));
        if (matched) {
          autoBuilding = matched.buildingName || `ตึก ${matched.buildingNo || matched.BuildingNo}`;
          autoFloor = matched.floor || matched.Floor;
        } else if (/^[1-4]\d{2}$/.test(candidate.roomNumber)) {
          autoBuilding = `ตึก ${candidate.roomNumber.substring(0, 1)}`;
          autoFloor = parseInt(candidate.roomNumber.substring(1, 2), 10) || 1;
        }
      }

      setStayForm((prev) => ({
        ...prev,
        selectedTraineeId: candidate.id,
        traineeName: candidate.name,
        traineeType: candidate.type || "DOCTOR",
        position: candidate.position || "",
        institution: candidate.institution || "",
        phone: candidate.phone || prev.phone,
        checkInDate: sDate,
        checkOutDate: eDate,
        dateRange: autoRange || prev.dateRange,
        feeText: candidate.dormFee && candidate.dormFee.includes("บาท") ? candidate.dormFee : `${candidate.dormFee || 500} บาท`,
        feeAmount: 500,
        building: autoBuilding,
        floor: autoFloor,
        roomNo: autoRoomNo
      }));
    }
  };

  // Open Manage Beds Modal
  const handleOpenManageBeds = (room) => {
    if (!room) return;
    setEditingRoomBeds(room);
    setNewBedCapacity(room.capacity || 2);
    setManageBedsModal(true);
  };

  // Save Room Bed Capacity
  const handleSaveBedCapacity = async () => {
    if (!editingRoomBeds) return;
    const capacityNum = parseInt(newBedCapacity, 10);
    if (isNaN(capacityNum) || capacityNum < 1) {
      setNotifyModal({
        isOpen: true,
        type: "WARNING",
        title: "กรุณาระบุข้อมูล",
        message: "กรุณาระบุจำนวนเตียงตั้งแต่ 1 เตียงขึ้นไป"
      });
      return;
    }
    setIsSavingBeds(true);
    try {
      if (editingRoomBeds.id) {
        await axios.put(`/api/v1/dormitory/rooms/${editingRoomBeds.id}`, {
          roomID: editingRoomBeds.id,
          roomNumber: editingRoomBeds.roomNo,
          buildingName: editingRoomBeds.buildingName,
          floor: editingRoomBeds.floor,
          bedCapacity: capacityNum,
          roomType: editingRoomBeds.type || "ทั่วไป",
          monthlyRate: editingRoomBeds.fee || 500,
          dailyRate: editingRoomBeds.dailyFee || 100,
          status: editingRoomBeds.status || "AVAILABLE",
          notes: editingRoomBeds.notes || ""
        });
      }
    } catch (err) {
      console.warn("Could not persist room bed capacity to backend API, updating local state", err);
    } finally {
      setIsSavingBeds(false);
    }

    setRooms((prev) =>
      prev.map((r) => {
        const isMatch = (editingRoomBeds.id && r.id === editingRoomBeds.id) ||
          (r.roomNo === editingRoomBeds.roomNo && (r.buildingName === editingRoomBeds.buildingName || !editingRoomBeds.buildingName));
        return isMatch ? { ...r, capacity: capacityNum } : r;
      })
    );

    if (selectedRoom && (selectedRoom.id === editingRoomBeds.id || selectedRoom.roomNo === editingRoomBeds.roomNo)) {
      setSelectedRoom((prev) => ({ ...prev, capacity: capacityNum }));
    }

    setManageBedsModal(false);
  };

  // Quick increment bed capacity by 1 (e.g. from 2 to 3)
  const handleQuickAddOneBed = async (targetRoom) => {
    if (!targetRoom) return;
    const nextCapacity = (targetRoom.capacity || 2) + 1;
    try {
      if (targetRoom.id) {
        await axios.put(`/api/v1/dormitory/rooms/${targetRoom.id}`, {
          roomID: targetRoom.id,
          roomNumber: targetRoom.roomNo,
          buildingName: targetRoom.buildingName,
          floor: targetRoom.floor,
          bedCapacity: nextCapacity,
          roomType: targetRoom.type || "ทั่วไป",
          monthlyRate: targetRoom.fee || 500,
          dailyRate: targetRoom.dailyFee || 100,
          status: targetRoom.status || "AVAILABLE",
          notes: targetRoom.notes || ""
        });
      }
    } catch (err) {
      console.warn("Quick add bed update to API failed, updating local state", err);
    }

    setRooms((prev) =>
      prev.map((r) => {
        const isMatch = (targetRoom.id && r.id === targetRoom.id) ||
          (r.roomNo === targetRoom.roomNo && (r.buildingName === targetRoom.buildingName || !targetRoom.buildingName));
        return isMatch ? { ...r, capacity: nextCapacity } : r;
      })
    );

    if (selectedRoom && (selectedRoom.id === targetRoom.id || selectedRoom.roomNo === targetRoom.roomNo)) {
      setSelectedRoom((prev) => ({ ...prev, capacity: nextCapacity }));
    }
  };

  // Target room for current stayForm
  const currentSelectedRoom = useMemo(() => {
    if (!stayForm.roomNo) return null;
    return rooms.find((r) => {
      const matchRoom = String(r.roomNo) === String(stayForm.roomNo);
      const matchBuilding = !stayForm.building || r.buildingName === stayForm.building || stayForm.building.includes(r.buildingName);
      return matchRoom && matchBuilding;
    }) || rooms.find((r) => String(r.roomNo) === String(stayForm.roomNo)) || null;
  }, [rooms, stayForm.roomNo, stayForm.building]);

  // คำนวณอัตราค่าหอพักอัตโนมัติตาม Master Data และช่วงเวลา (ไม่ต้องกรอกราคาเอง)
  const autoMasterFeeInfo = useMemo(() => {
    const isB2 = stayForm.building && (stayForm.building.includes("2") || stayForm.building.includes("แพทย์"));
    const dailyRate = currentSelectedRoom?.dailyFee || (isB2 ? 100 : 17.86);
    const monthlyRate = currentSelectedRoom?.fee || 500;

    let sDate = stayForm.checkInDate || "";
    let eDate = stayForm.checkOutDate || "";
    if ((!sDate || !eDate) && stayForm.dateRange) {
      const parsed = parseThaiDateRange(stayForm.dateRange, 2569);
      if (!sDate && parsed.start) sDate = parsed.start;
      if (!eDate && parsed.end) eDate = parsed.end;
    }

    if (sDate && eDate) {
      const days = countDaysBetween(sDate, eDate);
      if (isB2) {
        const amount = days * dailyRate;
        return {
          amount,
          text: `${amount.toLocaleString()} บาท (${days} วัน × ${dailyRate} บ./วัน)`,
          rateDesc: `วันละ ${dailyRate} บาท (ตึก 2 หอพักแพทย์)`
        };
      } else {
        const calc = calculateDormFeeByDays(stayForm.building, days);
        return {
          amount: calc.totalAmount,
          text: `${calc.totalAmount.toLocaleString()} บาท (${calc.breakdownText})`,
          rateDesc: `เดือนละ ${monthlyRate} บาท`
        };
      }
    }

    if (isB2) {
      return {
        amount: dailyRate,
        text: `วันละ ${dailyRate} บาท (ตึก 2 หอพักแพทย์)`,
        rateDesc: `วันละ ${dailyRate} บาท (ตึก 2 หอพักแพทย์)`
      };
    } else {
      return {
        amount: monthlyRate,
        text: `${monthlyRate.toLocaleString()} บาท/เดือน`,
        rateDesc: `เดือนละ ${monthlyRate.toLocaleString()} บาท`
      };
    }
  }, [stayForm.building, stayForm.checkInDate, stayForm.checkOutDate, stayForm.dateRange, currentSelectedRoom]);

  // Compute overlapping stays for stayForm
  const overlappingOccupantsInfo = useMemo(() => {
    if (!stayForm.roomNo) return { overlaps: [], isFull: false, capacity: 2, count: 0, hasDates: false };

    let newStart = stayForm.checkInDate || "";
    let newEnd = stayForm.checkOutDate || "";
    if ((!newStart || !newEnd) && stayForm.dateRange) {
      const parsed = parseThaiDateRange(stayForm.dateRange, 2569);
      if (!newStart && parsed.start) newStart = parsed.start;
      if (!newEnd && parsed.end) newEnd = parsed.end;
    }

    const roomCapacity = currentSelectedRoom?.capacity || 2;

    const targetRoomStays = stays.filter((s) => {
      if (stayForm.id && s.id === stayForm.id) return false;
      if (s.isDeleted || s.IsDeleted || s.status === "ยกเลิก" || s.isActive === false || s.IsActive === false) return false;
      const matchRoom = String(s.roomNo) === String(stayForm.roomNo);
      const matchBuilding = !stayForm.building || s.building === stayForm.building || stayForm.building.includes(s.building || "");
      return matchRoom && matchBuilding;
    });

    if (!newStart && !newEnd) {
      const activeStays = targetRoomStays.filter(isStayActive);
      return {
        overlaps: activeStays,
        isFull: activeStays.length >= roomCapacity,
        capacity: roomCapacity,
        count: activeStays.length,
        hasDates: false,
        startDate: "",
        endDate: ""
      };
    }

    const overlaps = targetRoomStays.filter((s) => {
      const sRange = getStayDateRange(s);
      if (!sRange.start && !sRange.end) {
        return isStayActive(s);
      }
      return checkDateRangesOverlap(newStart, newEnd, sRange.start, sRange.end);
    });

    return {
      overlaps,
      isFull: overlaps.length >= roomCapacity,
      capacity: roomCapacity,
      count: overlaps.length,
      hasDates: true,
      startDate: newStart,
      endDate: newEnd
    };
  }, [stays, stayForm, currentSelectedRoom]);

  // Save Stay
  const handleSaveStay = async () => {
    if (!stayForm.traineeName || !stayForm.traineeName.trim()) {
      setNotifyModal({
        isOpen: true,
        type: "WARNING",
        title: "กรุณาระบุข้อมูล",
        message: "กรุณาเลือกหรือกรอกชื่อ-สกุลผู้เข้าพัก"
      });
      return;
    }

    // ตรวจสอบความจุเตียงและช่วงเวลาที่ชนกัน (Bed capacity & date overlap check)
    if (overlappingOccupantsInfo.isFull) {
      const cap = overlappingOccupantsInfo.capacity;
      const count = overlappingOccupantsInfo.count;
      const overlapList = overlappingOccupantsInfo.overlaps
        .map((s, idx) => `• ${s.traineeName} (${s.institution || s.position || '-'}) [ช่วง: ${formatStayDateRangeThai(s.dateRange, s.checkInDate, s.checkOutDate)}]`)
        .join("\n");

      setNotifyModal({
        isOpen: true,
        type: "WARNING",
        title: "⛔ ไม่สามารถเพิ่มผู้เข้าพักเข้าห้องนี้ได้",
        message: `ห้อง ${stayForm.roomNo} (${stayForm.building}) มีเตียงที่กำหนดไว้ ${cap} เตียง\n` +
          `ในช่วงเวลาดังกล่าวมีผู้พักอาศัยซ้อนทับกันอยู่แล้ว ${count} คน (เตียงเต็ม):\n\n${overlapList}\n\n` +
          `⚠️ ไม่สามารถเพิ่มคนที่ ${count + 1} ได้\n` +
          `จะเพิ่มได้เมื่อ "Manage จัดการเตียงเพิ่ม" ให้ห้องนี้มีเตียงเพียงพอก่อน หรือเลือกห้องพักอื่น`
      });
      return;
    }
    const finalDateRange = formatStayDateRangeThai(stayForm.dateRange, stayForm.checkInDate, stayForm.checkOutDate);
    const fee = autoMasterFeeInfo.amount || 500;
    const paid = stayForm.paymentStatus === "PAID" ? fee : 0;
    const rem = stayForm.paymentStatus === "PAID" ? 0 : fee;
    const isB2 = stayForm.building && (stayForm.building.includes("2") || stayForm.building.includes("แพทย์"));
    const feeRateType = isB2 ? "DAILY" : "MONTHLY";

    const newStay = {
      ...stayForm,
      id: `stay-${Date.now()}`,
      dateRange: finalDateRange,
      checkInDate: stayForm.checkInDate,
      checkOutDate: stayForm.checkOutDate,
      feeAmount: fee,
      paidAmount: paid,
      remainingBalance: rem,
      feeText: stayForm.paymentStatus === "PAID" ? `จ่ายแล้ว ${fee.toLocaleString()} บาท` : `ค้างชำระ ${rem.toLocaleString()} บาท`,
      prorationText: autoMasterFeeInfo.text
    };

    try {
      const roomObj = rooms.find((r) => r.roomNo === stayForm.roomNo);
      const isCurrent = isStayActive({
        checkInDate: stayForm.checkInDate,
        checkOutDate: stayForm.checkOutDate,
        dateRange: finalDateRange,
        fiscalYear: 2569
      });
      await axios.post("/api/v1/dormitory/stays", {
        roomID: roomObj?.id || 1,
        buildingName: stayForm.building,
        roomNumber: stayForm.roomNo,
        floor: Number(stayForm.floor) || 1,
        traineeType: stayForm.traineeType || "DOCTOR",
        traineeName: stayForm.traineeName,
        positionText: stayForm.position,
        institutionName: stayForm.institution,
        contactPhone: stayForm.phone,
        checkInDate: stayForm.checkInDate || null,
        checkOutDate: stayForm.checkOutDate || null,
        stayDateRangeText: finalDateRange,
        fiscalYear: 2569,
        feeRateType: feeRateType,
        feeAmount: fee,
        paidAmount: paid,
        remainingBalance: rem,
        paymentStatus: stayForm.paymentStatus || "PAID",
        prorationFormula: autoMasterFeeInfo.text,
        remarks: stayForm.remarks,
        isCurrentStay: isCurrent
      });

      // ปรับปรุงประเภทห้องเป็น "นักศึกษา" (STUDENT) อัตโนมัติเมื่อนักศึกษาเข้าพัก
      if (roomObj && roomObj.id) {
        try {
          await axios.put(`/api/v1/dormitory/rooms/${roomObj.id}`, {
            targetGroup: "STUDENT",
            roomType: "นักศึกษา"
          });
        } catch (rErr) {
          // ignore if room update is not permitted
        }
      }

      fetchDormitoryData();
    } catch (err) {
      console.warn("Could not persist new stay to DB, updating local state", err);
    }

    setStays((prev) => [newStay, ...prev.filter(s => s.id !== newStay.id)]);
    setStayModal(false);
    setNotifyModal({
      isOpen: true,
      type: "SUCCESS",
      title: "บันทึกการเข้าพักสำเร็จ",
      message: `บันทึกการเข้าพักของ "${newStay.traineeName}" (${newStay.building} ห้อง ${newStay.roomNo}) เรียบร้อยแล้ว`
    });
  };

  // Filter Stays for Table
  const filteredStays = useMemo(() => {
    return stays.filter((s) => {
      if (selectedBuilding !== "ALL" && s.building !== selectedBuilding) return false;
      if (floorFilter !== "ALL" && s.floor !== Number(floorFilter)) return false;
      if (paymentFilter !== "ALL" && s.paymentStatus !== paymentFilter) return false;
      if (traineeTypeFilter !== "ALL" && s.traineeType !== traineeTypeFilter) return false;
      if (stayStatusFilter === "ACTIVE" && !isStayActive(s)) return false;
      if (stayStatusFilter === "FUTURE" && !isStayFuture(s)) return false;
      if (stayStatusFilter === "ENDED" && !isStayPast(s)) return false;
      if (searchTerm) {
        const query = searchTerm.toLowerCase();
        const matchName = s.traineeName && s.traineeName.toLowerCase().includes(query);
        const matchRoom = s.roomNo && s.roomNo.toLowerCase().includes(query);
        const matchInst = s.institution && s.institution.toLowerCase().includes(query);
        if (!matchName && !matchRoom && !matchInst) return false;
      }
      return true;
    });
  }, [stays, selectedBuilding, floorFilter, paymentFilter, traineeTypeFilter, stayStatusFilter, searchTerm]);

  const totalPages = Math.ceil(filteredStays.length / pageSize) || 1;
  const paginatedStays = filteredStays.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Buildings to display
  const buildingsToRender = selectedBuilding === "ALL"
    ? BUILDINGS
    : BUILDINGS.filter(b => b.shortName === selectedBuilding);

  return (
    <div className={`${classes.container} trainee-dormitory-page`} style={{ fontFamily: '"Sarabun", sans-serif' }}>
      <style>{`
        .trainee-dormitory-page,
        .trainee-dormitory-page *,
        .trainee-dormitory-page table,
        .trainee-dormitory-page th,
        .trainee-dormitory-page td,
        .trainee-dormitory-page tr,
        .trainee-dormitory-page span,
        .trainee-dormitory-page div,
        .trainee-dormitory-page p,
        .trainee-dormitory-page h1,
        .trainee-dormitory-page h2,
        .trainee-dormitory-page h3,
        .trainee-dormitory-page h4,
        .trainee-dormitory-page h5,
        .trainee-dormitory-page h6,
        .trainee-dormitory-page button,
        .trainee-dormitory-page input,
        .trainee-dormitory-page select,
        .trainee-dormitory-page textarea,
        .trainee-dormitory-page .btn,
        .trainee-dormitory-page .badge,
        .modal,
        .modal *,
        .modal-dialog,
        .modal-content,
        .modal-body,
        .modal-header,
        .modal-footer {
          font-family: "Sarabun", sans-serif !important;
        }
      `}</style>
      {/* Main Container Card: Dedicated to Dormitory Management */}
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="info" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", backgroundColor: "#00838f" }}>
              <div>
                <h4 style={{ color: "#fff", margin: 0, fontWeight: "bold" }}>
                  <HotelIcon style={{ verticalAlign: "middle", marginRight: "8px" }} />
                  ระบบจัดการหอพักและห้องพัก
                </h4>
                <p style={{ color: "rgba(255,255,255,0.85)", margin: "4px 0 0 0", fontSize: "0.88rem" }}>
                  โรงพยาบาลโรคผิวหนังเขตร้อนภาคใต้ จังหวัดตรัง | ผังห้องพักแยกตามตึกและชั้น, จัดสรรห้องพัก, และประวัติการพัก
                </p>
              </div>
              <div style={{ marginTop: "8px" }}>
                <Button
                  color="white"
                  size="sm"
                  style={{ fontWeight: "bold", color: "#00695c", marginRight: "10px" }}
                  onClick={() => history.push("/trainee/master-data")}
                >
                  <EditIcon style={{ fontSize: "1.1rem", verticalAlign: "middle", marginRight: "4px" }} />
                  จัดการเลขห้องใน Master Data
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  style={{ fontWeight: "bold" }}
                  onClick={() => handleOpenAddStay()}
                >
                  <AddIcon style={{ fontSize: "1.1rem", verticalAlign: "middle", marginRight: "4px" }} />
                  + จัดสรรห้องพัก / เช็คอิน
                </Button>
              </div>
            </CardHeader>

            {/* Navigation Tabs */}
            <div style={{ borderBottom: "2px solid #e0e0e0", backgroundColor: "#fafafa", padding: "10px 15px 0 15px" }}>
              <Nav tabs style={{ borderBottom: "none" }}>
                <NavItem>
                  <NavLink
                    style={{
                      cursor: "pointer",
                      fontWeight: activeTab === 0 ? "bold" : "normal",
                      color: activeTab === 0 ? "#00838f" : "#555",
                      borderBottom: activeTab === 0 ? "3px solid #00838f" : "none",
                      backgroundColor: activeTab === 0 ? "#fff" : "transparent",
                      fontSize: "1rem",
                      padding: "10px 20px"
                    }}
                    onClick={() => setActiveTab(0)}
                  >
                    <MeetingRoomIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                    ผังห้องพักและสถานะอาคาร (Floor Grid)
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    style={{
                      cursor: "pointer",
                      fontWeight: activeTab === 1 ? "bold" : "normal",
                      color: activeTab === 1 ? "#00838f" : "#555",
                      borderBottom: activeTab === 1 ? "3px solid #00838f" : "none",
                      backgroundColor: activeTab === 1 ? "#fff" : "transparent",
                      fontSize: "1rem",
                      padding: "10px 20px"
                    }}
                    onClick={() => setActiveTab(1)}
                  >
                    <AttachMoneyIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                    ทะเบียนประวัติการเข้าพักและค่าหอพัก ({stays.length} รายการ)
                  </NavLink>
                </NavItem>
                {/* ซ่อน tab 💰 ตารางติดตามค่าหอพักรายเดือน (Checklist การเงิน/บริหาร) ชั่วคราวตามที่ผู้ใช้ร้องขอ (เก็บโค้ดไว้ใช้งานภายหลัง) */}
                {/*
                <NavItem>
                  <NavLink
                    style={{
                      cursor: "pointer",
                      fontWeight: activeTab === 2 ? "bold" : "normal",
                      color: activeTab === 2 ? "#00838f" : "#555",
                      borderBottom: activeTab === 2 ? "3px solid #00838f" : "none",
                      backgroundColor: activeTab === 2 ? "#fff" : "transparent",
                      fontSize: "1rem",
                      padding: "10px 20px"
                    }}
                    onClick={() => setActiveTab(2)}
                  >
                    <CheckCircleIcon style={{ verticalAlign: "middle", marginRight: "6px", color: activeTab === 2 ? "#10b981" : "#888" }} />
                    💰 ตารางติดตามค่าหอพักรายเดือน (Checklist การเงิน/บริหาร)
                  </NavLink>
                </NavItem>
                */}
              </Nav>
            </div>

            <CardBody>
              {/* TAB 0: ผังห้องพัก (Floor Grid 4 Buildings x 3 Floors) */}
              {activeTab === 0 && (
                <div>
                  {/* View Mode Switcher Toolbar */}
                  <div
                    className="d-flex align-items-center justify-content-between mb-3 flex-wrap p-2"
                    style={{
                      gap: "10px",
                      backgroundColor: "#ffffff",
                      borderRadius: "14px",
                      border: "1.5px solid #cbd5e1"
                    }}
                  >
                    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                      <button
                        type="button"
                        onClick={() => setViewMode("3D")}
                        style={{
                          padding: "8px 18px",
                          borderRadius: "10px",
                          border: viewMode === "3D" ? "2px solid #00838f" : "1.5px solid #cbd5e1",
                          backgroundColor: viewMode === "3D" ? "#e0f2f1" : "#ffffff",
                          color: viewMode === "3D" ? "#00838f" : "#64748b",
                          fontWeight: "600",
                          fontFamily: '"Sarabun", sans-serif',
                          fontSize: "0.92rem",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          boxShadow: viewMode === "3D" ? "0 2px 8px rgba(0,131,143,0.2)" : "none",
                          transition: "all 0.2s ease"
                        }}
                      >
                        🏢 มุมมอง 3 มิติ (3D Interactive Building)
                      </button>
                      <button
                        type="button"
                        onClick={() => setViewMode("GRID")}
                        style={{
                          padding: "8px 18px",
                          borderRadius: "10px",
                          border: viewMode === "GRID" ? "2px solid #00838f" : "1.5px solid #cbd5e1",
                          backgroundColor: viewMode === "GRID" ? "#e0f2f1" : "#ffffff",
                          color: viewMode === "GRID" ? "#00838f" : "#64748b",
                          fontWeight: "600",
                          fontFamily: '"Sarabun", sans-serif',
                          fontSize: "0.92rem",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          boxShadow: viewMode === "GRID" ? "0 2px 8px rgba(0,131,143,0.2)" : "none",
                          transition: "all 0.2s ease"
                        }}
                      >
                        📋 ผังตารางห้องพัก (Floor Grid)
                      </button>
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                      <span
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: "600",
                          fontFamily: '"Sarabun", sans-serif',
                          color: "#0f766e",
                          backgroundColor: "#ccfbf1",
                          padding: "5px 12px",
                          borderRadius: "10px",
                          border: "1px solid #99f6e4"
                        }}
                      >
                        🎓 เฉพาะนักศึกษา & แพทย์ฝึกอบรม (ห้องเจ้าหน้าที่จะล็อกเป็น Read-only)
                      </span>
                    </div>
                  </div>

                  {viewMode === "3D" ? (
                    <Dormitory3DBuildingView
                      buildings={BUILDINGS}
                      rooms={rooms}
                      staffCandidates={staffCandidates}
                      isTraineeMode={true}
                      onSelectRoomCheckIn={handleOpenCheckInFrom3D}
                      onSelectRoomCheckOut={handleOpenCheckOutFrom3D}
                      onViewGridToggle={() => setViewMode("GRID")}
                    />
                  ) : (
                    <div>
                      {/* Summary Status Ribbon */}
                  <div className="row mb-3">
                    <div className="col-xl col-md-4 col-sm-6 mb-2">
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: roomOccupancyFilter === "ALL" ? "2px solid #00838f" : "1px solid #e2e8f0",
                          borderLeft: "5px solid #00838f",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          boxShadow: roomOccupancyFilter === "ALL" ? "0 2px 6px rgba(0,131,143,0.25)" : "0 1px 3px rgba(0,0,0,0.05)",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={() => setRoomOccupancyFilter("ALL")}
                        title="คลิกเพื่อแสดงห้องพักทั้งหมด"
                      >
                        <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: "600" }}>ห้องพักทั้งหมด</div>
                        <div style={{ fontSize: "1.35rem", fontWeight: "bold", color: "#0f172a" }}>
                          {rooms.length} <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#64748b" }}>ห้อง</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl col-md-4 col-sm-6 mb-2">
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: roomOccupancyFilter === "ACTIVE" ? "2px solid #0288d1" : "1px solid #e2e8f0",
                          borderLeft: "5px solid #0288d1",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          boxShadow: roomOccupancyFilter === "ACTIVE" ? "0 2px 6px rgba(2,136,209,0.25)" : "0 1px 3px rgba(0,0,0,0.05)",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={() => setRoomOccupancyFilter("ACTIVE")}
                        title="คลิกเพื่อกรองเฉพาะห้องที่มีคนพักอยู่"
                      >
                        <div style={{ fontSize: "0.78rem", color: "#0288d1", fontWeight: "600" }}>กำลังพักอาศัย (อยู่ในช่วง)</div>
                        <div style={{ fontSize: "1.35rem", fontWeight: "bold", color: "#0288d1" }}>
                          {occupiedRoomsCount} <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#64748b" }}>ห้อง ({activeOccupantsCount} คน)</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl col-md-4 col-sm-6 mb-2">
                      <div
                        style={{
                          backgroundColor: "#fff5f5",
                          border: roomOccupancyFilter === "OVERDUE" || roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "ACTIVE_OVERDUE" || roomOccupancyFilter === "PAST_OVERDUE" ? "2px solid #ef4444" : "1px solid #fed7d7",
                          borderLeft: "5px solid #ef4444",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          boxShadow: roomOccupancyFilter === "OVERDUE" || roomOccupancyFilter === "ALL_OVERDUE" ? "0 2px 6px rgba(239,68,68,0.25)" : "0 1px 3px rgba(0,0,0,0.05)",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={() => setRoomOccupancyFilter(roomOccupancyFilter === "ALL_OVERDUE" ? "ALL" : "ALL_OVERDUE")}
                        title="คลิกเพื่อกรองห้องที่มีค้างชำระทั้งหมด (รวมที่ออกแล้ว)"
                      >
                        <div style={{ fontSize: "0.78rem", color: "#dc2626", fontWeight: "600" }}>
                          <WarningIcon style={{ fontSize: "0.95rem", verticalAlign: "middle", marginRight: "3px", color: "#dc2626" }} />
                          มีค้างชำระ (รวมที่ออกแล้ว)
                        </div>
                        <div style={{ fontSize: "1.35rem", fontWeight: "bold", color: "#dc2626" }}>
                          {allOverdueRoomsCount} <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#ef4444" }}>ห้อง ({allOverdueOccupantsCount} คน)</span>
                        </div>
                        <div style={{ fontSize: "0.7rem", color: "#b91c1c", marginTop: "1px" }}>
                          ยังอยู่ {activeOverdueOccupantsCount} คน • ออกแล้ว {pastOverdueOccupantsCount} คน
                        </div>
                      </div>
                    </div>
                    <div className="col-xl col-md-4 col-sm-6 mb-2">
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: roomOccupancyFilter === "VACANT" ? "2px solid #10b981" : "1px solid #e2e8f0",
                          borderLeft: "5px solid #10b981",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          boxShadow: roomOccupancyFilter === "VACANT" ? "0 2px 6px rgba(16,185,129,0.25)" : "0 1px 3px rgba(0,0,0,0.05)",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={() => setRoomOccupancyFilter("VACANT")}
                        title="คลิกเพื่อกรองเฉพาะห้องว่าง"
                      >
                        <div style={{ fontSize: "0.78rem", color: "#10b981", fontWeight: "600" }}>ห้องว่างพร้อมรับเข้าพัก</div>
                        <div style={{ fontSize: "1.35rem", fontWeight: "bold", color: "#10b981" }}>
                          {rooms.length - occupiedRoomsCount} <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#64748b" }}>ห้อง</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl col-md-4 col-sm-6 mb-2">
                      <div
                        style={{
                          backgroundColor: "#ffffff",
                          border: roomOccupancyFilter === "FUTURE" ? "2px solid #0284c7" : "1px solid #e2e8f0",
                          borderLeft: "5px solid #0284c7",
                          borderRadius: "8px",
                          padding: "10px 14px",
                          boxShadow: roomOccupancyFilter === "FUTURE" ? "0 2px 6px rgba(2,132,199,0.25)" : "0 1px 3px rgba(0,0,0,0.05)",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={() => setRoomOccupancyFilter(roomOccupancyFilter === "FUTURE" ? "ALL" : "FUTURE")}
                        title="คลิกเพื่อกรองห้องที่มีการจองล่วงหน้าในอนาคต (ไม่ใช่ตอนนี้)"
                      >
                        <div style={{ fontSize: "0.78rem", color: "#0284c7", fontWeight: "600" }}>⏳ จองล่วงหน้า (ไม่ใช่ตอนนี้)</div>
                        <div style={{ fontSize: "1.35rem", fontWeight: "bold", color: "#0284c7" }}>
                          {futureOccupantsCount} <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#64748b" }}>คน (ยังไม่ถึงเวลา)</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-xl col-md-4 col-sm-6 mb-2">
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderLeft: "5px solid #94a3b8", borderRadius: "8px", padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                        <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: "600" }}>ออกไปแล้ว (พ้นวันฝึก)</div>
                        <div style={{ fontSize: "1.35rem", fontWeight: "bold", color: "#64748b" }}>
                          {pastOccupantsCount} <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#64748b" }}>คน</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Building & Floor & Room Status Selector Filter Bar */}
                  <div className="p-3 mb-3" style={{ backgroundColor: "#e0f2f1", borderRadius: "8px", border: "1px solid #b2dfdb" }}>
                    {/* Row 1: Building & Floor */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between pb-2 mb-2" style={{ borderBottom: "1px dashed #80cbc4" }}>
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="mr-2" style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#00695c" }}>
                          <BusinessIcon style={{ fontSize: "1.2rem", verticalAlign: "middle", marginRight: "3px" }} /> เลือกอาคาร:
                        </span>
                        {["ALL", "ตึก 1", "ตึก 2", "ตึก 3", "ตึก 4"].map((b) => (
                          <button
                            key={b}
                            type="button"
                            className={`btn btn-sm mr-1 mb-1 ${selectedBuilding === b ? "btn-info font-weight-bold" : "btn-outline-info"}`}
                            style={{ fontSize: "0.85rem", borderRadius: "16px", padding: "4px 12px" }}
                            onClick={() => setSelectedBuilding(b)}
                          >
                            {b === "ALL" ? "🏢 ทุกอาคาร" : `🏢 ${b}`}
                          </button>
                        ))}
                      </div>

                      <div className="d-flex align-items-center mt-2 mt-md-0">
                        <span className="mr-2" style={{ fontSize: "0.88rem", fontWeight: "600", color: "#00695c" }}>ชั้น:</span>
                        <select
                          className="form-control form-control-sm"
                          style={{ width: "135px", borderRadius: "6px", border: "1px solid #80cbc4" }}
                          value={floorFilter}
                          onChange={(e) => setFloorFilter(e.target.value)}
                        >
                          <option value="ALL">ทุกชั้น (ชั้น 1-3)</option>
                          <option value="3">ชั้น 3</option>
                          <option value="2">ชั้น 2</option>
                          <option value="1">ชั้น 1</option>
                        </select>
                      </div>
                    </div>

                    {/* Row 2: Room Status Filter */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between pt-1">
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="mr-2" style={{ fontSize: "0.9rem", fontWeight: "bold", color: "#00695c" }}>
                          <FilterListIcon style={{ fontSize: "1.2rem", verticalAlign: "middle", marginRight: "3px" }} /> กรองห้องพัก:
                        </span>

                        <button
                          type="button"
                          className="btn btn-sm mr-1 mb-1"
                          style={{
                            fontSize: "0.83rem",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontWeight: roomOccupancyFilter === "ALL" ? "bold" : "normal",
                            backgroundColor: roomOccupancyFilter === "ALL" ? "#00695c" : "#ffffff",
                            color: roomOccupancyFilter === "ALL" ? "#ffffff" : "#00695c",
                            border: "1px solid #00695c",
                            boxShadow: roomOccupancyFilter === "ALL" ? "0 2px 4px rgba(0,105,92,0.3)" : "none"
                          }}
                          onClick={() => setRoomOccupancyFilter("ALL")}
                        >
                          📋 ทุกห้องพัก ({roomFilterCounts.total})
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm mr-1 mb-1"
                          style={{
                            fontSize: "0.83rem",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontWeight: roomOccupancyFilter === "ACTIVE" ? "bold" : "normal",
                            backgroundColor: roomOccupancyFilter === "ACTIVE" ? "#0288d1" : "#ffffff",
                            color: roomOccupancyFilter === "ACTIVE" ? "#ffffff" : "#0288d1",
                            border: "1px solid #0288d1",
                            boxShadow: roomOccupancyFilter === "ACTIVE" ? "0 2px 4px rgba(2,136,209,0.3)" : "none"
                          }}
                          onClick={() => setRoomOccupancyFilter("ACTIVE")}
                        >
                          🟢 เฉพาะคนที่ยังอยู่ ({roomFilterCounts.active})
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm mr-1 mb-1"
                          style={{
                            fontSize: "0.83rem",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontWeight: roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "OVERDUE" ? "bold" : "normal",
                            backgroundColor: roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "OVERDUE" ? "#d32f2f" : "#ffffff",
                            color: roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "OVERDUE" ? "#ffffff" : "#d32f2f",
                            border: "1px solid #d32f2f",
                            boxShadow: roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "OVERDUE" ? "0 2px 4px rgba(211,47,47,0.3)" : "none"
                          }}
                          onClick={() => setRoomOccupancyFilter("ALL_OVERDUE")}
                          title="กรองห้องที่มีค้างชำระทั้งหมด ทั้งคนที่ยังอยู่และคนที่ออกไปแล้ว"
                        >
                          🔴 รวมที่ยังไม่จ่ายทั้งหมด ({roomFilterCounts.allOverdue})
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm mr-1 mb-1"
                          style={{
                            fontSize: "0.83rem",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontWeight: roomOccupancyFilter === "ACTIVE_OVERDUE" ? "bold" : "normal",
                            backgroundColor: roomOccupancyFilter === "ACTIVE_OVERDUE" ? "#ed6c02" : "#ffffff",
                            color: roomOccupancyFilter === "ACTIVE_OVERDUE" ? "#ffffff" : "#ed6c02",
                            border: "1px solid #ed6c02",
                            boxShadow: roomOccupancyFilter === "ACTIVE_OVERDUE" ? "0 2px 4px rgba(237,108,2,0.3)" : "none"
                          }}
                          onClick={() => setRoomOccupancyFilter("ACTIVE_OVERDUE")}
                          title="กรองห้องที่ผู้พักยังอยู่ ณ ปัจจุบัน และยังค้างชำระ"
                        >
                          ⚠️ คนที่ยังอยู่ + ยังไม่จ่าย ({roomFilterCounts.activeOverdue})
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm mr-1 mb-1"
                          style={{
                            fontSize: "0.83rem",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontWeight: roomOccupancyFilter === "PAST_OVERDUE" ? "bold" : "normal",
                            backgroundColor: roomOccupancyFilter === "PAST_OVERDUE" ? "#7b1fa2" : "#ffffff",
                            color: roomOccupancyFilter === "PAST_OVERDUE" ? "#ffffff" : "#7b1fa2",
                            border: "1px solid #7b1fa2",
                            boxShadow: roomOccupancyFilter === "PAST_OVERDUE" ? "0 2px 4px rgba(123,31,162,0.3)" : "none"
                          }}
                          onClick={() => setRoomOccupancyFilter("PAST_OVERDUE")}
                          title="กรองห้องที่ผู้พักออกไปแล้วแต่ยังมีประวัติค้างชำระ"
                        >
                          🚪 ออกไปแล้ว + ยังไม่จ่าย ({roomFilterCounts.pastOverdue})
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm mr-1 mb-1"
                          style={{
                            fontSize: "0.83rem",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontWeight: roomOccupancyFilter === "FUTURE" ? "bold" : "normal",
                            backgroundColor: roomOccupancyFilter === "FUTURE" ? "#0284c7" : "#ffffff",
                            color: roomOccupancyFilter === "FUTURE" ? "#ffffff" : "#0284c7",
                            border: "1px solid #0284c7",
                            boxShadow: roomOccupancyFilter === "FUTURE" ? "0 2px 4px rgba(2,132,199,0.3)" : "none"
                          }}
                          onClick={() => setRoomOccupancyFilter("FUTURE")}
                          title="กรองห้องที่มีการจองล่วงหน้าในอนาคต (ไม่ใช่ตอนนี้)"
                        >
                          ⏳ มีจองล่วงหน้า ({roomFilterCounts.future})
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm mr-1 mb-1"
                          style={{
                            fontSize: "0.83rem",
                            borderRadius: "16px",
                            padding: "4px 12px",
                            fontWeight: roomOccupancyFilter === "VACANT" ? "bold" : "normal",
                            backgroundColor: roomOccupancyFilter === "VACANT" ? "#2e7d32" : "#ffffff",
                            color: roomOccupancyFilter === "VACANT" ? "#ffffff" : "#2e7d32",
                            border: "1px solid #2e7d32",
                            boxShadow: roomOccupancyFilter === "VACANT" ? "0 2px 4px rgba(46,125,50,0.3)" : "none"
                          }}
                          onClick={() => setRoomOccupancyFilter("VACANT")}
                          title="กรองห้องว่างพร้อมรับเข้าพัก"
                        >
                          ⚪ เฉพาะห้องว่าง ({roomFilterCounts.vacant})
                        </button>
                      </div>

                      {roomOccupancyFilter !== "ALL" && (
                        <div className="mt-1 mt-md-0">
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            style={{ fontSize: "0.82rem", textDecoration: "underline", fontWeight: "600" }}
                            onClick={() => setRoomOccupancyFilter("ALL")}
                          >
                            ✕ ล้างตัวกรองสถานะ
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Render Buildings */}
                  {buildingsToRender.map((bldg) => {
                    const bldgRooms = rooms.filter((r) => r.buildingName === bldg.shortName);
                    const bldgOccupied = bldgRooms.filter((r) => (roomOccupantsMap[`${bldg.shortName}-${r.roomNo}`]?.length || 0) > 0).length;
                    const bldgMatchingRooms = bldgRooms.filter((r) => {
                      const occs = roomOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomOccupantsMap[r.roomNo] || [];
                      const pastOccs = roomPastOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomPastOccupantsMap[r.roomNo] || [];
                      const futureOccs = roomFutureOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomFutureOccupantsMap[r.roomNo] || [];

                      const hasActiveOvd = occs.some(isStayOverdue);
                      const hasPastOvd = pastOccs.some(isStayOverdue);
                      const isVct = occs.length === 0;

                      if (roomOccupancyFilter === "ACTIVE") return !isVct;
                      if (roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "OVERDUE") return hasActiveOvd || hasPastOvd;
                      if (roomOccupancyFilter === "ACTIVE_OVERDUE") return !isVct && hasActiveOvd;
                      if (roomOccupancyFilter === "PAST_OVERDUE") return hasPastOvd;
                      if (roomOccupancyFilter === "FUTURE") return futureOccs.length > 0;
                      if (roomOccupancyFilter === "VACANT") return isVct;
                      return true;
                    });
                    const floorsList = floorFilter === "ALL" ? [3, 2, 1] : [Number(floorFilter)]; // เรียงจากชั้น 3 ลงมาชั้น 1 ตามกายภาพตึกจริง

                    return (
                      <div key={bldg.id} style={{ marginBottom: "25px", border: "1px solid #b2dfdb", borderRadius: "8px", padding: "16px", backgroundColor: "#fafcfc" }}>
                        {/* Building Title Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3 pb-2" style={{ borderBottom: "2px solid #80cbc4" }}>
                          <div>
                            <h5 style={{ margin: 0, fontWeight: "bold", color: "#00695c" }}>
                              🏢 {bldg.name}
                            </h5>
                            <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                              {bldg.note} | จำนวน {bldg.totalFloors} ชั้น
                            </span>
                          </div>
                          <div>
                            {roomOccupancyFilter !== "ALL" && (
                              <Badge color={bldgMatchingRooms.length > 0 ? "warning" : "secondary"} style={{ marginRight: "6px", fontSize: "0.82rem" }}>
                                ตรงตามตัวกรอง {bldgMatchingRooms.length} ห้อง
                              </Badge>
                            )}
                            <Badge color="info" style={{ marginRight: "6px", fontSize: "0.82rem" }}>
                              ห้องทั้งหมด {bldgRooms.length} ห้อง
                            </Badge>
                            <Badge color={bldgOccupied === bldgRooms.length && bldgRooms.length > 0 ? "warning" : "success"} style={{ fontSize: "0.82rem" }}>
                              เข้าพัก {bldgOccupied} ห้อง / ว่าง {bldgRooms.length - bldgOccupied} ห้อง
                            </Badge>
                          </div>
                        </div>

                        {/* Floors Grid for this building */}
                        {bldgMatchingRooms.length === 0 && roomOccupancyFilter !== "ALL" ? (
                          <div style={{ backgroundColor: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: "8px", padding: "20px", textAlign: "center", color: "#64748b" }}>
                            <div style={{ fontSize: "0.95rem", fontWeight: "600", color: "#475569" }}>
                              ไม่มีห้องพักใน{bldg.name} ที่ตรงกับเงื่อนไข "{
                                roomOccupancyFilter === "ACTIVE"
                                  ? "เฉพาะคนที่ยังอยู่"
                                  : (roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "OVERDUE")
                                  ? "ค้างชำระทั้งหมด (รวมที่ออกแล้ว)"
                                  : roomOccupancyFilter === "ACTIVE_OVERDUE"
                                  ? "คนที่ยังอยู่ + ยังไม่จ่าย"
                                  : roomOccupancyFilter === "PAST_OVERDUE"
                                  ? "ออกไปแล้ว + ยังไม่จ่าย"
                                  : roomOccupancyFilter === "FUTURE"
                                  ? "มีผู้จองล่วงหน้า (ไม่ใช่ตอนนี้)"
                                  : "เฉพาะห้องว่าง"
                              }"
                            </div>
                            <div style={{ fontSize: "0.82rem", color: "#94a3b8", marginTop: "4px" }}>
                              (อาคารนี้มีห้องพักทั้งหมด {bldgRooms.length} ห้อง - เข้าพัก {bldgOccupied} ห้อง / ว่าง {bldgRooms.length - bldgOccupied} ห้อง)
                            </div>
                          </div>
                        ) : (
                          floorsList.map((floorNum) => {
                            const floorAllRooms = bldgRooms.filter((r) => r.floor === floorNum);
                            const floorRooms = floorAllRooms.filter((r) => {
                              const occs = roomOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomOccupantsMap[r.roomNo] || [];
                              const pastOccs = roomPastOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomPastOccupantsMap[r.roomNo] || [];
                              const futureOccs = roomFutureOccupantsMap[`${r.buildingName}-${r.roomNo}`] || roomFutureOccupantsMap[r.roomNo] || [];

                              const hasActiveOvd = occs.some(isStayOverdue);
                              const hasPastOvd = pastOccs.some(isStayOverdue);
                              const isVct = occs.length === 0;

                              if (roomOccupancyFilter === "ACTIVE") return !isVct;
                              if (roomOccupancyFilter === "ALL_OVERDUE" || roomOccupancyFilter === "OVERDUE") return hasActiveOvd || hasPastOvd;
                              if (roomOccupancyFilter === "ACTIVE_OVERDUE") return !isVct && hasActiveOvd;
                              if (roomOccupancyFilter === "PAST_OVERDUE") return hasPastOvd;
                              if (roomOccupancyFilter === "FUTURE") return futureOccs.length > 0;
                              if (roomOccupancyFilter === "VACANT") return isVct;
                              return true;
                            });

                            return (
                              <div key={floorNum} style={{ marginBottom: "18px" }}>
                                <div className={classes.floorHeader}>
                                  <span>
                                    🚩 <strong>ชั้น {floorNum}</strong> {floorNum === 3 ? "(ชั้นบนสุด)" : floorNum === 1 ? "(ชั้นล่าง)" : ""}
                                  </span>
                                  <span style={{ fontSize: "0.82rem", color: "#555" }}>
                                    {roomOccupancyFilter === "ALL"
                                      ? `${floorRooms.length} ห้องในชั้นนี้`
                                      : `${floorRooms.length} จาก ${floorAllRooms.length} ห้อง (ตามตัวกรอง)`}
                                  </span>
                                </div>

                                {floorAllRooms.length === 0 ? (
                                  <div style={{ backgroundColor: "#f5f5f5", border: "1px dashed #ccc", borderRadius: "6px", padding: "15px", textAlign: "center", color: "#777" }}>
                                    ยังไม่มีการบันทึกเลขห้องพักในชั้น {floorNum} ของ{bldg.shortName}
                                    <div className="mt-2">
                                      <Button
                                        color="info"
                                        simple
                                        size="sm"
                                        onClick={() => history.push("/trainee/master-data")}
                                      >
                                        + เพิ่มเลขห้องพักในชั้นนี้ที่หน้า Master Data &raquo;
                                      </Button>
                                    </div>
                                  </div>
                                ) : floorRooms.length === 0 ? (
                                  <div style={{ backgroundColor: "#f8fafc", border: "1px dashed #e2e8f0", borderRadius: "6px", padding: "12px", textAlign: "center", color: "#94a3b8", fontSize: "0.83rem" }}>
                                    ไม่มีห้องพักที่ตรงกับเงื่อนไขในชั้น {floorNum} (มีห้องในชั้นนี้ทั้งหมด {floorAllRooms.length} ห้อง)
                                  </div>
                                ) : (
                                  <div className="row">
                                    {floorRooms.map((room) => {
                                      const occupants = roomOccupantsMap[`${room.buildingName}-${room.roomNo}`] || roomOccupantsMap[room.roomNo] || [];
                                      const pastOccupants = roomPastOccupantsMap[`${room.buildingName}-${room.roomNo}`] || roomPastOccupantsMap[room.roomNo] || [];
                                      const futureOccupants = roomFutureOccupantsMap[`${room.buildingName}-${room.roomNo}`] || roomFutureOccupantsMap[room.roomNo] || [];
                                      const hasStaffOccupant = occupants.some(isStaffOccupant) || futureOccupants.some(isStaffOccupant);
                                      const isWelfare = isWelfareRoom(room) || hasStaffOccupant;
                                      const hasActiveOverdue = occupants.some(isStayOverdue);
                                      const hasPastOverdue = pastOccupants.some(isStayOverdue);
                                      const isFull = occupants.length >= room.capacity;
                                      const isVacant = occupants.length === 0;

                                      return (
                                        <div key={room.id} className="col-lg-4 col-md-6 mb-3">
                                          <div
                                            className={classes.roomCard}
                                            style={{
                                              borderLeft: `6px solid ${
                                                hasActiveOverdue ? "#f44336" : hasPastOverdue ? "#e65100" : isVacant ? "#4caf50" : (hasStaffOccupant || isWelfare) ? "#0284c7" : "#0288d1"
                                              }`
                                            }}
                                          >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", flexWrap: "wrap", gap: "6px" }}>
                                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <HotelIcon style={{ color: "#00838f", fontSize: "1.5rem" }} />
                                                <h5 style={{ margin: 0, fontWeight: "bold", color: "#111" }}>
                                                  ห้อง {room.roomNo}
                                                </h5>
                                                <Badge color="light" style={{ color: "#555", border: "1px solid #ccc", fontSize: "0.75rem" }}>
                                                  {room.buildingName} ช.{room.floor}
                                                </Badge>
                                              </div>
                                              <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
                                                {(hasStaffOccupant || isWelfare) && (
                                                  <Badge style={{ fontSize: "0.78rem", padding: "4px 8px", backgroundColor: "#0284c7", color: "#fff" }}>
                                                    🏠 สวัสดิการ รพ.
                                                  </Badge>
                                                )}
                                                {!isVacant && (
                                                  <Badge
                                                    color={isFull ? "primary" : "warning"}
                                                    style={{
                                                      fontSize: "0.78rem",
                                                      padding: "4px 8px",
                                                      backgroundColor: isFull ? "#0288d1" : undefined
                                                    }}
                                                  >
                                                    {isFull
                                                      ? `มีผู้พักเต็ม (${occupants.length}/${room.capacity})`
                                                      : `มีผู้พัก (${occupants.length}/${room.capacity})`}
                                                  </Badge>
                                                )}
                                                {isVacant && (
                                                  <Badge color="success" style={{ fontSize: "0.78rem", padding: "4px 8px" }}>
                                                    ว่างพร้อมพัก
                                                  </Badge>
                                                )}
                                                {futureOccupants.length > 0 && (
                                                  <Badge
                                                    color="info"
                                                    style={{
                                                      fontSize: "0.78rem",
                                                      padding: "4px 8px",
                                                      backgroundColor: "#0284c7"
                                                    }}
                                                    title={`มีผู้จองล่วงหน้า ${futureOccupants.length} คน (ยังไม่ถึงเวลาเข้าพัก/ไม่ใช่ตอนนี้)`}
                                                  >
                                                    ⏳ จองล่วงหน้า {futureOccupants.length} คน
                                                  </Badge>
                                                )}
                                                {hasActiveOverdue && (
                                                  <Badge color="danger" style={{ fontSize: "0.78rem", padding: "4px 8px" }}>
                                                    มีค้างชำระ
                                                  </Badge>
                                                )}
                                                {hasPastOverdue && !hasActiveOverdue && (
                                                  <Badge color="danger" style={{ fontSize: "0.78rem", padding: "4px 8px", backgroundColor: "#b71c1c" }}>
                                                    มีค้างชำระ (ออกแล้ว)
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>

                                             <div style={{ fontSize: "0.82rem", color: "#555", marginBottom: "6px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
                                               <div>
                                                 <strong>ประเภท:</strong> {isWelfare ? "ห้องสวัสดิการ รพ." : (room.type || "ทั่วไป")} | <strong>ความจุ:</strong> {room.capacity} เตียง | <strong>อัตรา:</strong>{" "}
                                                 {isWelfare ? (
                                                   "ห้องสวัสดิการ (ไม่มีค่าเช่า)"
                                                 ) : room.electricityAmount > 0 ? (
                                                   <span>
                                                     {room.fee} บ./ด.{" "}
                                                     <span
                                                       className="badge badge-warning"
                                                       style={{
                                                         backgroundColor: "#fef3c7",
                                                         color: "#92400e",
                                                         border: "1px solid #f59e0b",
                                                         fontSize: "0.72rem",
                                                         padding: "1px 5px",
                                                         borderRadius: "4px"
                                                       }}
                                                       title={`มิเตอร์ ${room.electricity?.previousMeter} ➔ ${room.electricity?.currentMeter} (${room.electricity?.netUnits || room.electricity?.totalUnits} หน่วย @ ${room.electricity?.ratePerUnit || 8} บ.)`}
                                                     >
                                                       ⚡ ค่าไฟ +${Number(room.electricityAmount).toLocaleString()} บ.
                                                     </span>{" "}
                                                     <strong style={{ color: "#047857" }}>
                                                       (รวม ${(Number(room.fee) + Number(room.electricityAmount)).toLocaleString()} บ.)
                                                     </strong>
                                                   </span>
                                                 ) : (
                                                   `${room.fee} บ./ด.`
                                                 )}
                                               </div>
                                               {hasStaffOccupant || isWelfare ? (
                                                 <span
                                                   className="badge badge-light"
                                                   style={{ fontSize: "0.72rem", padding: "3px 6px", color: "#64748b", border: "1px solid #cbd5e1" }}
                                                   title="ห้องพักบุคลากรโรงพยาบาล สงวนสิทธิ์จัดการที่ระบบหอพักบุคลากร"
                                                 >
                                                   🔒 ห้องเจ้าหน้าที่
                                                 </span>
                                               ) : (
                                                 <button
                                                   type="button"
                                                   className="btn btn-xs btn-outline-primary"
                                                   style={{ fontSize: "0.72rem", padding: "1px 6px", borderRadius: "4px" }}
                                                   onClick={() => handleOpenManageBeds(room)}
                                                   title="จัดการจำนวนเตียงของห้องนี้"
                                                 >
                                                   🛏️ จัดการเตียง
                                                 </button>
                                               )}
                                             </div>

                                            <hr style={{ margin: "6px 0" }} />

                                            <div style={{ minHeight: "70px" }}>
                                              <div style={{ fontSize: "0.8rem", fontWeight: "bold", color: "#555", marginBottom: "3px" }}>
                                                ผู้เข้าพักปัจจุบัน ({occupants.length} คน):
                                              </div>
                                              {occupants.length === 0 ? (
                                                <div style={{ color: "#2e7d32", fontSize: "0.82rem", fontWeight: "500", padding: "8px 0" }}>
                                                  🟢 ไม่มีผู้พักอาศัยในปัจจุบัน (ห้องว่าง)
                                                </div>
                                              ) : (
                                                occupants.map((occ) => {
                                                  const isStaff = isStaffOccupant(occ);
                                                  const isWelfare = isStaff || isWelfareStay(occ, room);

                                                  return (
                                                    <div
                                                      key={occ.id}
                                                      style={{
                                                        backgroundColor: "#f5f9fc",
                                                        borderRadius: "4px",
                                                        padding: "5px 8px",
                                                        marginBottom: "4px",
                                                        fontSize: "0.82rem",
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        borderLeft: isStaff ? "3px solid #0284c7" : "3px solid #00838f"
                                                      }}
                                                    >
                                                      <div>
                                                        <strong style={{ color: isStaff ? "#0284c7" : "#00695c" }}>{occ.traineeName}</strong>
                                                        <div style={{ fontSize: "0.74rem", color: "#666" }}>
                                                          {occ.institution || ""} {occ.dateRange && String(occ.dateRange).trim() && String(occ.dateRange).trim() !== "-" && String(occ.dateRange).trim() !== "()" ? `(${formatStayDateRangeThai(occ.dateRange, occ.checkInDate, occ.checkOutDate)})` : ""}
                                                        </div>
                                                      </div>
                                                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                        {isWelfare ? (
                                                          <>
                                                            <Badge
                                                              style={{
                                                                fontSize: "0.68rem",
                                                                padding: "3px 6px",
                                                                backgroundColor: "#0284c7",
                                                                color: "#ffffff"
                                                              }}
                                                              title="ผู้เข้าพักในห้องสวัสดิการ รพ. ไม่มีค่าเช่าห้องพัก"
                                                            >
                                                              🏠 สวัสดิการ รพ.
                                                            </Badge>
                                                            {isStaff && (
                                                              <span
                                                                style={{
                                                                  fontSize: "0.68rem",
                                                                  color: "#475569",
                                                                  backgroundColor: "#e2e8f0",
                                                                  padding: "2px 5px",
                                                                  borderRadius: "4px",
                                                                  fontWeight: "600",
                                                                  border: "1px solid #cbd5e1"
                                                                }}
                                                                title="บุคลากรโรงพยาบาล สงวนสิทธิ์จัดการที่ระบบหอพักบุคลากร"
                                                              >
                                                                🔒 เจ้าหน้าที่ รพ.
                                                              </span>
                                                            )}
                                                          </>
                                                        ) : (
                                                          <>
                                                            <button
                                                              type="button"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenPaymentModal(occ);
                                                              }}
                                                              style={{
                                                                border: "none",
                                                                background: "none",
                                                                padding: 0,
                                                                cursor: "pointer"
                                                              }}
                                                              title="คลิกเพื่ออัพเดทสถานะ / บันทึกการชำระเงิน"
                                                            >
                                                              {isStayOverdue(occ) ? (
                                                                <Badge color="danger" style={{ fontSize: "0.68rem", cursor: "pointer", padding: "3px 6px" }}>
                                                                  ค้าง {occ.remainingBalance ? `${Number(occ.remainingBalance).toLocaleString()} บ.` : 'ค้างจ่าย'} ✏️
                                                                </Badge>
                                                              ) : (
                                                                <Badge color="success" style={{ fontSize: "0.68rem", cursor: "pointer", padding: "3px 6px" }}>
                                                                  จ่ายแล้ว ✏️
                                                                </Badge>
                                                              )}
                                                            </button>
                                                            <button
                                                              type="button"
                                                              onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenCheckoutModal(occ);
                                                              }}
                                                              className="btn btn-xs btn-outline-danger ml-1"
                                                              style={{ fontSize: "0.68rem", padding: "2px 5px", lineHeight: 1 }}
                                                              title="กดยืนยันการย้ายออกและคำนวณค่าใช้จ่ายตามจริง"
                                                            >
                                                              🚪 ย้ายออก
                                                            </button>
                                                          </>
                                                        )}
                                                      </div>
                                                    </div>
                                                  );
                                                })
                                              )}

                                              {/* ผู้พักล่วงหน้า/ในอนาคต (ไม่ใช่ตอนนี้ - ยังไม่ถึงเวลา) */}
                                              {futureOccupants.length > 0 && (
                                                <div
                                                  style={{
                                                    backgroundColor: "#f0f9ff",
                                                    border: "1px dashed #0284c7",
                                                    borderRadius: "6px",
                                                    padding: "6px 8px",
                                                    marginTop: "6px",
                                                    marginBottom: "4px"
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontSize: "0.75rem",
                                                      fontWeight: "bold",
                                                      color: "#0369a1",
                                                      display: "flex",
                                                      justifyContent: "space-between",
                                                      alignItems: "center",
                                                      marginBottom: "4px"
                                                    }}
                                                  >
                                                    <span>⏳ ผู้พักล่วงหน้า/ในอนาคต ({futureOccupants.length} คน):</span>
                                                    <span
                                                      style={{
                                                        backgroundColor: "#bae6fd",
                                                        color: "#0369a1",
                                                        padding: "1px 6px",
                                                        borderRadius: "4px",
                                                        fontSize: "0.68rem",
                                                        fontWeight: "bold"
                                                      }}
                                                    >
                                                      ไม่ใช่ตอนนี้
                                                    </span>
                                                  </div>
                                                  {futureOccupants.map((fOcc) => {
                                                    const isStaff = isStaffOccupant(fOcc);
                                                    const isWelfare = isStaff || isWelfareStay(fOcc, room);

                                                    return (
                                                      <div
                                                        key={fOcc.id}
                                                        style={{
                                                          backgroundColor: "#ffffff",
                                                          borderRadius: "4px",
                                                          padding: "4px 7px",
                                                          marginBottom: "3px",
                                                          fontSize: "0.78rem",
                                                          display: "flex",
                                                          justifyContent: "space-between",
                                                          alignItems: "center",
                                                          borderLeft: "3px solid #0284c7"
                                                        }}
                                                      >
                                                        <div>
                                                          <strong style={{ color: "#0369a1" }}>{fOcc.traineeName}</strong>
                                                          <div style={{ fontSize: "0.72rem", color: "#64748b" }}>
                                                            {fOcc.institution || ""} {fOcc.dateRange && String(fOcc.dateRange).trim() && String(fOcc.dateRange).trim() !== "-" && String(fOcc.dateRange).trim() !== "()" ? `(${formatStayDateRangeThai(fOcc.dateRange, fOcc.checkInDate, fOcc.checkOutDate)})` : ""}
                                                          </div>
                                                        </div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                          <Badge color="info" style={{ fontSize: "0.65rem", padding: "2px 5px", backgroundColor: "#0284c7" }}>
                                                            ⏳ ยังไม่ถึงเวลา
                                                          </Badge>
                                                          {isWelfare ? (
                                                            <>
                                                              <Badge
                                                                style={{
                                                                  fontSize: "0.65rem",
                                                                  padding: "2px 5px",
                                                                  backgroundColor: "#0284c7",
                                                                  color: "#ffffff"
                                                                }}
                                                              >
                                                                🏠 สวัสดิการ รพ.
                                                              </Badge>
                                                              {isStaff && (
                                                                <span
                                                                  style={{
                                                                    fontSize: "0.65rem",
                                                                    color: "#475569",
                                                                    backgroundColor: "#e2e8f0",
                                                                    padding: "1px 4px",
                                                                    borderRadius: "4px",
                                                                    fontWeight: "600",
                                                                    border: "1px solid #cbd5e1"
                                                                  }}
                                                                  title="บุคลากรโรงพยาบาล สงวนสิทธิ์จัดการที่ระบบหอพักบุคลากร"
                                                                >
                                                                  🔒 เจ้าหน้าที่ รพ.
                                                                </span>
                                                              )}
                                                            </>
                                                          ) : (
                                                            <>
                                                              <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  handleOpenPaymentModal(fOcc);
                                                                }}
                                                                style={{
                                                                  border: "none",
                                                                  background: "none",
                                                                  padding: 0,
                                                                  cursor: "pointer"
                                                                }}
                                                                title="คลิกเพื่ออัพเดทสถานะ / บันทึกการชำระเงิน"
                                                              >
                                                                {isStayOverdue(fOcc) ? (
                                                                  <Badge color="danger" style={{ fontSize: "0.65rem", cursor: "pointer", padding: "2px 5px" }}>
                                                                    ค้าง {fOcc.remainingBalance ? `${Number(fOcc.remainingBalance).toLocaleString()} บ.` : 'ค้างจ่าย'} ✏️
                                                                  </Badge>
                                                                ) : (
                                                                  <Badge color="success" style={{ fontSize: "0.65rem", cursor: "pointer", padding: "2px 5px" }}>
                                                                    จ่ายแล้ว ✏️
                                                                  </Badge>
                                                                )}
                                                              </button>
                                                              <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  handleRemoveFutureOccupant(fOcc);
                                                                }}
                                                                className="btn btn-xs btn-outline-danger"
                                                                style={{ fontSize: "0.65rem", padding: "1px 5px", lineHeight: 1 }}
                                                                title="ยกเลิกการจองและเอาออกจากห้องพัก"
                                                              >
                                                                ❌ เอาออก
                                                              </button>
                                                            </>
                                                          )}
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                  <div style={{ fontSize: "0.68rem", color: "#64748b", fontStyle: "italic", marginTop: "2px" }}>
                                                    * ระบบจะนับเป็นผู้เข้าพักปัจจุบันเมื่อถึงช่วงวันเวลาที่เข้าพักจริง
                                                  </div>
                                                </div>
                                              )}

                                              {/* แจ้งเตือนผู้พักในอดีตที่ค้างชำระ */}
                                              {hasPastOverdue && (
                                                <div style={{ backgroundColor: "#fff5f5", border: "1px solid #feb2b2", borderRadius: "6px", padding: "6px 8px", marginTop: "6px", marginBottom: "4px" }}>
                                                  <div style={{ fontSize: "0.75rem", fontWeight: "bold", color: "#c53030", marginBottom: "3px" }}>
                                                    ⚠️ ประวัติผู้พักที่ออกแล้วแต่ยังค้างชำระ:
                                                  </div>
                                                  {pastOccupants.filter(isStayOverdue).map((p) => (
                                                    <div key={p.id} style={{ fontSize: "0.74rem", color: "#4a5568", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px", padding: "2px 0" }}>
                                                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "165px" }} title={`${p.traineeName} (${p.institution})`}>
                                                        • <strong style={{ color: "#742a2a" }}>{p.traineeName}</strong>
                                                      </span>
                                                      <button
                                                        type="button"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleOpenPaymentModal(p);
                                                        }}
                                                        className="btn btn-xs"
                                                        style={{
                                                          backgroundColor: "#dc2626",
                                                          color: "#ffffff",
                                                          border: "none",
                                                          borderRadius: "4px",
                                                          padding: "2px 7px",
                                                          fontSize: "0.68rem",
                                                          fontWeight: "bold",
                                                          cursor: "pointer",
                                                          display: "inline-flex",
                                                          alignItems: "center",
                                                          gap: "3px",
                                                          boxShadow: "0 1px 2px rgba(220,38,38,0.2)"
                                                        }}
                                                        title="คลิกเพื่ออัพเดทสถานะ / หักลบยอดชำระเงิน"
                                                      >
                                                        <span>ค้าง {p.remainingBalance ? `${Number(p.remainingBalance).toLocaleString()} บ.` : 'ค้างชำระ'}</span>
                                                        <span>✏️</span>
                                                      </button>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}

                                              {/* ป้ายแจ้งเตือนผู้ที่ออกไปแล้ว / พ้นวันฝึก */}
                                              {pastOccupants.length > 0 && (
                                                <div style={{ marginTop: "6px", fontSize: "0.74rem" }}>
                                                  <span
                                                    style={{
                                                      backgroundColor: hasPastOverdue ? "#ffebee" : "#eceff1",
                                                      color: hasPastOverdue ? "#c62828" : "#546e7a",
                                                      border: hasPastOverdue ? "1px solid #ef9a9a" : "none",
                                                      padding: "3px 7px",
                                                      borderRadius: "4px",
                                                      display: "inline-block",
                                                      fontSize: "0.72rem",
                                                      fontWeight: hasPastOverdue ? "600" : "normal"
                                                    }}
                                                    title={`เคยเข้าพัก: ${pastOccupants.map(p => `${p.traineeName} (${isStayOverdue(p) ? 'ค้างจ่าย' : 'จ่ายแล้ว'})`).join(', ')}`}
                                                  >
                                                    🚪 ออกไปแล้ว {pastOccupants.length} คน {hasPastOverdue ? `(⚠️ มีค้างชำระ ${pastOccupants.filter(isStayOverdue).length} คน)` : "(พ้นวันฝึก)"}
                                                  </span>
                                                </div>
                                              )}
                                              </div>

                                          <div style={{ marginTop: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <Button
                                              color="info"
                                              size="sm"
                                              simple
                                              onClick={() => handleOpenRoomModal(room)}
                                            >
                                              ดูรายละเอียด
                                            </Button>
                                            {hasStaffOccupant || isWelfare ? (
                                              <Button
                                                color="secondary"
                                                size="sm"
                                                disabled
                                                style={{ opacity: 0.65, cursor: "not-allowed", fontSize: "0.75rem" }}
                                                title="ห้องสำหรับเจ้าหน้าที่โรงพยาบาล สงวนสิทธิ์จัดการที่ระบบหอพักบุคลากร"
                                              >
                                                🔒 ห้องเจ้าหน้าที่ (ดูอย่างเดียว)
                                              </Button>
                                            ) : (
                                              <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => handleOpenAddStay(room.roomNo, room.buildingName, room.floor)}
                                              >
                                                + จัดคนเข้าห้อง
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                      </div>
                    );
                  })}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 1: ทะเบียนประวัติการเข้าพักและค่าหอพัก */}
              {activeTab === 1 && (
                <div>
                  {/* Toolbar */}
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 15px",
                      borderRadius: "6px",
                      marginBottom: "15px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      alignItems: "center"
                    }}
                  >
                    <div style={{ flex: "1 1 200px", position: "relative" }}>
                      <SearchIcon style={{ position: "absolute", left: "10px", top: "10px", color: "#888" }} />
                      <input
                        type="text"
                        className="form-control"
                        style={{ paddingLeft: "36px", fontSize: "0.9rem" }}
                        placeholder="ค้นหาชื่อผู้เข้าพัก, เลขห้อง, สถาบัน..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                      />
                    </div>

                    <div style={{ minWidth: "140px" }}>
                      <select
                        className="form-control"
                        style={{ fontSize: "0.9rem" }}
                        value={selectedBuilding}
                        onChange={(e) => { setSelectedBuilding(e.target.value); setCurrentPage(1); }}
                      >
                        <option value="ALL">🏢 ทุกอาคาร</option>
                        <option value="ตึก 1">🏢 ตึก 1</option>
                        <option value="ตึก 2">🏢 ตึก 2</option>
                        <option value="ตึก 3">🏢 ตึก 3</option>
                        <option value="ตึก 4">🏢 ตึก 4</option>
                      </select>
                    </div>

                    <div style={{ minWidth: "160px" }}>
                      <select
                        className="form-control"
                        style={{ fontSize: "0.9rem" }}
                        value={paymentFilter}
                        onChange={(e) => { setPaymentFilter(e.target.value); setCurrentPage(1); }}
                      >
                        <option value="ALL">💰 ทุกสถานะการเงิน</option>
                        <option value="PAID">✅ ชำระครบแล้ว (PAID)</option>
                        <option value="PENDING">⏳ รอชำระ (PENDING)</option>
                        <option value="PARTIAL">⚠️ ชำระบางส่วน (PARTIAL)</option>
                        <option value="OVERDUE">❌ ค้างชำระ (OVERDUE)</option>
                        <option value="FREE">🆓 ยกเว้นค่าธรรมเนียม (FREE)</option>
                      </select>
                    </div>

                    <div style={{ minWidth: "140px" }}>
                      <select
                        className="form-control"
                        style={{ fontSize: "0.9rem" }}
                        value={traineeTypeFilter}
                        onChange={(e) => { setTraineeTypeFilter(e.target.value); setCurrentPage(1); }}
                      >
                        <option value="ALL">👥 ทุกประเภท</option>
                        <option value="DOCTOR">👨‍⚕️ แพทย์</option>
                        <option value="STUDENT">🎓 นักศึกษา</option>
                      </select>
                    </div>

                    <div style={{ minWidth: "160px" }}>
                      <select
                        className="form-control"
                        style={{ fontSize: "0.9rem" }}
                        value={stayStatusFilter}
                        onChange={(e) => { setStayStatusFilter(e.target.value); setCurrentPage(1); }}
                      >
                        <option value="ALL">📋 ทุกสถานะการพัก</option>
                        <option value="ACTIVE">🟢 กำลังพักอาศัย (อยู่ในช่วง)</option>
                        <option value="FUTURE">⏳ จองล่วงหน้า (ไม่ใช่ตอนนี้)</option>
                        <option value="ENDED">🚪 ออกไปแล้ว (พ้นวันฝึก)</option>
                      </select>
                    </div>

                    {(searchTerm || selectedBuilding !== "ALL" || paymentFilter !== "ALL" || traineeTypeFilter !== "ALL" || stayStatusFilter !== "ALL") && (
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={() => {
                          setSearchTerm("");
                          setSelectedBuilding("ALL");
                          setPaymentFilter("ALL");
                          setTraineeTypeFilter("ALL");
                          setStayStatusFilter("ALL");
                          setCurrentPage(1);
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    )}
                  </div>

                  {/* Stays Table */}
                  <div style={{ overflowX: "auto" }}>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.thCenter} style={{ width: "50px" }}>ลำดับ</th>
                          <th className={classes.thCenter}>สถานะการพัก</th>
                          <th className={classes.thCenter}>อาคาร / ห้องพัก</th>
                          <th className={classes.th}>ชื่อ-สกุล ผู้เข้าพัก</th>
                          <th className={classes.th}>ประเภท / ตำแหน่ง</th>
                          <th className={classes.th}>สถาบัน / ต้นสังกัด</th>
                          <th className={classes.th}>ช่วงเวลาเข้าพัก</th>
                          <th className={classes.th}>ค่าหอพัก / สถานะชำระเงิน</th>
                          <th className={classes.th}>หมายเหตุการพัก</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedStays.length === 0 ? (
                          <tr>
                            <td colSpan="9" style={{ textAlign: "center", padding: "30px", color: "#888" }}>
                              ไม่พบข้อมูลประวัติการเข้าพักตามเงื่อนไขที่ค้นหา
                            </td>
                          </tr>
                        ) : (
                          paginatedStays.map((stay, idx) => (
                            <tr key={stay.id} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafcff" }}>
                              <td className={classes.td} style={{ textAlign: "center", fontWeight: "bold", color: "#666" }}>
                                {(currentPage - 1) * pageSize + idx + 1}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                {(() => {
                                  const timeline = getStayTimelineStatus(stay);
                                  if (timeline.key === "CURRENT") {
                                    return (
                                      <Badge color="success" style={{ fontSize: "0.78rem", padding: "4px 8px" }}>
                                        🟢 กำลังพักอาศัย
                                      </Badge>
                                    );
                                  } else if (timeline.key === "FUTURE") {
                                    return (
                                      <div>
                                        <Badge color="info" style={{ fontSize: "0.78rem", padding: "4px 8px", backgroundColor: "#0284c7" }}>
                                          ⏳ ในอนาคต
                                        </Badge>
                                        <div style={{ fontSize: "0.68rem", color: "#0284c7", marginTop: "2px", fontWeight: "600" }}>
                                          (ไม่ใช่ตอนนี้)
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div>
                                        <Badge color="secondary" style={{ fontSize: "0.78rem", padding: "4px 8px", backgroundColor: "#78909c" }}>
                                          🚪 ออกไปแล้ว
                                        </Badge>
                                        <div style={{ fontSize: "0.68rem", color: "#888", marginTop: "2px" }}>
                                          พ้นวันฝึกแล้ว
                                        </div>
                                      </div>
                                    );
                                  }
                                })()}
                              </td>
                              <td className={classes.td} style={{ textAlign: "center" }}>
                                <Badge color="info" style={{ fontSize: "0.85rem", padding: "5px 8px" }}>
                                  {stay.building || "ตึก 1"} ห้อง {stay.roomNo}
                                </Badge>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontWeight: "bold", color: "#222" }}>
                                  {stay.traineeName}
                                </div>
                                <div style={{ fontSize: "0.78rem", color: "#888" }}>
                                  {stay.phone || "-"}
                                </div>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontSize: "0.84rem" }}>{stay.position}</div>
                                {isStaffOccupant(stay) ? (
                                  <Badge color="info" style={{ fontSize: "0.72rem", backgroundColor: "#0284c7" }}>
                                    เจ้าหน้าที่ รพ.
                                  </Badge>
                                ) : (
                                  <Badge color={stay.traineeType === "DOCTOR" ? "warning" : "success"} style={{ fontSize: "0.72rem" }}>
                                    {stay.traineeType === "DOCTOR" ? "แพทย์" : "นักศึกษา"}
                                  </Badge>
                                )}
                              </td>
                              <td className={classes.td}>
                                <span style={{ fontSize: "0.85rem", color: "#444" }}>{stay.institution}</span>
                              </td>
                              <td className={classes.td}>
                                <div style={{ fontSize: "0.85rem", fontWeight: "500", color: "#333", lineHeight: 1.4, fontFamily: '"Sarabun", sans-serif' }}>
                                  {formatStayDateRangeThai(stay.dateRange, stay.checkInDate, stay.checkOutDate)}
                                </div>
                              </td>
                              <td className={classes.td}>
                                {isStaffOccupant(stay) || isWelfareStay(stay) ? (
                                  <div>
                                    <Badge style={{ fontSize: "0.8rem", padding: "5px 8px", backgroundColor: "#0284c7", color: "#ffffff", fontFamily: '"Sarabun", sans-serif' }}>
                                      🏠 สวัสดิการ รพ.
                                    </Badge>
                                    <div style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: "600", marginTop: "4px", fontFamily: '"Sarabun", sans-serif' }}>
                                      🔒 เจ้าหน้าที่ (ดูอย่างเดียว)
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div>
                                      {stay.paymentStatus === "OVERDUE" ? (
                                        <Badge color="danger" style={{ fontSize: "0.8rem", padding: "5px 8px", fontFamily: '"Sarabun", sans-serif' }}>
                                          ⚠️ {stay.feeText}
                                        </Badge>
                                      ) : (
                                        <Badge color="success" style={{ fontSize: "0.8rem", padding: "5px 8px", fontFamily: '"Sarabun", sans-serif' }}>
                                          {stay.feeText}
                                        </Badge>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleOpenPaymentModal(stay)}
                                      className="btn btn-sm"
                                      style={{
                                        backgroundColor: isStayOverdue(stay) ? "#dc2626" : "#0284c7",
                                        color: "#ffffff",
                                        border: "none",
                                        borderRadius: "4px",
                                        padding: "4px 10px",
                                        fontSize: "0.78rem",
                                        fontWeight: "600",
                                        fontFamily: '"Sarabun", sans-serif',
                                        marginTop: "6px",
                                        cursor: "pointer",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
                                      }}
                                      title="คลิกเพื่อบันทึกหรืออัพเดทสถานะการเงิน"
                                    >
                                      💳 บันทึกรับชำระเงิน
                                    </button>
                                  </>
                                )}
                              </td>
                              <td className={classes.td} style={{ fontSize: "0.82rem", color: stay.remarks ? "#d32f2f" : "#888" }}>
                                {stay.remarks || "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div style={{ marginTop: "15px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ color: "#666", fontSize: "0.88rem" }}>
                      แสดง {filteredStays.length === 0 ? 0 : (currentPage - 1) * pageSize + 1} ถึง{" "}
                      {Math.min(currentPage * pageSize, filteredStays.length)} จากทั้งหมด {filteredStays.length} รายการ
                    </div>
                    <div>
                      <Pagination
                        current={currentPage}
                        total={totalPages}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: ตารางติดตามค่าหอพักรายเดือน (ซ่อนไว้ก่อนตามที่ผู้ใช้ร้องขอ) */}
              {false && activeTab === 2 && (
                <div>
                  {/* Executive Header Banner */}
                  <div
                    style={{
                      background: "linear-gradient(135deg, #064e3b 0%, #047857 100%)",
                      borderRadius: "10px",
                      padding: "16px 20px",
                      color: "#ffffff",
                      marginBottom: "20px",
                      boxShadow: "0 4px 12px rgba(6, 78, 59, 0.15)"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <h5 style={{ margin: 0, fontWeight: "bold", fontSize: "1.15rem" }}>
                          <CheckCircleIcon style={{ verticalAlign: "middle", marginRight: "8px", color: "#6ee7b7" }} />
                          ตารางติดตามและจัดเก็บค่าหอพักรายเดือน (Monthly Fee Collection Matrix)
                        </h5>
                        <p style={{ margin: "5px 0 0 0", fontSize: "0.88rem", color: "rgba(255,255,255,0.9)" }}>
                          เครื่องมือสำหรับฝ่ายการเงินและบริหารงานทั่วไป ตรวจสอบว่าน้องมาเดือนไหนบ้าง จ่ายครบไหม หรือค้างชำระเท่าไร
                          พร้อมคำนวณตามสูตรเศษสัปดาห์/วัน (ตึก 1,3,4: 500 บ./ด., สัปดาห์ละ 125 บ., วันละ 17.86 บ. / ตึก 2: 100 บ./วัน)
                        </p>
                      </div>
                      <div style={{ marginTop: "8px" }}>
                        <span style={{ backgroundColor: "rgba(255,255,255,0.2)", padding: "6px 14px", borderRadius: "20px", fontSize: "0.85rem", fontWeight: "700" }}>
                          🎯 อัตราจัดเก็บได้: {checklistKpis.collectionRate}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Top 4 KPI Cards */}
                  <div className="row g-3 mb-3">
                    <div className="col-md-3 col-sm-6 mb-2">
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderLeft: "5px solid #0284c7", borderRadius: "10px", padding: "14px 16px", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}>
                        <div style={{ fontSize: "0.82rem", color: "#64748b", fontWeight: "700" }}>💰 ยอดเรียกเก็บทั้งหมด</div>
                        <div style={{ fontSize: "1.5rem", fontWeight: "800", color: "#0f172a", marginTop: "4px" }}>
                          {checklistKpis.totalDue.toLocaleString()} <span style={{ fontSize: "0.9rem", fontWeight: "normal", color: "#64748b" }}>บาท</span>
                        </div>
                        <div style={{ fontSize: "0.78rem", color: "#0284c7", marginTop: "4px" }}>
                          ผู้เข้าพักทั้งหมด {checklistStays.length} รายการ
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3 col-sm-6 mb-2">
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderLeft: "5px solid #10b981", borderRadius: "10px", padding: "14px 16px", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}>
                        <div style={{ fontSize: "0.82rem", color: "#64748b", fontWeight: "700" }}>🟢 ชำระแล้วทั้งหมด</div>
                        <div style={{ fontSize: "1.5rem", fontWeight: "800", color: "#065f46", marginTop: "4px" }}>
                          {checklistKpis.totalPaid.toLocaleString()} <span style={{ fontSize: "0.9rem", fontWeight: "normal", color: "#64748b" }}>บาท</span>
                        </div>
                        <div style={{ fontSize: "0.78rem", color: "#10b981", marginTop: "4px" }}>
                          ✓ ชำระครบถ้วน {checklistKpis.fullyPaidCount} คน ({checklistKpis.collectionRate}%)
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3 col-sm-6 mb-2">
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderLeft: "5px solid #ef4444", borderRadius: "10px", padding: "14px 16px", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}>
                        <div style={{ fontSize: "0.82rem", color: "#64748b", fontWeight: "700" }}>🔴 ยอดค้างชำระทั้งหมด (ทวงถาม)</div>
                        <div style={{ fontSize: "1.5rem", fontWeight: "800", color: "#dc2626", marginTop: "4px" }}>
                          {checklistKpis.totalRemaining.toLocaleString()} <span style={{ fontSize: "0.9rem", fontWeight: "normal", color: "#64748b" }}>บาท</span>
                        </div>
                        <div style={{ fontSize: "0.78rem", color: "#dc2626", fontWeight: "700", marginTop: "4px" }}>
                          ⚠️ ต้องติดตามทวงถาม {checklistKpis.overdueCount} คน
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3 col-sm-6 mb-2">
                      <div style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderLeft: "5px solid #f59e0b", borderRadius: "10px", padding: "14px 16px", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" }}>
                        <div style={{ fontSize: "0.82rem", color: "#64748b", fontWeight: "700" }}>👥 ผู้เข้าพักที่ค้างชำระ</div>
                        <div style={{ fontSize: "1.5rem", fontWeight: "800", color: "#b45309", marginTop: "4px" }}>
                          {checklistKpis.overdueCount} <span style={{ fontSize: "0.9rem", fontWeight: "normal", color: "#64748b" }}>คน</span>
                        </div>
                        <div style={{ fontSize: "0.78rem", color: "#b45309", marginTop: "4px" }}>
                          การเงินสามารถกดปุ่มบันทึกรับเงินได้ทันที
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Filter Toolbar */}
                  <div
                    style={{
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      alignItems: "center"
                    }}
                  >
                    <div style={{ flex: "1 1 220px", position: "relative" }}>
                      <SearchIcon style={{ position: "absolute", left: "10px", top: "9px", color: "#94a3b8" }} />
                      <input
                        type="text"
                        className="form-control"
                        style={{ paddingLeft: "36px", fontSize: "0.88rem" }}
                        placeholder="ค้นหาชื่อผู้เข้าพัก, ห้อง, สถาบัน..."
                        value={checklistSearch}
                        onChange={(e) => setChecklistSearch(e.target.value)}
                      />
                    </div>

                    <div style={{ minWidth: "140px" }}>
                      <select
                        className="form-control"
                        style={{ fontSize: "0.88rem" }}
                        value={checklistYear}
                        onChange={(e) => setChecklistYear(e.target.value)}
                      >
                        <option value="2569">📅 ปีงบประมาณ 2569</option>
                        <option value="2568">📅 ปีงบประมาณ 2568</option>
                        <option value="ALL">📅 ทุกปีงบประมาณ</option>
                      </select>
                    </div>

                    <div style={{ minWidth: "140px" }}>
                      <select
                        className="form-control"
                        style={{ fontSize: "0.88rem" }}
                        value={checklistBuilding}
                        onChange={(e) => setChecklistBuilding(e.target.value)}
                      >
                        <option value="ALL">🏢 ทุกอาคารหอพัก</option>
                        <option value="ตึก 1">ตึก 1 (นศ.ฝึกงาน)</option>
                        <option value="ตึก 2">ตึก 2 (แพทย์ Fellow/Elective)</option>
                        <option value="ตึก 3">ตึก 3 (พยาบาล/ทั่วไป)</option>
                        <option value="ตึก 4">ตึก 4 (นศ.ชาย/รับรอง)</option>
                      </select>
                    </div>

                    <div style={{ minWidth: "160px" }}>
                      <select
                        className="form-control"
                        style={{ fontSize: "0.88rem" }}
                        value={checklistStatus}
                        onChange={(e) => setChecklistStatus(e.target.value)}
                      >
                        <option value="ALL">💳 ทุกสถานะการเงิน</option>
                        <option value="PAID">✅ ชำระครบแล้ว (PAID)</option>
                        <option value="PENDING">⏳ รอชำระ (PENDING)</option>
                        <option value="PARTIAL">⚠️ ชำระบางส่วน (PARTIAL)</option>
                        <option value="OVERDUE">❌ ค้างชำระ (OVERDUE)</option>
                        <option value="FREE">🆓 ยกเว้นค่าธรรมเนียม (FREE)</option>
                      </select>
                    </div>

                    {(checklistSearch || checklistBuilding !== "ALL" || checklistStatus !== "ALL" || checklistYear !== "2569") && (
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={() => {
                          setChecklistSearch("");
                          setChecklistBuilding("ALL");
                          setChecklistStatus("ALL");
                          setChecklistYear("2569");
                        }}
                      >
                        ล้างตัวกรอง
                      </Button>
                    )}
                  </div>

                  {/* Checklist Matrix Table */}
                  <div style={{ overflowX: "auto", border: "1px solid #cbd5e1", borderRadius: "8px", backgroundColor: "#ffffff" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.84rem" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#064e3b", color: "#ffffff" }}>
                          <th style={{ padding: "10px 12px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "left", minWidth: "180px" }}>
                            ผู้เข้าพัก / สถาบัน / เบอร์โทร
                          </th>
                          <th style={{ padding: "10px 8px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "center", minWidth: "90px" }}>
                            ห้องพัก
                          </th>
                          <th style={{ padding: "10px 10px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "left", minWidth: "180px" }}>
                            ช่วงเวลาพัก & สูตรคำนวณ
                          </th>
                          {THAI_FISCAL_MONTHS.map((m) => (
                            <th key={m.key} style={{ padding: "10px 4px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "center", minWidth: "62px" }}>
                              {m.name}
                            </th>
                          ))}
                          <th style={{ padding: "10px 8px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "right", minWidth: "90px" }}>
                            ยอดรวม (บ.)
                          </th>
                          <th style={{ padding: "10px 8px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "right", minWidth: "90px" }}>
                            จ่ายแล้ว (บ.)
                          </th>
                          <th style={{ padding: "10px 8px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "right", minWidth: "90px" }}>
                            ค้างชำระ (บ.)
                          </th>
                          <th style={{ padding: "10px 8px", border: "1px solid #047857", whiteSpace: "nowrap", textAlign: "center", minWidth: "100px" }}>
                            จัดการ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklistStays.length === 0 ? (
                          <tr>
                            <td colSpan={20} style={{ textAlign: "center", padding: "30px", color: "#64748b" }}>
                              ไม่พบข้อมูลผู้เข้าพักตามเงื่อนไขที่เลือก
                            </td>
                          </tr>
                        ) : (
                          checklistStays.map((stay, idx) => {
                            const fee = stay.feeAmount || 0;
                            const paid = stay.paidAmount !== undefined ? stay.paidAmount : (stay.paymentStatus === "PAID" ? fee : 0);
                            const remaining = stay.remainingBalance !== undefined ? stay.remainingBalance : Math.max(0, fee - paid);
                            const isPaid = remaining === 0;
                            const isPartial = paid > 0 && remaining > 0;
                            const isOverdue = paid === 0 && remaining > 0;

                            const activeMonths = extractActiveMonths(stay.dateRange, stay.stayMonths);

                            return (
                              <tr
                                key={stay.id || idx}
                                style={{
                                  backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                                  borderBottom: "1px solid #e2e8f0",
                                  transition: "background-color 0.15s ease"
                                }}
                              >
                                <td style={{ padding: "8px 12px", borderRight: "1px solid #e2e8f0" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                    <span style={{ fontWeight: "700", color: "#0f172a" }}>{stay.traineeName}</span>
                                    {isStayActive(stay) ? (
                                      <Badge color="success" style={{ fontSize: "0.68rem" }}>🟢 กำลังพัก</Badge>
                                    ) : (
                                      <Badge color="secondary" style={{ fontSize: "0.68rem", backgroundColor: "#94a3b8" }}>🚪 ออกแล้ว</Badge>
                                    )}
                                  </div>
                                  <div style={{ fontSize: "0.78rem", color: "#64748b" }}>{stay.institution}</div>
                                  {stay.phone && (
                                    <div style={{ fontSize: "0.76rem", color: "#0284c7" }}>
                                      📞 {stay.phone}
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: "8px 8px", textAlign: "center", borderRight: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                                  <span style={{ backgroundColor: "#e0f2fe", color: "#0369a1", padding: "3px 8px", borderRadius: "6px", fontWeight: "700", fontSize: "0.82rem", display: "inline-block" }}>
                                    {stay.building} ห้อง {stay.roomNo}
                                  </span>
                                  <div style={{ fontSize: "0.74rem", color: "#64748b", marginTop: "2px" }}>
                                    ชั้น {stay.floor}
                                  </div>
                                </td>
                                <td style={{ padding: "8px 10px", borderRight: "1px solid #e2e8f0" }}>
                                  <div style={{ fontWeight: "600", color: "#334155" }}>{formatStayDateRangeThai(stay.dateRange, stay.checkInDate, stay.checkOutDate)}</div>
                                  <div style={{ fontSize: "0.76rem", color: "#059669", marginTop: "2px", fontWeight: "600" }}>
                                    💡 {stay.prorationText || "คำนวณตามสูตรหอพัก"}
                                  </div>
                                </td>

                                {/* 12 Thai Months Columns */}
                                {THAI_FISCAL_MONTHS.map((m) => {
                                  const isActive = activeMonths.has(m.name);
                                  return (
                                    <td
                                      key={m.key}
                                      style={{
                                        padding: "6px 2px",
                                        textAlign: "center",
                                        borderRight: "1px solid #e2e8f0",
                                        backgroundColor: isActive ? (isPaid ? "#f0fdf4" : isPartial ? "#fffbeb" : "#fef2f2") : "transparent"
                                      }}
                                    >
                                      {isActive ? (
                                        isPaid ? (
                                          <span
                                            title={`เดือน ${m.name}: จ่ายแล้วเรียบร้อย`}
                                            style={{
                                              backgroundColor: "#dcfce7",
                                              color: "#166534",
                                              border: "1px solid #86efac",
                                              borderRadius: "4px",
                                              padding: "3px 4px",
                                              fontSize: "0.75rem",
                                              fontWeight: "800",
                                              display: "inline-block"
                                            }}
                                          >
                                            ✓ จ่ายแล้ว
                                          </span>
                                        ) : isPartial ? (
                                          <span
                                            title={`เดือน ${m.name}: จ่ายบางส่วน ค้างชำระ`}
                                            style={{
                                              backgroundColor: "#fef3c7",
                                              color: "#92400e",
                                              border: "1px solid #fcd34d",
                                              borderRadius: "4px",
                                              padding: "3px 4px",
                                              fontSize: "0.72rem",
                                              fontWeight: "700",
                                              display: "inline-block"
                                            }}
                                          >
                                            ⚠️ บางส่วน
                                          </span>
                                        ) : (
                                          <span
                                            title={`เดือน ${m.name}: ยังไม่ชำระ (ทวงถาม)`}
                                            style={{
                                              backgroundColor: "#fee2e2",
                                              color: "#991b1b",
                                              border: "1px solid #fca5a5",
                                              borderRadius: "4px",
                                              padding: "3px 4px",
                                              fontSize: "0.72rem",
                                              fontWeight: "700",
                                              display: "inline-block"
                                            }}
                                          >
                                            ⏳ ค้างจ่าย
                                          </span>
                                        )
                                      ) : (
                                        <span style={{ color: "#cbd5e1", fontWeight: "bold" }}>-</span>
                                      )}
                                    </td>
                                  );
                                })}

                                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "700", color: "#0f172a", borderRight: "1px solid #e2e8f0" }}>
                                  {fee.toLocaleString()}
                                </td>
                                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "700", color: "#166534", borderRight: "1px solid #e2e8f0" }}>
                                  {paid.toLocaleString()}
                                </td>
                                <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "800", color: remaining > 0 ? "#dc2626" : "#64748b", borderRight: "1px solid #e2e8f0", backgroundColor: remaining > 0 ? "#fef2f2" : "transparent" }}>
                                  {remaining > 0 ? remaining.toLocaleString() : "0"}
                                </td>
                                <td style={{ padding: "8px 8px", textAlign: "center", whiteSpace: "nowrap" }}>
                                  <Button
                                    color={remaining > 0 ? "danger" : "info"}
                                    size="sm"
                                    style={{ fontSize: "0.78rem", padding: "4px 8px", fontWeight: "bold" }}
                                    onClick={() => handleOpenPaymentModal(stay)}
                                  >
                                    <AttachMoneyIcon style={{ fontSize: "1rem", verticalAlign: "middle", marginRight: "2px" }} />
                                    {remaining > 0 ? "บันทึกจ่ายเงิน" : "อัพเดทสถานะ"}
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Modal: Manage Bed Capacity */}
      <Modal isOpen={manageBedsModal} toggle={() => setManageBedsModal(false)} size="md" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setManageBedsModal(false)} style={{ backgroundColor: "#0284c7", color: "#fff" }}>
          🛏️ จัดการจำนวนเตียงพัก: {editingRoomBeds?.buildingName} ห้อง {editingRoomBeds?.roomNo}
        </ModalHeader>
        <ModalBody>
          {editingRoomBeds && (
            <div>
              <div style={{ backgroundColor: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
                <div className="row g-2">
                  <div className="col-6"><strong>อาคาร:</strong> {editingRoomBeds.buildingName}</div>
                  <div className="col-6"><strong>ชั้น:</strong> ชั้น {editingRoomBeds.floor}</div>
                  <div className="col-6"><strong>หมายเลขห้อง:</strong> ห้อง {editingRoomBeds.roomNo}</div>
                  <div className="col-6"><strong>ประเภทห้อง:</strong> {editingRoomBeds.type || "ทั่วไป"}</div>
                  <div className="col-12 mt-1" style={{ fontSize: "0.82rem", color: "#0369a1" }}>
                    ℹ️ อัตราค่าหอพัก: {editingRoomBeds.fee} บาท/เดือน ({editingRoomBeds.dailyFee} บาท/วัน)
                  </div>
                </div>
              </div>

              <div className="text-center my-3">
                <label className="form-label font-weight-bold" style={{ fontSize: "1rem", color: "#0f172a", display: "block", marginBottom: "12px" }}>
                  กำหนดจำนวนเตียงในห้องนี้ (ความจุเตียง)
                </label>
                <div className="d-flex justify-content-center align-items-center" style={{ gap: "16px" }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-lg"
                    style={{ width: "48px", height: "48px", borderRadius: "50%", fontSize: "1.4rem", fontWeight: "bold", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={() => setNewBedCapacity((prev) => Math.max(1, parseInt(prev, 10) - 1))}
                    disabled={newBedCapacity <= 1}
                  >
                    -
                  </button>
                  <div style={{ width: "110px" }}>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      className="form-control form-control-lg text-center font-weight-bold"
                      style={{ fontSize: "1.7rem", color: "#0369a1", height: "54px" }}
                      value={newBedCapacity}
                      onChange={(e) => setNewBedCapacity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-lg"
                    style={{ width: "48px", height: "48px", borderRadius: "50%", fontSize: "1.4rem", fontWeight: "bold", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                    onClick={() => setNewBedCapacity((prev) => parseInt(prev, 10) + 1)}
                  >
                    +
                  </button>
                </div>
                <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                  ปัจจุบันกำหนดไว้: <strong>{editingRoomBeds.capacity || 2} เตียง</strong>
                  {newBedCapacity !== (editingRoomBeds.capacity || 2) && (
                    <span style={{ color: newBedCapacity > (editingRoomBeds.capacity || 2) ? "#16a34a" : "#dc2626", fontWeight: "bold", marginLeft: "6px" }}>
                      ➜ จะเปลี่ยนเป็น {newBedCapacity} เตียง
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setManageBedsModal(false)}>ยกเลิก</Button>
          <Button color="info" onClick={handleSaveBedCapacity} disabled={isSavingBeds}>
            {isSavingBeds ? "กำลังบันทึก..." : "💾 บันทึกจำนวนเตียง"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal: Room Details */}
      <Modal isOpen={roomModal} toggle={() => setRoomModal(false)} size="lg" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setRoomModal(false)} style={{ backgroundColor: "#00838f", color: "#fff" }}>
          🏢 รายละเอียดห้องพัก: {selectedRoom?.buildingName} ห้อง {selectedRoom?.roomNo}
        </ModalHeader>
        <ModalBody>
          {selectedRoom && (
            <div>
              {(() => {
                const currentOccs = roomOccupantsMap[`${selectedRoom.buildingName}-${selectedRoom.roomNo}`] || roomOccupantsMap[selectedRoom.roomNo] || [];
                const futureOccs = roomFutureOccupantsMap[`${selectedRoom.buildingName}-${selectedRoom.roomNo}`] || roomFutureOccupantsMap[selectedRoom.roomNo] || [];
                const hasStaffInRoom = currentOccs.some(isStaffOccupant) || futureOccs.some(isStaffOccupant);
                const isWelfare = isWelfareRoom(selectedRoom) || hasStaffInRoom;

                return (
                  <div>
                    {isWelfare && (
                      <div
                        style={{
                          backgroundColor: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          color: "#1e40af",
                          padding: "10px 14px",
                          borderRadius: "8px",
                          marginBottom: "14px",
                          fontSize: "0.85rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                      >
                        <span style={{ fontSize: "1.2rem" }}>🔒</span>
                        <div>
                          <strong>ห้องพักนี้เป็นห้องสวัสดิการบุคลากร / เจ้าหน้าที่โรงพยาบาล:</strong>
                          <div style={{ fontSize: "0.78rem", color: "#3b82f6" }}>
                            ไม่มีค่าเช่าห้องพัก และสงวนสิทธิ์การแก้ไข/บันทึกการเงิน/ย้ายออกที่ระบบจัดการหอพักบุคลากร
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="row mb-3" style={{ backgroundColor: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                      <div className="col-md-4 mb-2"><strong>อาคาร:</strong> {selectedRoom.buildingName}</div>
                      <div className="col-md-4 mb-2"><strong>ชั้น:</strong> ชั้น {selectedRoom.floor}</div>
                      <div className="col-md-4 mb-2">
                        <strong>ความจุเตียง:</strong> {selectedRoom.capacity} เตียง{" "}
                        {isWelfare ? (
                          <span
                            className="badge badge-light ml-1"
                            style={{ fontSize: "0.74rem", padding: "3px 6px", color: "#64748b", border: "1px solid #cbd5e1" }}
                          >
                            🔒 ห้องเจ้าหน้าที่
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-info ml-1"
                            style={{ fontSize: "0.74rem", padding: "1px 7px", borderRadius: "4px" }}
                            onClick={() => handleOpenManageBeds(selectedRoom)}
                          >
                            🛏️ จัดการเตียง
                          </button>
                        )}
                      </div>
                      <div className="col-md-4 mb-2"><strong>ประเภทห้อง:</strong> {isWelfare ? "ห้องสวัสดิการ รพ." : (selectedRoom.type || "ทั่วไป")}</div>
                      <div className="col-md-4 mb-2">
                        <strong>อัตราค่าเช่า:</strong>{" "}
                        {isWelfare
                          ? "ห้องสวัสดิการ รพ. (ไม่มีค่าเช่าห้องพัก)"
                          : selectedRoom.electricityAmount > 0
                          ? `${selectedRoom.fee} บ./ด. + ค่าไฟ ${Number(selectedRoom.electricityAmount).toLocaleString()} บ. (รวม ${(Number(selectedRoom.fee) + Number(selectedRoom.electricityAmount)).toLocaleString()} บ.)`
                          : `${selectedRoom.fee} บาท/เดือน (${selectedRoom.dailyFee} บาท/วัน)`}
                      </div>
                      <div className="col-md-4 mb-2"><strong>หมายเหตุ:</strong> {selectedRoom.notes || "-"}</div>
                    </div>
                  </div>
                );
              })()}

              {/* บัตรแสดงข้อมูลค่าไฟมิเตอร์ที่ดึงจากระบบจัดการห้องพักอัตโนมัติ */}
              {selectedRoom.electricityAmount > 0 && selectedRoom.electricity && (
                <div
                  className="p-3 mb-3 rounded shadow-sm d-flex align-items-center justify-content-between flex-wrap gap-2"
                  style={{ backgroundColor: "#fffbeb", border: "1.5px solid #fde047" }}
                >
                  <div className="d-flex align-items-center">
                    <span style={{ fontSize: "1.8rem", marginRight: "12px" }}>⚡</span>
                    <div>
                      <div style={{ fontWeight: "800", color: "#92400e", fontSize: "0.96rem" }}>
                        ค่าไฟฟ้ารายเดือน: ${Number(selectedRoom.electricityAmount).toLocaleString()} บาท (ดึงจากระบบจดมิเตอร์อัตโนมัติ)
                      </div>
                      <div style={{ fontSize: "0.82rem", color: "#b45309", marginTop: "2px" }}>
                        จดมิเตอร์: <b>${selectedRoom.electricity.previousMeter}</b> ➔ <b>${selectedRoom.electricity.currentMeter}</b>{" "}
                        (ใช้จริง ${selectedRoom.electricity.netUnits || selectedRoom.electricity.totalUnits} หน่วย @ ${selectedRoom.electricity.ratePerUnit || 8} บ./หน่วย)
                        {selectedRoom.electricity.meterDate ? ` • วันที่จด: ${selectedRoom.electricity.meterDate.split("T")[0]}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span
                      className="badge badge-warning"
                      style={{
                        fontSize: "0.84rem",
                        backgroundColor: "#fef08a",
                        color: "#854d0e",
                        border: "1.5px solid #facc15",
                        padding: "5px 10px",
                        borderRadius: "8px"
                      }}
                    >
                      เฉลี่ยคนละ ${Math.round(selectedRoom.electricityAmount / Math.max(1, (roomOccupantsMap[`${selectedRoom.buildingName}-${selectedRoom.roomNo}`] || roomOccupantsMap[selectedRoom.roomNo] || []).length)).toLocaleString()} บาท
                    </span>
                  </div>
                </div>
              )}

              {/* ผู้พักปัจจุบัน */}
              <h6 style={{ fontWeight: "bold", color: "#00695c", marginBottom: "8px" }}>
                🟢 ผู้พักอาศัยปัจจุบัน (กำลังพักอาศัย / อยู่ในช่วงวันฝึก)
              </h6>
              {(() => {
                const currentOccs = roomOccupantsMap[`${selectedRoom.buildingName}-${selectedRoom.roomNo}`] || roomOccupantsMap[selectedRoom.roomNo] || [];
                if (currentOccs.length === 0) {
                  return (
                    <div style={{ padding: "10px 14px", backgroundColor: "#e8f5e9", color: "#2e7d32", borderRadius: "6px", marginBottom: "16px", fontSize: "0.88rem" }}>
                      ✅ ปัจจุบันไม่มีผู้พักอาศัยในห้องนี้ (ห้องว่างพร้อมรับเข้าพัก)
                    </div>
                  );
                }
                return (
                  <div style={{ overflowX: "auto", marginBottom: "16px" }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: "0.85rem" }}>
                      <thead style={{ backgroundColor: "#e0f2f1", color: "#004d40" }}>
                        <tr>
                          <th>ชื่อ-สกุล ผู้เข้าพัก</th>
                          <th>ตำแหน่ง / ประเภท</th>
                          <th>สถาบัน / ต้นสังกัด</th>
                          <th>ช่วงเวลาเข้าพัก</th>
                          <th style={{ textAlign: "center" }}>สถานะการเงิน</th>
                          <th style={{ textAlign: "center", width: "130px" }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOccs.map((occ) => {
                          const isStaff = isStaffOccupant(occ);
                          const isWelfareStayItem = isStaff || isWelfareStay(occ, selectedRoom);

                          return (
                            <tr key={occ.id}>
                              <td style={{ fontWeight: "bold", color: isStaff ? "#0284c7" : "#111" }}>{occ.traineeName}</td>
                              <td>
                                <div>{occ.position || occ.traineeType}</div>
                                {isStaff && (
                                  <Badge color="info" style={{ fontSize: "0.68rem", backgroundColor: "#0284c7" }}>
                                    เจ้าหน้าที่ รพ.
                                  </Badge>
                                )}
                              </td>
                              <td>{occ.institution}</td>
                              <td>{formatStayDateRangeThai(occ.dateRange, occ.checkInDate, occ.checkOutDate)}</td>
                              <td style={{ textAlign: "center" }}>
                                {isWelfareStayItem ? (
                                  <Badge style={{ backgroundColor: "#0284c7", color: "#ffffff" }}>
                                    🏠 สวัสดิการ รพ. (ไม่มีค่าเช่า)
                                  </Badge>
                                ) : (
                                  <Badge color={occ.paymentStatus === "OVERDUE" ? "danger" : "success"}>
                                    {occ.feeText}
                                  </Badge>
                                )}
                              </td>
                              <td style={{ textAlign: "center", width: "190px", whiteSpace: "nowrap" }}>
                                {isWelfareStayItem ? (
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#64748b",
                                      fontWeight: "600",
                                      padding: "4px 8px",
                                      backgroundColor: "#f1f5f9",
                                      borderRadius: "4px",
                                      border: "1px solid #cbd5e1"
                                    }}
                                    title="สงวนสิทธิ์การจัดการที่ระบบหอพักบุคลากร"
                                  >
                                    🔒 เจ้าหน้าที่ (ดูอย่างเดียว)
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleOpenPaymentModal(occ)}
                                      className="btn btn-sm mr-1"
                                      style={{
                                        backgroundColor: isStayOverdue(occ) ? "#dc2626" : "#0284c7",
                                        color: "#ffffff",
                                        border: "none",
                                        borderRadius: "4px",
                                        padding: "3px 8px",
                                        fontSize: "0.75rem",
                                        fontWeight: "bold",
                                        cursor: "pointer"
                                      }}
                                      title="คลิกเพื่อบันทึกหรืออัพเดทสถานะการเงิน"
                                    >
                                      💳 การเงิน
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleOpenCheckoutModal(occ)}
                                      className="btn btn-sm btn-outline-danger"
                                      style={{
                                        padding: "3px 8px",
                                        fontSize: "0.75rem",
                                        fontWeight: "bold",
                                        borderRadius: "4px"
                                      }}
                                      title="กดยืนยันการย้ายออกและคำนวณค่าใช้จ่ายตามเวลาจริง"
                                    >
                                      🚪 ย้ายออก
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* ผู้เข้าพักล่วงหน้า / ในอนาคต (ไม่ใช่ตอนนี้ - ยังไม่ถึงเวลา) */}
              {(() => {
                const futureOccs = roomFutureOccupantsMap[`${selectedRoom.buildingName}-${selectedRoom.roomNo}`] || roomFutureOccupantsMap[selectedRoom.roomNo] || [];
                return (
                  <div style={{ marginBottom: "16px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 style={{ fontWeight: "bold", color: "#0284c7", margin: 0 }}>
                        ⏳ ผู้เข้าพักล่วงหน้า / ในอนาคต (ไม่ใช่ตอนนี้ - ยังไม่ถึงเวลาเข้าพัก)
                      </h6>
                      <Badge color="info" style={{ backgroundColor: "#0284c7", fontSize: "0.74rem" }}>
                        {futureOccs.length} คน (ยังไม่นับเป็นผู้พักปัจจุบัน)
                      </Badge>
                    </div>

                    {futureOccs.length === 0 ? (
                      <div style={{ padding: "8px 12px", backgroundColor: "#f0f9ff", color: "#0369a1", borderRadius: "6px", fontSize: "0.85rem" }}>
                        ไม่มีรายการจองหรือผู้เข้าพักล่วงหน้าในห้องนี้
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table className="table table-sm table-bordered" style={{ fontSize: "0.85rem" }}>
                          <thead style={{ backgroundColor: "#e0f2fe", color: "#0369a1" }}>
                            <tr>
                              <th>ชื่อ-สกุล ผู้เข้าพัก</th>
                              <th>ตำแหน่ง / ประเภท</th>
                              <th>สถาบัน / ต้นสังกัด</th>
                              <th>ช่วงเวลาเข้าพัก</th>
                              <th style={{ textAlign: "center" }}>สถานะช่วงเวลา</th>
                              <th style={{ textAlign: "center" }}>ค่าหอพัก</th>
                              <th style={{ textAlign: "center", width: "190px" }}>จัดการ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {futureOccs.map((occ) => {
                              const isStaff = isStaffOccupant(occ);
                              const isWelfareStayItem = isStaff || isWelfareStay(occ, selectedRoom);

                              return (
                                <tr key={occ.id} style={{ backgroundColor: "#f8fafc" }}>
                                  <td style={{ fontWeight: "bold", color: "#0369a1" }}>{occ.traineeName}</td>
                                  <td>
                                    <div>{occ.position || occ.traineeType}</div>
                                    {isStaff && (
                                      <Badge color="info" style={{ fontSize: "0.68rem", backgroundColor: "#0284c7" }}>
                                        เจ้าหน้าที่ รพ.
                                      </Badge>
                                    )}
                                  </td>
                                  <td>{occ.institution}</td>
                                  <td>{occ.dateRange}</td>
                                  <td style={{ textAlign: "center" }}>
                                    <Badge color="info" style={{ backgroundColor: "#0284c7", fontSize: "0.72rem" }}>
                                      ⏳ ยังไม่ถึงเวลา (ไม่ใช่ตอนนี้)
                                    </Badge>
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {isWelfareStayItem ? (
                                      <Badge style={{ backgroundColor: "#0284c7", color: "#ffffff" }}>
                                        🏠 สวัสดิการ รพ.
                                      </Badge>
                                    ) : (
                                      <Badge color={occ.paymentStatus === "OVERDUE" ? "danger" : "success"}>
                                        {occ.feeText}
                                      </Badge>
                                    )}
                                  </td>
                                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                    {isWelfareStayItem ? (
                                      <span
                                        style={{
                                          fontSize: "0.75rem",
                                          color: "#64748b",
                                          fontWeight: "600",
                                          padding: "3px 8px",
                                          backgroundColor: "#f1f5f9",
                                          borderRadius: "4px",
                                          border: "1px solid #cbd5e1"
                                        }}
                                        title="สงวนสิทธิ์การจัดการที่ระบบหอพักบุคลากร"
                                      >
                                        🔒 เจ้าหน้าที่ (ดูอย่างเดียว)
                                      </span>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => handleOpenPaymentModal(occ)}
                                          className="btn btn-sm mr-1"
                                          style={{
                                            backgroundColor: isStayOverdue(occ) ? "#dc2626" : "#0284c7",
                                            color: "#ffffff",
                                            border: "none",
                                            borderRadius: "4px",
                                            padding: "3px 8px",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold",
                                            cursor: "pointer"
                                          }}
                                          title="คลิกเพื่อบันทึกหรืออัพเดทสถานะการเงิน"
                                        >
                                          💳 การเงิน
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveFutureOccupant(occ)}
                                          className="btn btn-sm btn-outline-danger"
                                          style={{
                                            padding: "3px 8px",
                                            fontSize: "0.75rem",
                                            fontWeight: "bold",
                                            borderRadius: "4px"
                                          }}
                                          title="ยกเลิกการจองห้องพักและเอาออกจากห้องนี้"
                                        >
                                          ❌ เอาออก
                                        </button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ fontSize: "0.78rem", color: "#64748b", fontStyle: "italic", marginTop: "2px" }}>
                          * บุคคลเหล่านี้ได้ลงทะเบียนจองห้องพักไว้ล่วงหน้า ยังไม่ถึงช่วงเวลาเข้าพักจริง (ไม่ใช่ตอนนี้) และระบบจะนับเป็นผู้เข้าพักปัจจุบันโดยอัตโนมัติเมื่อถึงช่วงวันเวลาที่เข้าพักจริง
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ประวัติผู้ที่ออกไปแล้ว (พ้นวันฝึก) */}
              <h6 style={{ fontWeight: "bold", color: "#546e7a", marginBottom: "8px" }}>
                🚪 ประวัติผู้ที่เคยเข้าพักเดิม (ออกไปแล้ว / พ้นวันฝึกแล้ว)
              </h6>
              {(() => {
                const pastOccs = roomPastOccupantsMap[`${selectedRoom.buildingName}-${selectedRoom.roomNo}`] || roomPastOccupantsMap[selectedRoom.roomNo] || [];
                if (pastOccs.length === 0) {
                  return (
                    <div style={{ padding: "8px 12px", backgroundColor: "#f5f5f5", color: "#888", borderRadius: "6px", fontSize: "0.85rem" }}>
                      ไม่มีประวัติการเข้าพักเดิมที่ออกไปแล้วในห้องนี้
                    </div>
                  );
                }
                return (
                  <div style={{ overflowX: "auto" }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: "0.85rem" }}>
                      <thead style={{ backgroundColor: "#f1f5f9", color: "#475569" }}>
                        <tr>
                          <th>ชื่อ-สกุล ผู้เข้าพัก</th>
                          <th>ตำแหน่ง / ประเภท</th>
                          <th>สถาบัน / ต้นสังกัด</th>
                          <th>ช่วงเวลาเข้าพักเดิม</th>
                          <th style={{ textAlign: "center" }}>สถานะการพัก</th>
                          <th style={{ textAlign: "center" }}>ค่าหอพัก</th>
                          <th style={{ textAlign: "center", width: "130px" }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pastOccs.map((occ) => {
                          const isStaff = isStaffOccupant(occ);
                          const isWelfareStayItem = isStaff || isWelfareStay(occ, selectedRoom);

                          return (
                            <tr key={occ.id} style={{ backgroundColor: "#fafafa" }}>
                              <td style={{ color: isStaff ? "#0284c7" : "#333", fontWeight: isStayOverdue(occ) ? "bold" : "normal" }}>
                                {occ.traineeName}
                              </td>
                              <td>
                                <div>{occ.position || occ.traineeType}</div>
                                {isStaff && (
                                  <Badge color="info" style={{ fontSize: "0.68rem", backgroundColor: "#0284c7" }}>
                                    เจ้าหน้าที่ รพ.
                                  </Badge>
                                )}
                              </td>
                              <td>{occ.institution}</td>
                              <td>{formatStayDateRangeThai(occ.dateRange, occ.checkInDate, occ.checkOutDate)}</td>
                              <td style={{ textAlign: "center" }}>
                                <Badge color="secondary" style={{ backgroundColor: "#78909c" }}>
                                  🚪 ออกไปแล้ว
                                </Badge>
                                <div style={{ fontSize: "0.7rem", color: "#888" }}>พ้นวันฝึกแล้ว</div>
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {isWelfareStayItem ? (
                                  <Badge style={{ backgroundColor: "#0284c7", color: "#ffffff" }}>
                                    🏠 สวัสดิการ รพ.
                                  </Badge>
                                ) : isStayOverdue(occ) ? (
                                  <Badge color="danger">
                                    {occ.feeText}
                                  </Badge>
                                ) : (
                                  <span>{occ.feeText}</span>
                                )}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                {isWelfareStayItem ? (
                                  <span
                                    style={{
                                      fontSize: "0.75rem",
                                      color: "#64748b",
                                      fontWeight: "600",
                                      padding: "3px 8px",
                                      backgroundColor: "#f1f5f9",
                                      borderRadius: "4px",
                                      border: "1px solid #cbd5e1"
                                    }}
                                    title="สงวนสิทธิ์การจัดการที่ระบบหอพักบุคลากร"
                                  >
                                    🔒 เจ้าหน้าที่ (ดูอย่างเดียว)
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenPaymentModal(occ)}
                                    className="btn btn-sm"
                                    style={{
                                      backgroundColor: isStayOverdue(occ) ? "#dc2626" : "#0284c7",
                                      color: "#ffffff",
                                      border: "none",
                                      borderRadius: "4px",
                                      padding: "3px 8px",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      cursor: "pointer"
                                    }}
                                    title="คลิกเพื่อบันทึกหรืออัพเดทสถานะการเงิน"
                                  >
                                    💳 อัพเดทการเงิน
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRoomModal(false)}>ปิด</Button>
        </ModalFooter>
      </Modal>

      {/* Modal: Check-in / Assign Stay */}
      <Modal isOpen={stayModal} toggle={() => setStayModal(false)} size="lg" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setStayModal(false)} style={{ backgroundColor: "#00838f", color: "#fff" }}>
          ➕ จัดสรรห้องพัก / บันทึกการเข้าพักหอพัก
        </ModalHeader>
        <ModalBody>
          <div className="row g-3">
            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">เลือกอาคาร / ตึก *</label>
              <select
                className="form-control"
                value={stayForm.building}
                onChange={(e) => handleStayBuildingChange(e.target.value)}
              >
                <option value="ตึก 1">🏢 อาคารหอพัก 1 (ตึก 1)</option>
                <option value="ตึก 2">🏢 อาคารหอพัก 2 (ตึก 2)</option>
                <option value="ตึก 3">🏢 อาคารหอพัก 3 (ตึก 3)</option>
                <option value="ตึก 4">🏢 อาคารหอพัก 4 (ตึก 4)</option>
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">เลือกชั้น *</label>
              <select
                className="form-control"
                value={stayForm.floor}
                onChange={(e) => handleStayFloorChange(Number(e.target.value))}
              >
                <option value={1}>ชั้น 1</option>
                <option value={2}>ชั้น 2</option>
                <option value={3}>ชั้น 3</option>
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">เลือกหมายเลขห้อง *</label>
              <select
                className="form-control"
                value={stayForm.roomNo}
                onChange={(e) => handleStayRoomNoChange(e.target.value)}
                required
              >
                <option value="">-- กรุณาเลือกห้องพัก ({roomsOnFloor.length} ห้อง) --</option>
                {roomsOnFloor.map((r) => {
                  const rNum = getRoomNumberStr(r);
                  const rType = r.type || r.RoomType || r.roomType || "ทั่วไป";
                  const rCap = r.capacity || r.BedCapacity || r.bedCapacity || 2;
                  return (
                    <option key={r.id || r.RoomID || rNum} value={rNum}>
                      ห้อง {rNum} ({rType} - {rCap} เตียง)
                    </option>
                  );
                })}
                {stayForm.roomNo && !roomsOnFloor.some((r) => getRoomNumberStr(r) === String(stayForm.roomNo)) && (
                  <option value={stayForm.roomNo}>
                    ห้อง {stayForm.roomNo} (ห้องที่เลือกไว้)
                  </option>
                )}
              </select>
            </div>

            {/* เลือกผู้เข้าพักจากฐานข้อมูล / คนที่เพิ่มไป */}
            <div className="col-md-12 mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap" style={{ gap: "8px" }}>
                <label className="form-label font-weight-bold" style={{ color: "#00838f", fontSize: "0.95rem", margin: 0 }}>
                  🔍 เลือกรายชื่อผู้เข้าพักจากระบบ (ดึงข้อมูลอัตโนมัติ)
                </label>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setOnlyActiveOrFuture((prev) => !prev)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOnlyActiveOrFuture((prev) => !prev);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.2s ease",
                    backgroundColor: onlyActiveOrFuture ? "#ecfdf5" : "#f1f5f9",
                    border: onlyActiveOrFuture ? "1.5px solid #10b981" : "1.5px solid #cbd5e1",
                    boxShadow: onlyActiveOrFuture ? "0 1px 4px rgba(16, 185, 129, 0.15)" : "none",
                    margin: 0
                  }}
                  title="คลิกเพื่อสลับการกรองรายชื่อ"
                >
                  <span
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: "32px",
                      height: "18px",
                      backgroundColor: onlyActiveOrFuture ? "#10b981" : "#cbd5e1",
                      borderRadius: "18px",
                      transition: "background-color 0.2s ease",
                      flexShrink: 0
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: "2px",
                        left: onlyActiveOrFuture ? "16px" : "2px",
                        width: "14px",
                        height: "14px",
                        backgroundColor: "#ffffff",
                        borderRadius: "50%",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                        transition: "left 0.2s ease"
                      }}
                    />
                  </span>
                  <input
                    type="checkbox"
                    id="filterActiveOnly"
                    checked={onlyActiveOrFuture}
                    onChange={(e) => setOnlyActiveOrFuture(e.target.checked)}
                    style={{
                      position: "absolute",
                      opacity: 0,
                      width: 0,
                      height: 0,
                      margin: 0,
                      padding: 0,
                      pointerEvents: "none",
                      display: "none"
                    }}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      color: onlyActiveOrFuture ? "#047857" : "#64748b",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {onlyActiveOrFuture
                      ? "กรองเฉพาะคนที่อยู่ในช่วงเวลา & อนาคต"
                      : "แสดงทุกคน (รวมคนที่พ้นกำหนดแล้ว)"}
                  </span>
                </div>
              </div>

              <Select
                options={traineeSelectOptions}
                value={selectedCandidateOption}
                onChange={(selected) => {
                  if (!selected) {
                    handleSelectTraineeCandidate("");
                  } else {
                    handleSelectTraineeCandidate(selected.value);
                  }
                }}
                placeholder="🔍 พิมพ์เพื่อค้นหาชื่อแพทย์/นักศึกษา, สถาบัน หรือเลือกจากรายการ..."
                isClearable
                noOptionsMessage={() => "ไม่พบรายชื่อผู้เข้าพักที่ตรงกับคำค้นหา (หรือทุกคนมีห้องพักแล้ว)"}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: "#f0fdfa",
                    borderColor: state.isFocused ? "#00838f" : "#5eead4",
                    boxShadow: state.isFocused ? "0 0 0 1px #00838f" : "none",
                    fontWeight: "600",
                    fontSize: "0.9rem"
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  }),
                  groupHeading: (base) => ({
                    ...base,
                    fontWeight: "bold",
                    color: "#00695c",
                    fontSize: "0.85rem"
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: "0.88rem",
                    backgroundColor: state.isFocused ? "#e0f2fe" : "#ffffff",
                    color: "#1e293b",
                    cursor: "pointer"
                  })
                }}
              />
              <small className="text-info font-weight-bold d-block mt-1" style={{ fontSize: "0.78rem" }}>
                * สามารถพิมพ์ค้นหาชื่อได้ทันที และระบบซ่อนคนที่มีห้องพักอยู่แล้ว (ทั้งปัจจุบันและอนาคต) เพื่อป้องกันการจัดห้องซ้ำ
              </small>
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">
                ชื่อ-สกุล ผู้เข้าพัก *
                {stayForm.selectedTraineeId && stayForm.selectedTraineeId !== "CUSTOM" && (
                  <span className="badge badge-success ml-2" style={{ fontSize: "0.72rem" }}>
                    ✓ ดึงข้อมูลแล้ว
                  </span>
                )}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น พญ.เกศราภรณ์ เสาะหายิ่ง"
                value={stayForm.traineeName}
                onChange={(e) => setStayForm({ ...stayForm, traineeName: e.target.value })}
                required
              />
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">ประเภทผู้ฝึกอบรม</label>
              <select
                className="form-control"
                value={stayForm.traineeType}
                onChange={(e) => setStayForm({ ...stayForm, traineeType: e.target.value })}
              >
                <option value="DOCTOR">👨‍⚕️ แพทย์ฝึกอบรม / ดูงาน</option>
                <option value="STUDENT">🎓 นักศึกษาฝึกงาน</option>
              </select>
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">ตำแหน่ง / สาขาวิชา</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น นายแพทย์ปฏิบัติงาน หรือ นศ.เภสัชฯ"
                value={stayForm.position}
                onChange={(e) => setStayForm({ ...stayForm, position: e.target.value })}
              />
            </div>

            <div className="col-md-6 mb-2">
              <label className="form-label font-weight-bold">สถาบัน / ต้นสังกัด</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น โรงพยาบาลบุรีรัมย์"
                value={stayForm.institution}
                onChange={(e) => setStayForm({ ...stayForm, institution: e.target.value })}
              />
            </div>

            {/* วันที่เริ่มเข้าพัก และ วันที่สิ้นสุด (พ.ศ.) */}
            <div className="col-md-6 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label font-weight-bold">
                  วันที่เริ่มเข้าพัก * (พ.ศ.)
                </label>
                {stayForm.checkInDate && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0"
                    style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                    onClick={() => {
                      const autoRange = formatThaiDateRangeText("", stayForm.checkOutDate);
                      setStayForm({ ...stayForm, checkInDate: "", dateRange: autoRange });
                    }}
                  >
                    ล้างวันที่
                  </button>
                )}
              </div>
              <Datetime
                value={stayForm.checkInDate && moment(stayForm.checkInDate).isValid() ? moment(stayForm.checkInDate) : null}
                renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                inputProps={{
                  placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                  readOnly: "readonly",
                  style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                  className: "form-control",
                  value: stayForm.checkInDate && moment(stayForm.checkInDate).isValid() ? moment(stayForm.checkInDate).format("D MMMM BBBB") : ""
                }}
                closeOnSelect={true}
                closeOnTab={true}
                dateFormat="D MMMM BBBB"
                timeFormat={false}
                onChange={(val) => {
                  let dStr = "";
                  if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                  else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                  const autoRange = formatStayDateRangeThai("", dStr, stayForm.checkOutDate);
                  setStayForm({ ...stayForm, checkInDate: dStr, dateRange: autoRange });
                }}
              />
            </div>

            <div className="col-md-6 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <label className="form-label font-weight-bold">
                  วันที่สิ้นสุดการเข้าพัก (พ.ศ.)
                </label>
                {stayForm.checkOutDate && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0"
                    style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                    onClick={() => {
                      const autoRange = formatStayDateRangeThai("", stayForm.checkInDate, "");
                      setStayForm({ ...stayForm, checkOutDate: "", dateRange: autoRange });
                    }}
                  >
                    ล้างวันที่
                  </button>
                )}
              </div>
              <Datetime
                value={stayForm.checkOutDate && moment(stayForm.checkOutDate).isValid() ? moment(stayForm.checkOutDate) : null}
                renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                inputProps={{
                  placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                  readOnly: "readonly",
                  style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                  className: "form-control",
                  value: stayForm.checkOutDate && moment(stayForm.checkOutDate).isValid() ? moment(stayForm.checkOutDate).format("D MMMM BBBB") : ""
                }}
                closeOnSelect={true}
                closeOnTab={true}
                dateFormat="D MMMM BBBB"
                timeFormat={false}
                onChange={(val) => {
                  let dStr = "";
                  if (moment.isMoment(val) && val.isValid()) dStr = val.format("YYYY-MM-DD");
                  else if (val && typeof val === "string" && moment(val).isValid()) dStr = moment(val).format("YYYY-MM-DD");
                  const autoRange = formatStayDateRangeThai("", stayForm.checkInDate, dStr);
                  setStayForm({ ...stayForm, checkOutDate: dStr, dateRange: autoRange });
                }}
              />
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-label font-weight-bold" style={{ fontSize: "0.85rem", color: "#475569" }}>
                ช่วงเวลาเข้าพัก (จัดรูปแบบข้อความอัตโนมัติ)
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="เช่น 3 พ.ย. - 16 พ.ย. 2568"
                value={stayForm.dateRange}
                onChange={(e) => setStayForm({ ...stayForm, dateRange: e.target.value })}
              />
              <small className="text-muted" style={{ fontSize: "0.78rem" }}>
                * ระบบจัดข้อความ พ.ศ. ให้อัตโนมัติตามวันที่เลือก หรือปรับแต่งข้อความเพิ่มเติมได้
              </small>
            </div>

            {/* แจ้งเตือนเมื่อเป็นการเพิ่มคนเข้าพักในอนาคต (ไม่ใช่ตอนนี้) */}
            {isStayFuture(stayForm) && (
              <div className="col-md-12 mb-2">
                <div
                  style={{
                    backgroundColor: "#f0f9ff",
                    border: "1px solid #7dd3fc",
                    borderRadius: "6px",
                    padding: "10px 14px",
                    fontSize: "0.85rem",
                    color: "#0369a1",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}
                >
                  <span style={{ fontSize: "1.3rem" }}>⏳</span>
                  <div>
                    <strong>ผู้เข้าพักนี้เป็นช่วงเวลาในอนาคต (ไม่ใช่ตอนนี้):</strong>
                    <div style={{ fontSize: "0.8rem", color: "#0c4a6e", marginTop: "2px" }}>
                      ระบบอนุญาตให้ลงทะเบียนจองห้องพักได้ตามปกติ และจะยังไม่นับรวมในยอดผู้เข้าพักปัจจุบันของห้องพัก จนกว่าจะถึงช่วงวันเวลาที่เข้าพักจริง
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* กล่องแจ้งเตือนสถานะเตียงและความจุเตียง / ช่วงเวลาที่ชนกัน */}
            {stayForm.roomNo && (
              <div className="col-md-12 mb-2">
                {overlappingOccupantsInfo.isFull ? (
                  <div
                    style={{
                      backgroundColor: "#fef2f2",
                      border: "2px solid #ef4444",
                      borderRadius: "8px",
                      padding: "14px 16px",
                      boxShadow: "0 2px 6px rgba(239, 68, 68, 0.12)"
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                      <div>
                        <div style={{ color: "#991b1b", fontWeight: "bold", fontSize: "1rem" }}>
                          ⛔ ห้อง {stayForm.roomNo} ({stayForm.building}) เตียงเต็มแล้ว! ({overlappingOccupantsInfo.count}/{overlappingOccupantsInfo.capacity} เตียง)
                        </div>
                        <div style={{ color: "#b91c1c", fontSize: "0.85rem", marginTop: "2px" }}>
                          ช่วงเวลาเข้าพักนี้ ชนกับผู้พักเดิมที่อยู่ในห้องนี้แล้ว {overlappingOccupantsInfo.count} คน (เต็มตามจำนวนเตียงที่กำหนด)
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger font-weight-bold"
                          style={{ fontSize: "0.82rem", padding: "5px 12px", borderRadius: "6px" }}
                          onClick={() => currentSelectedRoom && handleQuickAddOneBed(currentSelectedRoom)}
                          title="เพิ่มเตียงให้อีก 1 เตียงทันที"
                        >
                          ➕ เพิ่ม 1 เตียงทันที (เป็น {overlappingOccupantsInfo.capacity + 1} เตียง)
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger font-weight-bold"
                          style={{ fontSize: "0.82rem", padding: "5px 12px", borderRadius: "6px", backgroundColor: "#ffffff" }}
                          onClick={() => currentSelectedRoom && handleOpenManageBeds(currentSelectedRoom)}
                        >
                          🛏️ Manage จัดการเตียง...
                        </button>
                      </div>
                    </div>

                    <div style={{ fontSize: "0.84rem", color: "#7f1d1d", fontWeight: "bold", marginBottom: "4px" }}>
                      รายชื่อผู้พักที่ช่วงเวลาซ้อนทับกัน ({overlappingOccupantsInfo.count} คน):
                    </div>
                    <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "0.83rem", color: "#991b1b" }}>
                      {overlappingOccupantsInfo.overlaps.map((s) => (
                        <li key={s.id} style={{ marginBottom: "2px" }}>
                          <strong>{s.traineeName}</strong> — {s.institution || s.position} (ช่วงวันที่: {formatStayDateRangeThai(s.dateRange, s.checkInDate, s.checkOutDate)})
                        </li>
                      ))}
                    </ul>
                    <div style={{ marginTop: "8px", fontSize: "0.8rem", color: "#dc2626", fontWeight: "600" }}>
                      ⚠️ ไม่สามารถเพิ่มคนที่ {overlappingOccupantsInfo.count + 1} ได้ เนื่องจากเตียงเต็ม จะเพิ่มได้เมื่อกดปุ่ม "Manage จัดการเตียงเพิ่ม" ด้านบน หรือเลือกห้องพักอื่น
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #86efac",
                      borderRadius: "6px",
                      padding: "8px 14px",
                      fontSize: "0.86rem",
                      color: "#166534",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px"
                    }}
                  >
                    <div>
                      <strong>🟢 สถานะเตียงห้อง {stayForm.roomNo}:</strong> ว่างพร้อมจัดเข้าพัก ({overlappingOccupantsInfo.count}/{overlappingOccupantsInfo.capacity} เตียง)
                      {overlappingOccupantsInfo.count > 0 && (
                        <span style={{ fontSize: "0.8rem", color: "#15803d", marginLeft: "6px" }}>
                          (มีผู้พักร่วมในช่วงเวลานี้: {overlappingOccupantsInfo.overlaps.map((o) => o.traineeName).join(", ")})
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-success font-weight-bold"
                      style={{ fontSize: "0.8rem", textDecoration: "underline" }}
                      onClick={() => currentSelectedRoom && handleOpenManageBeds(currentSelectedRoom)}
                    >
                      🛏️ จัดการเตียง ({overlappingOccupantsInfo.capacity} เตียง)
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold" style={{ color: "#1e293b" }}>
                อัตราค่าหอพัก (คำนวณตาม Master)
              </label>
              <div
                style={{
                  backgroundColor: "#f8fafc",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  padding: "7px 12px",
                  fontSize: "0.88rem",
                  fontWeight: "bold",
                  color: "#0f766e",
                  display: "flex",
                  alignItems: "center",
                  minHeight: "38px"
                }}
                title="ระบบคิดราคาตาม Master Data: ตึก 2 วันละ 100 บาท, ตึกอื่นเดือนละ 500 บาท"
              >
                💰 {autoMasterFeeInfo.text}
              </div>
              <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                * คำนวณตาม Master Data อัตโนมัติ (ไม่ต้องกรอกเอง)
              </small>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label font-weight-bold">สถานะการชำระเงิน</label>
              <select
                className="form-control"
                value={stayForm.paymentStatus}
                onChange={(e) => setStayForm({ ...stayForm, paymentStatus: e.target.value })}
              >
                <option value="PAID">✅ ชำระเรียบร้อยแล้ว (PAID)</option>
                <option value="PENDING">⏳ รอชำระ (PENDING)</option>
                <option value="PARTIAL">⚠️ ชำระบางส่วน / จ่ายไม่ครบ (PARTIAL)</option>
                <option value="OVERDUE">❌ ค้างชำระ (OVERDUE)</option>
                <option value="FREE">🆓 ฟรี / ยกเว้นค่าธรรมเนียม (FREE)</option>
              </select>
            </div>

            <div className="col-md-4 mb-2">
              <label className="form-label">เบอร์โทรศัพท์ติดต่อ</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น 089-111-2233"
                value={stayForm.phone}
                onChange={(e) => setStayForm({ ...stayForm, phone: e.target.value })}
              />
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-label">หมายเหตุการพัก</label>
              <input
                type="text"
                className="form-control"
                placeholder="เช่น ย้ายห้อง, เลื่อนวัน"
                value={stayForm.remarks}
                onChange={(e) => setStayForm({ ...stayForm, remarks: e.target.value })}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setStayModal(false)}>ยกเลิก</Button>
          <Button
            color={overlappingOccupantsInfo.isFull ? "danger" : "success"}
            onClick={handleSaveStay}
            style={{ fontWeight: "bold" }}
          >
            {overlappingOccupantsInfo.isFull ? "⛔ เตียงเต็ม (ไม่สามารถบันทึกได้)" : "บันทึกข้อมูลการเข้าพัก"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal: Record Payment & Balance Calculation */}
      <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} size="lg" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setPaymentModal(false)} style={{ backgroundColor: "#064e3b", color: "#ffffff" }}>
          💳 บันทึกรับชำระเงินและคำนวณยอดค้างชำระค่าหอพัก
        </ModalHeader>
        <ModalBody>
          {selectedStayForPayment && (
            <div>
              {/* Trainee Info Card */}
              <div style={{ backgroundColor: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: "800", fontSize: "1.1rem", color: "#0f172a" }}>
                      {selectedStayForPayment.traineeName}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#475569", marginTop: "2px" }}>
                      <strong>สังกัด:</strong> {selectedStayForPayment.institution} | {selectedStayForPayment.building} ชั้น {selectedStayForPayment.floor} ห้อง {selectedStayForPayment.roomNo}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#334155", marginTop: "2px" }}>
                      <strong>ช่วงเวลาเข้าพัก:</strong> {formatStayDateRangeThai(selectedStayForPayment.dateRange, selectedStayForPayment.checkInDate, selectedStayForPayment.checkOutDate)}
                    </div>
                  </div>
                  <div>
                    {isStayActive(selectedStayForPayment) ? (
                      <Badge color="success" style={{ fontSize: "0.8rem", padding: "5px 10px" }}>
                        🟢 กำลังพักอาศัย
                      </Badge>
                    ) : (
                      <Badge color="secondary" style={{ fontSize: "0.8rem", padding: "5px 10px", backgroundColor: "#64748b" }}>
                        🚪 ออกไปแล้ว (พ้นวันฝึก)
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* บัตรแจกแจงค่าไฟที่รวมในค่าห้อง */}
              {selectedStayForPayment.electricityShare > 0 && (
                <div
                  className="p-3 mb-3 rounded"
                  style={{
                    backgroundColor: "#fffbeb",
                    border: "1.5px solid #fde047",
                    color: "#78350f"
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between flex-wrap mb-1">
                    <div className="d-flex align-items-center font-weight-bold" style={{ fontSize: "0.94rem", color: "#92400e" }}>
                      <span style={{ fontSize: "1.2rem", marginRight: "6px" }}>⚡</span>
                      รวมค่าไฟฟ้ารายเดือนจากมิเตอร์อัตโนมัติ: +${Number(selectedStayForPayment.electricityShare).toLocaleString()} บาท
                    </div>
                    <span className="badge badge-warning" style={{ fontSize: "0.8rem", backgroundColor: "#fef08a", color: "#854d0e", border: "1px solid #facc15" }}>
                      ห้องรวม ${selectedStayForPayment.roomElectricityTotal?.toLocaleString()} บ.
                    </span>
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "#b45309" }}>
                    • ค่าห้องพักตั้งต้น: <b>{(selectedStayForPayment.baseFeeAmount || 500).toLocaleString()} บาท</b>
                    <br />
                    • ค่าไฟฟ้าห้องพัก: <b>{Number(selectedStayForPayment.electricityShare).toLocaleString()} บาท</b>{" "}
                    {selectedStayForPayment.electricityRecord ? (
                      <span>(มิเตอร์ {selectedStayForPayment.electricityRecord.previousMeter} ➔ {selectedStayForPayment.electricityRecord.currentMeter} รวม {selectedStayForPayment.electricityRecord.netUnits || selectedStayForPayment.electricityRecord.totalUnits} หน่วย @ {selectedStayForPayment.electricityRecord.ratePerUnit || 8} บ./หน่วย)</span>
                    ) : ""}
                    <br />
                    • <strong>รวมยอดที่ต้องชำระทั้งหมด: {Number(selectedStayForPayment.feeAmount).toLocaleString()} บาท</strong>
                  </div>
                </div>
              )}

              {/* Fee and Payment Inputs */}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label font-weight-bold" style={{ color: "#1e293b" }}>
                    ยอดค่าหอพักทั้งหมด (Total Due) *
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#0f172a" }}
                      value={paymentInput.feeAmount}
                      onChange={(e) => {
                        const newFee = Number(e.target.value) || 0;
                        const paid = Number(paymentInput.paidAmount) || 0;
                        const rem = Math.max(0, newFee - paid);
                        let autoStatus = "OVERDUE";
                        if (rem === 0 && newFee > 0) autoStatus = "PAID";
                        else if (paid > 0 && rem > 0) autoStatus = "PARTIAL";
                        setPaymentInput({
                          ...paymentInput,
                          feeAmount: e.target.value,
                          paymentStatus: autoStatus
                        });
                      }}
                      placeholder="เช่น 500, 1500, 1600"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">บาท</span>
                    </div>
                  </div>
                  <small className="text-muted">สูตรตั้งต้น: {selectedStayForPayment.prorationText || "500 บาท/เดือน"}</small>
                </div>

                <div className="col-md-6">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label className="form-label font-weight-bold" style={{ color: "#065f46" }}>
                      ยอดเงินที่ชำระแล้วจริง (Paid Amount) *
                    </label>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-success"
                        style={{ fontSize: "0.75rem", padding: "2px 6px", fontWeight: "bold" }}
                        onClick={() => {
                          const fee = Number(paymentInput.feeAmount) || 0;
                          setPaymentInput({
                            ...paymentInput,
                            paidAmount: fee,
                            paymentStatus: fee > 0 ? "PAID" : "FREE"
                          });
                        }}
                      >
                        ⚡ ชำระครบ
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline-secondary"
                        style={{ fontSize: "0.75rem", padding: "2px 6px" }}
                        onClick={() => {
                          setPaymentInput({
                            ...paymentInput,
                            paidAmount: 0,
                            paymentStatus: "OVERDUE"
                          });
                        }}
                      >
                        🔄 ยังไม่จ่าย (0)
                      </button>
                    </div>
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#065f46" }}
                      value={paymentInput.paidAmount}
                      onChange={(e) => {
                        const paid = Number(e.target.value) || 0;
                        const fee = Number(paymentInput.feeAmount) || 0;
                        const rem = Math.max(0, fee - paid);
                        let autoStatus = "OVERDUE";
                        if (rem === 0 && fee > 0) autoStatus = "PAID";
                        else if (paid > 0 && rem > 0) autoStatus = "PARTIAL";
                        setPaymentInput({
                          ...paymentInput,
                          paidAmount: e.target.value,
                          paymentStatus: autoStatus
                        });
                      }}
                      placeholder="เช่น 500, 1000, 1500"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">บาท</span>
                    </div>
                  </div>
                  <small className="text-muted">กรอกยอดเงินที่น้องโอนหรือชำระเข้ามาจริง</small>
                </div>
              </div>

              {/* Live Balance Deduction Display */}
              {(() => {
                const totalDue = Number(paymentInput.feeAmount) || 0;
                const paid = Number(paymentInput.paidAmount) || 0;
                const remaining = Math.max(0, totalDue - paid);

                return (
                  <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
                    <div style={{ fontSize: "0.85rem", fontWeight: "bold", color: "#475569", marginBottom: "8px" }}>
                      📊 สรุปการหักลบยอดเงิน (Realtime Calculation):
                    </div>
                    <div className="row text-center mb-2">
                      <div className="col-4">
                        <div style={{ fontSize: "0.8rem", color: "#64748b" }}>ยอดทั้งหมด</div>
                        <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#0f172a" }}>
                          {totalDue.toLocaleString()} <span style={{ fontSize: "0.75rem", fontWeight: "normal" }}>บ.</span>
                        </div>
                      </div>
                      <div className="col-4" style={{ borderLeft: "1px solid #e2e8f0", borderRight: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "0.8rem", color: "#166534" }}>หักลบจ่ายแล้ว</div>
                        <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#166534" }}>
                          {paid.toLocaleString()} <span style={{ fontSize: "0.75rem", fontWeight: "normal" }}>บ.</span>
                        </div>
                      </div>
                      <div className="col-4">
                        <div style={{ fontSize: "0.8rem", color: remaining > 0 ? "#dc2626" : "#64748b" }}>ยอดที่ยังไม่จ่าย (ค้างชำระ)</div>
                        <div style={{ fontSize: "1.25rem", fontWeight: "700", color: remaining > 0 ? "#dc2626" : "#059669" }}>
                          {remaining.toLocaleString()} <span style={{ fontSize: "0.75rem", fontWeight: "normal" }}>บ.</span>
                        </div>
                      </div>
                    </div>

                    {remaining === 0 && totalDue > 0 ? (
                      <div style={{ backgroundColor: "#dcfce7", color: "#166534", border: "1px solid #86efac", borderRadius: "6px", padding: "8px 12px", fontWeight: "700", fontSize: "0.88rem" }}>
                        <CheckCircleIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ชำระครบถ้วนสมบูรณ์แล้ว (ยอดค้างชำระ 0 บาท)
                      </div>
                    ) : remaining > 0 && paid > 0 ? (
                      <div style={{ backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: "6px", padding: "8px 12px", fontWeight: "700", fontSize: "0.88rem" }}>
                        <WarningIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ⚠️ จ่ายบางส่วน (จ่ายไม่ครบ) ยังเหลือค้างชำระอีก <strong>{remaining.toLocaleString()} บาท</strong>
                      </div>
                    ) : (
                      <div style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", borderRadius: "6px", padding: "8px 12px", fontWeight: "700", fontSize: "0.88rem" }}>
                        <WarningIcon style={{ verticalAlign: "middle", marginRight: "6px" }} />
                        ⚠️ ยังไม่ชำระ มียอดค้างชำระเต็มจำนวน <strong>{remaining.toLocaleString()} บาท</strong>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Status and Details */}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label font-weight-bold">สถานะการชำระเงิน *</label>
                  <select
                    className="form-control"
                    value={paymentInput.paymentStatus}
                    onChange={(e) => {
                      const nextStatus = e.target.value;
                      if (nextStatus === "OVERDUE" || nextStatus === "PENDING") {
                        setPaymentInput({
                          ...paymentInput,
                          paymentStatus: nextStatus,
                          paidAmount: 0,
                          paidDate: ""
                        });
                      } else if (nextStatus === "PAID") {
                        const fullFee = Number(paymentInput.feeAmount) >= 0 ? Number(paymentInput.feeAmount) : (selectedStayForPayment?.feeAmount || 500);
                        setPaymentInput({
                          ...paymentInput,
                          paymentStatus: nextStatus,
                          paidAmount: fullFee,
                          paidDate: paymentInput.paidDate || new Date().toISOString().split("T")[0]
                        });
                      } else {
                        setPaymentInput({ ...paymentInput, paymentStatus: nextStatus });
                      }
                    }}
                  >
                    <option value="PAID">✅ ชำระครบแล้ว (PAID)</option>
                    <option value="PENDING">⏳ รอชำระ (PENDING)</option>
                    <option value="PARTIAL">⚠️ ชำระบางส่วน / จ่ายไม่ครบ (PARTIAL)</option>
                    <option value="OVERDUE">❌ ค้างชำระ / ยังไม่จ่าย (OVERDUE)</option>
                    <option value="FREE">🆓 ยกเว้นค่าธรรมเนียม (FREE)</option>
                  </select>
                </div>

                <div className="col-md-6">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="form-label font-weight-bold">วันที่รับชำระ (พ.ศ.)</label>
                    {paymentInput.paidDate && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        style={{ fontSize: "0.74rem", textDecoration: "underline" }}
                        onClick={() => setPaymentInput({ ...paymentInput, paidDate: "" })}
                      >
                        ล้างวันที่
                      </button>
                    )}
                  </div>
                  <Datetime
                    value={paymentInput.paidDate && moment(paymentInput.paidDate).isValid() ? moment(paymentInput.paidDate) : null}
                    renderYear={(props, year) => <td {...props}>{year + 543}</td>}
                    inputProps={{
                      placeholder: "วว/ดด/ปปปป (พ.ศ.)",
                      readOnly: "readonly",
                      style: { cursor: "pointer", backgroundColor: "#ffffff", fontWeight: "bold" },
                      className: "form-control",
                      value: paymentInput.paidDate && moment(paymentInput.paidDate).isValid() ? moment(paymentInput.paidDate).format("D MMMM BBBB") : ""
                    }}
                    closeOnSelect={true}
                    closeOnTab={true}
                    dateFormat="D MMMM BBBB"
                    timeFormat={false}
                    onChange={(val) => {
                      if (moment.isMoment(val) && val.isValid()) {
                        setPaymentInput({ ...paymentInput, paidDate: val.format("YYYY-MM-DD") });
                      } else if (val && typeof val === "string") {
                        const m = moment(val);
                        if (m.isValid()) setPaymentInput({ ...paymentInput, paidDate: m.format("YYYY-MM-DD") });
                      } else if (!val) {
                        setPaymentInput({ ...paymentInput, paidDate: "" });
                      }
                    }}
                  />
                </div>

                {/* ซ่อนเลขที่ใบเสร็จรับเงินไว้ก่อนตามที่ผู้ใช้ร้องขอ (เก็บ state ไว้ในระบบ) */}
                {/*
                <div className="col-md-4">
                  <label className="form-label font-weight-bold">เลขที่ใบเสร็จรับเงิน</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="เช่น REC-2569-11-001"
                    value={paymentInput.receiptNo}
                    onChange={(e) => setPaymentInput({ ...paymentInput, receiptNo: e.target.value })}
                  />
                </div>
                */}
              </div>

              {/* Reason / Remarks Field */}
              <div className="mb-2">
                <label className="form-label font-weight-bold" style={{ color: "#1e293b" }}>
                  เหตุผล / หมายเหตุการชำระเงิน
                  <span style={{ fontSize: "0.82rem", fontWeight: "normal", color: "#64748b", marginLeft: "6px" }}>
                    (ระบุรายละเอียด เช่น จ่ายงวดแรก 500 บาท ที่เหลือจะจ่ายสิ้นเดือน, ขอผ่อนผัน ฯลฯ)
                  </span>
                </label>
                <textarea
                  rows={2}
                  className="form-control"
                  placeholder="เช่น ทยอยจ่ายงวดแรก 500 บาท ส่วนที่เหลือ 1,000 บาท จะโอนเพิ่มสิ้นเดือนนี้"
                  value={paymentInput.note}
                  onChange={(e) => setPaymentInput({ ...paymentInput, note: e.target.value })}
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPaymentModal(false)}>ยกเลิก</Button>
          <Button color="success" onClick={handleSavePayment}>💾 บันทึกรับชำระเงิน</Button>
        </ModalFooter>
      </Modal>

      {/* Modal: Check-out Current Occupant with Calculation & Override */}
      <Modal isOpen={checkoutModal} toggle={() => setCheckoutModal(false)} size="lg" style={{ fontFamily: '"Sarabun", sans-serif' }}>
        <ModalHeader toggle={() => setCheckoutModal(false)} style={{ backgroundColor: "#991b1b", color: "#ffffff" }}>
          🚪 ยืนยันการย้ายออกและคำนวณค่าใช้จ่ายตามจริง (Check-out)
        </ModalHeader>
        <ModalBody>
          {selectedStayForCheckout && (
            <div>
              {/* Trainee & Room Summary */}
              <div style={{ backgroundColor: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: "800", fontSize: "1.1rem", color: "#0f172a" }}>
                      {selectedStayForCheckout.traineeName}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#475569", marginTop: "2px" }}>
                      <strong>ห้องพัก:</strong> {selectedStayForCheckout.building} ชั้น {selectedStayForCheckout.floor} ห้อง {selectedStayForCheckout.roomNo} | <strong>สังกัด:</strong> {selectedStayForCheckout.institution}
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#334155", marginTop: "2px" }}>
                      <strong>กำหนดการเดิมในระบบ:</strong> {formatStayDateRangeThai(selectedStayForCheckout.dateRange, selectedStayForCheckout.checkInDate, selectedStayForCheckout.checkOutDate)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.85rem", color: "#475569" }}>
                      อัตราค่าห้อง: <strong>{checkoutForm.monthlyRate} บ./เดือน ({checkoutForm.dailyRate} บ./วัน)</strong>
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#16a34a", fontWeight: "bold", marginTop: "2px" }}>
                      ชำระแล้วเดิม: {Number(checkoutForm.paidAmount).toLocaleString()} บาท
                    </div>
                  </div>
                </div>
              </div>

              {/* Date & Stay Period Section */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "14px", marginBottom: "16px", backgroundColor: "#ffffff" }}>
                <h6 style={{ fontWeight: "bold", color: "#00695c", marginBottom: "12px" }}>
                  📅 วันที่เข้าพักและวันที่กดออก (คำนวณเริ่มต้นจากวันที่เข้าถึงวันนี้)
                </h6>
                <div className="row g-3">
                  <div className="col-md-6 mb-2">
                    <label className="form-label font-weight-bold" style={{ fontSize: "0.88rem" }}>
                      วันที่เข้าพัก (Check-in Date)
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={checkoutForm.checkInDate}
                      onChange={(e) => handleRecalculateCheckout({ checkInDate: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6 mb-2">
                    <label className="form-label font-weight-bold" style={{ fontSize: "0.88rem" }}>
                      วันที่กดออกวันนี้ (Check-out Date)
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={checkoutForm.checkOutDate}
                      onChange={(e) => handleRecalculateCheckout({ checkOutDate: e.target.value })}
                    />
                  </div>
                </div>

                {/* Duration Badge */}
                <div className="mt-2 p-2" style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "0.85rem", color: "#166534" }}>
                    ⏱️ ระยะเวลาที่อยู่ตามวันที่: <strong>{checkoutForm.isManualDuration ? `${checkoutForm.customMonths} เดือน ${checkoutForm.customDays} วัน (ระบุเอง)` : `${checkoutForm.customMonths} เดือน ${checkoutForm.customDays} วัน (รวม ${checkoutForm.customMonths * 30 + checkoutForm.customDays} วัน)`}</strong>
                  </span>
                  <span className="badge badge-success" style={{ fontSize: "0.78rem" }}>
                    คำนวณอัตโนมัติ
                  </span>
                </div>
              </div>

              {/* Manual Override Option */}
              <div style={{ border: "1px solid #bae6fd", borderRadius: "8px", padding: "14px", marginBottom: "16px", backgroundColor: "#f0f9ff" }}>
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="manualDurationCheck"
                    checked={checkoutForm.isManualDuration}
                    onChange={(e) => handleRecalculateCheckout({ isManualDuration: e.target.checked })}
                    style={{ cursor: "pointer" }}
                  />
                  <label className="form-check-label font-weight-bold" htmlFor="manualDurationCheck" style={{ cursor: "pointer", color: "#0369a1", fontSize: "0.9rem" }}>
                    ✍️ หากไม่ใช่ตามวันเข้าจริงๆ ให้ติ๊กช่องนี้เพื่อกรอกว่าอยู่จริงกี่เดือนกี่วัน และคำนวณใหม่
                  </label>
                </div>

                {checkoutForm.isManualDuration && (
                  <div className="row g-2 mt-2 pt-2" style={{ borderTop: "1px dashed #7dd3fc" }}>
                    <div className="col-md-5">
                      <label className="form-label font-weight-bold" style={{ fontSize: "0.85rem", color: "#0c4a6e" }}>
                        จำนวนเดือนที่อยู่จริง (Months)
                      </label>
                      <div className="input-group">
                        <input
                          type="number"
                          min="0"
                          className="form-control font-weight-bold"
                          value={checkoutForm.customMonths}
                          onChange={(e) => handleRecalculateCheckout({ customMonths: Math.max(0, parseInt(e.target.value) || 0) })}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">เดือน</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label font-weight-bold" style={{ fontSize: "0.85rem", color: "#0c4a6e" }}>
                        จำนวนวันที่อยู่จริง (Days)
                      </label>
                      <div className="input-group">
                        <input
                          type="number"
                          min="0"
                          className="form-control font-weight-bold"
                          value={checkoutForm.customDays}
                          onChange={(e) => handleRecalculateCheckout({ customDays: Math.max(0, parseInt(e.target.value) || 0) })}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">วัน</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-info btn-block"
                        style={{ height: "38px", fontWeight: "bold" }}
                        onClick={() => handleRecalculateCheckout()}
                      >
                        🔄 คำนวณใหม่
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Calculation Mode */}
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", padding: "14px", marginBottom: "16px", backgroundColor: "#ffffff" }}>
                <label className="form-label font-weight-bold" style={{ color: "#1e293b", fontSize: "0.9rem" }}>
                  เลือกรูปแบบการคำนวณค่าหอพักตามที่อยู่จริง:
                </label>
                <div className="d-flex flex-wrap" style={{ gap: "16px" }}>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="calcMode"
                      id="modeDaily"
                      checked={checkoutForm.calculationMode === "DAILY"}
                      onChange={() => handleRecalculateCheckout({ calculationMode: "DAILY" })}
                      style={{ cursor: "pointer" }}
                    />
                    <label className="form-check-label font-weight-bold" htmlFor="modeDaily" style={{ cursor: "pointer", color: "#334155", fontSize: "0.88rem" }}>
                      🔘 คิดเป็นรายวัน (วันละ {checkoutForm.dailyRate} บาท)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="calcMode"
                      id="modeMonthly"
                      checked={checkoutForm.calculationMode === "MONTHLY"}
                      onChange={() => handleRecalculateCheckout({ calculationMode: "MONTHLY" })}
                      style={{ cursor: "pointer" }}
                    />
                    <label className="form-check-label font-weight-bold" htmlFor="modeMonthly" style={{ cursor: "pointer", color: "#334155", fontSize: "0.88rem" }}>
                      🔘 คิดเป็นรายเดือน (เดือนละ {checkoutForm.monthlyRate} บาท + เศษวัน)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="calcMode"
                      id="modeProrated"
                      checked={checkoutForm.calculationMode === "PRORATED"}
                      onChange={() => handleRecalculateCheckout({ calculationMode: "PRORATED" })}
                      style={{ cursor: "pointer" }}
                    />
                    <label className="form-check-label font-weight-bold" htmlFor="modeProrated" style={{ cursor: "pointer", color: "#334155", fontSize: "0.88rem" }}>
                      🔘 คิดตามสูตรหอพักเดิม (Prorated)
                    </label>
                  </div>
                </div>
              </div>

              {/* แสดงค่าไฟฟ้ารายเดือนในกล่องสรุปย้ายออก */}
              {checkoutForm.electricityShare > 0 && (
                <div className="p-2 px-3 mb-2 rounded d-flex align-items-center justify-content-between" style={{ backgroundColor: "#fffbeb", border: "1px solid #fde047", fontSize: "0.85rem", color: "#92400e" }}>
                  <span>⚡ รวมค่าไฟฟ้ารายเดือนตามมิเตอร์: <b>{checkoutForm.electricityShare.toLocaleString()} บาท</b></span>
                  <span className="badge badge-warning" style={{ backgroundColor: "#fef08a", color: "#854d0e" }}>คิดรวมในยอดสุทธิแล้ว</span>
                </div>
              )}

              {/* Financial Calculation Result Card */}
              <div style={{ backgroundColor: "#f8fafc", border: "2px solid #00838f", borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
                <div style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#00695c", marginBottom: "6px" }}>
                  📊 สรุปยอดคำนวณค่าหอพักตามที่อยู่จริง:
                </div>
                <div style={{ fontSize: "0.88rem", color: "#334155", marginBottom: "10px" }}>
                  สูตรคำนวณ: <strong>{checkoutForm.formulaText}</strong>
                </div>

                <div className="row text-center mb-2">
                  <div className="col-4">
                    <div style={{ fontSize: "0.78rem", color: "#64748b" }}>ค่าหอพักที่ต้องชำระจริง</div>
                    <div style={{ fontSize: "1.2rem", fontWeight: "800", color: "#0f172a" }}>
                      {Number(checkoutForm.feeAmount).toLocaleString()} บ.
                    </div>
                  </div>
                  <div className="col-4">
                    <div style={{ fontSize: "0.78rem", color: "#64748b" }}>ชำระมาแล้วเดิม</div>
                    <div style={{ fontSize: "1.2rem", fontWeight: "800", color: "#16a34a" }}>
                      {Number(checkoutForm.paidAmount).toLocaleString()} บ.
                    </div>
                  </div>
                  <div className="col-4">
                    <div style={{ fontSize: "0.78rem", color: "#64748b" }}>สถานะส่วนต่าง</div>
                    {checkoutForm.remainingToPay > 0 ? (
                      <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "#dc2626" }}>
                        ค้างเพิ่ม {checkoutForm.remainingToPay.toLocaleString()} บ.
                      </div>
                    ) : checkoutForm.refundAmount > 0 ? (
                      <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "#0284c7" }}>
                        คืนเงิน {checkoutForm.refundAmount.toLocaleString()} บ.
                      </div>
                    ) : (
                      <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "#16a34a" }}>
                        ✓ ครบถ้วนพอดี
                      </div>
                    )}
                  </div>
                </div>

                {checkoutForm.remainingToPay > 0 && (
                  <div className="alert alert-danger p-2 mb-0 mt-2" style={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                    ⚠️ ผู้เข้าพักต้องชำระเงินส่วนต่างเพิ่มเติมจำนวน <strong>{checkoutForm.remainingToPay.toLocaleString()} บาท</strong>
                  </div>
                )}
                {checkoutForm.refundAmount > 0 && (
                  <div className="alert alert-info p-2 mb-0 mt-2" style={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                    ℹ️ ผู้เข้าพักชำระเกินไว้ ทางหอพักต้องคืนเงินส่วนต่างจำนวน <strong>{checkoutForm.refundAmount.toLocaleString()} บาท</strong>
                  </div>
                )}
                {checkoutForm.remainingToPay === 0 && checkoutForm.refundAmount === 0 && (
                  <div className="alert alert-success p-2 mb-0 mt-2" style={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                    ✓ ชำระครบถ้วนพอดีกับระยะเวลาที่อยู่จริง ไม่มียอดค้างชำระ
                  </div>
                )}
              </div>

              {/* Remarks Field */}
              <div className="mb-2">
                <label className="form-label font-weight-bold" style={{ fontSize: "0.88rem" }}>
                  หมายเหตุการย้ายออก
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="เช่น ย้ายออกก่อนกำหนด, เสร็จสิ้นการฝึกอบรม"
                  value={checkoutForm.remarks}
                  onChange={(e) => setCheckoutForm({ ...checkoutForm, remarks: e.target.value })}
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCheckoutModal(false)}>
            ยกเลิก
          </Button>
          <Button
            color="danger"
            style={{ fontWeight: "bold" }}
            onClick={handleConfirmCheckout}
          >
            🚪 ยืนยันการย้ายออก (Check-out)
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirm Modal สไตล์ระบบลา (Leave System Styled Confirm Modal) */}
      <Modal
        isOpen={confirmModal.isOpen}
        toggle={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        centered
        style={{ fontFamily: '"Sarabun", sans-serif' }}
      >
        <div className="modal-header justify-content-center" style={{ borderBottom: "none", paddingTop: "25px", paddingBottom: "0px" }}>
          <div>
            <i
              className={confirmModal.icon || "far fa-question-circle"}
              style={{ color: confirmModal.iconColor || "red", fontSize: "3.8em" }}
            ></i>
          </div>
        </div>
        <ModalBody style={{ paddingTop: "12px", paddingBottom: "12px" }}>
          <p
            style={{
              textAlign: "center",
              fontSize: "1.35rem",
              fontWeight: "bold",
              color: "#1e293b",
              marginBottom: "14px",
              padding: "0 10px"
            }}
          >
            {confirmModal.title}
          </p>

          {confirmModal.stayData && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "12px 16px",
                fontSize: "0.95rem",
                color: "#334155",
                lineHeight: "1.8",
                margin: "0 10px"
              }}
            >
              <div>
                <strong>• ผู้เข้าพัก:</strong> <span style={{ color: "#0369a1", fontWeight: "bold" }}>{confirmModal.stayData.traineeName}</span>
              </div>
              <div>
                <strong>• ห้องพัก:</strong> {confirmModal.stayData.building} ห้อง {confirmModal.stayData.roomNo}
              </div>
              <div>
                <strong>• กำหนดการ:</strong> {formatStayDateRangeThai(confirmModal.stayData.dateRange, confirmModal.stayData.checkInDate, confirmModal.stayData.checkOutDate) || "-"}
              </div>
              <div style={{ marginTop: "6px", color: "#dc2626", fontSize: "0.85rem", fontStyle: "italic" }}>
                * ข้อมูลการจองนี้จะถูกยกเลิกและนำออกจากห้องพัก
              </div>
            </div>
          )}
        </ModalBody>
        <div className="modal-footer justify-content-center" style={{ borderTop: "none", paddingBottom: "25px", gap: "10px" }}>
          <Button
            color="success"
            type="button"
            onClick={confirmModal.onConfirm}
            style={{ minWidth: "125px", padding: "8px 20px" }}
          >
            <i className="fa fa-thumbs-up" style={{ fontSize: "1.4em", marginRight: "8px", verticalAlign: "middle" }}></i>
            <h4 style={{ margin: 0, display: "inline-block", verticalAlign: "middle", fontSize: "1.1rem" }}>ยืนยัน</h4>
          </Button>

          <Button
            color="danger"
            type="button"
            onClick={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
            style={{ minWidth: "125px", padding: "8px 20px" }}
          >
            <i className="fa fa-thumbs-down" style={{ fontSize: "1.4em", marginRight: "8px", verticalAlign: "middle" }}></i>
            <h4 style={{ margin: 0, display: "inline-block", verticalAlign: "middle", fontSize: "1.1rem" }}>ยกเลิก</h4>
          </Button>
        </div>
      </Modal>

      {/* Alert / Notification Modal สไตล์ระบบลา (Leave System Styled Alert Modal) */}
      <Modal
        isOpen={notifyModal.isOpen}
        toggle={() => setNotifyModal((prev) => ({ ...prev, isOpen: false }))}
        centered
        style={{ fontFamily: '"Sarabun", sans-serif' }}
      >
        <div className="modal-header justify-content-center" style={{ borderBottom: "none", paddingTop: "25px", paddingBottom: "0px" }}>
          <div>
            {notifyModal.type === "SUCCESS" ? (
              <i
                className="fas fa-check"
                style={{ color: "green", fontSize: "3.8em" }}
              ></i>
            ) : notifyModal.type === "ERROR" ? (
              <i
                className="fa fa-times"
                style={{ color: "red", fontSize: "3.8em" }}
              ></i>
            ) : (
              <i
                className="fa fa-exclamation-triangle"
                style={{ color: "#d97706", fontSize: "3.8em" }}
              ></i>
            )}
          </div>
        </div>
        <ModalBody style={{ paddingTop: "12px", paddingBottom: "12px" }}>
          <p
            style={{
              textAlign: "center",
              fontSize: "1.4rem",
              fontWeight: "bold",
              color: "#1e293b",
              marginBottom: "8px"
            }}
          >
            {notifyModal.title}
          </p>
          {notifyModal.message && (
            <div
              style={{
                textAlign: "center",
                fontSize: "0.95rem",
                color: "#475569",
                whiteSpace: "pre-line",
                padding: "0 15px",
                lineHeight: "1.6"
              }}
            >
              {notifyModal.message}
            </div>
          )}
        </ModalBody>
        <div className="modal-footer justify-content-center" style={{ borderTop: "none", paddingBottom: "25px" }}>
          <Button
            color={notifyModal.type === "SUCCESS" ? "info" : notifyModal.type === "ERROR" ? "danger" : "warning"}
            type="button"
            onClick={() => setNotifyModal((prev) => ({ ...prev, isOpen: false }))}
            style={{ minWidth: "125px", padding: "8px 24px" }}
          >
            <i className="fa fa-thumbs-up" style={{ fontSize: "1.4em", marginRight: "8px", verticalAlign: "middle" }}></i>
            <h4 style={{ margin: 0, display: "inline-block", verticalAlign: "middle", fontSize: "1.1rem" }}>ตกลง</h4>
          </Button>
        </div>
      </Modal>
    </div>
  );
}
